import { Upload } from 'tus-js-client';
import { getBranding } from './branding';
import type { PlaybackSources } from './playback';
import type {
  BackupControllerResourceGovernance,
  BackupLifecycleAction,
  BackupLifecycleActions,
  BackupLifecycleOperationType,
  BackupOperation,
  BackupOperationPayload,
  BackupOperationType,
  BackupReactivateRepositoryGenerationPayload,
  BackupOverview,
  BackupRestoreTest,
  BackupScheduleOverlapWarning,
  BackupSnapshot,
  CaptionTrack,
  CaptionCue,
  TranscriptionJob,
  TranscriptionOverview,
  Folder,
  HostedEmailDelivery,
  HostedCapacityOverview,
  HostedCapacitySettingsInput,
  HostedCommercialOrders,
  HostedCommercialDecision,
  HostedR2CostObservationInput,
  HostedMember,
  HostedDashboardSession,
  HostedIntegrationKey,
  HostedQuotaUsage,
  HostedMemberEmailChange,
  HostedMemberPolicyReview,
  HostedPolicyReview,
  HostedPolicyReviewOutcome,
  HostedPolicySignal,
  HostedEntitlement,
  MemberPurchaseOptions,
  MemberPublicBranding,
  MemberPublicBrandingInput,
  MemberPublicDomainRequest,
  HostedEntitlementManagement,
  HostedDowngradeBlocker,
  HostedDowngradeImpact,
  HostedNotificationPayload,
  HostedPlan,
  HostedPlanPublicationPreview,
  HostedPlanCreateInput,
  HostedPlanInitialInput,
  HostedPlanVersionInput,
  HostedProcessingOverview,
  ProcessingMode,
  ProcessingAvailability,
  ProcessingAvailabilityInput,
  ProcessingWorker,
  ProcessingWorkersOverview,
  PublicBranding,
  Stats,
  SupportContext,
  MediaStorageOverview,
  MediaArchiveAuditEvent,
  MediaArchiveAuditPage,
  MediaArchiveOperation,
  MediaArchiveOverview,
  MediaArchiveRecoveryCoverage,
  MediaArchiveRecoveryTarget,
  MediaArchiveRecoveryTargetInput,
  MediaArchivePolicyInput,
  MediaArchiveR2Input,
  MediaArchiveRecord,
  MediaArchiveRecordDetail,
  MediaArchiveRecordFilters,
  MediaArchiveRecordPage,
  MediaArchiveRestorePlan,
  MediaArchiveSettings,
  MediaArchiveState,
  SourceCleanupOverview,
  R2ConnectionInput,
  TeamUser,
  UploadResult,
  UploadSession,
  User,
  ImageItem,
  ImageArchiveRecord,
  VideoItem,
  VideoMetadataDraft,
  VideoChapter,
  VideoAnalytics,
  VideoListResult,
  VideoThumbnailOption,
  WorkerEnrollment,
} from './types';

type JsonRecord = Record<string, unknown>;
type RequestOptions = Omit<RequestInit, 'body'> & { body?: unknown; rawBody?: BodyInit };

export type { PlaybackSources } from './playback';
export interface PublicShareLink {
  allowOriginalDownload?: boolean;
  id: string; kind: 'permanent' | 'temporary_24h'; title: string; description: string; creatorName: string;
  createdAt: string; expiresAt: string | null; revokedAt: string | null;
  state: 'active' | 'revoked' | 'expired' | 'unavailable'; url: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function apiErrorCode(reason: unknown): string {
  if (!(reason instanceof ApiError) || !reason.details || typeof reason.details !== 'object') return '';
  const source = reason.details as JsonRecord;
  const nested = source.error;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return text((nested as JsonRecord).code);
  }
  return text(source.code);
}

let csrfToken = '';
let activeSupportContext: SupportContext | null = null;
let supportContextVersion = 0;

const scopedResourcePaths = ['/api/folders', '/api/upload-sessions', '/api/videos', '/api/images', '/api/media', '/api/public-shares', '/api/stats', '/api/account-lifecycle/public-shares'];

function isScopedResourcePath(path: string): boolean {
  return scopedResourcePaths.some((prefix) => path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(`${prefix}?`));
}

function replaceSupportContext(context: SupportContext | null): void {
  activeSupportContext = context;
  supportContextVersion += 1;
}

function applySupportHeaders(headers: Headers, context: SupportContext | null): void {
  if (!context) return;
  headers.set('X-Dewe-Support-Owner-Id', context.memberId);
  headers.set('X-Dewe-Support-Reason', context.reason);
}

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function record(value: unknown): JsonRecord {
  return isRecord(value) ? value : {};
}

function first(source: JsonRecord, ...keys: string[]): unknown {
  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null) return source[key];
  }
  return undefined;
}

function text(value: unknown, fallback = ''): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return fallback;
}

function numberValue(value: unknown, fallback = 0): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function booleanValue(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value;
  if (value === 1 || value === '1' || value === 'true') return true;
  if (value === 0 || value === '0' || value === 'false') return false;
  return fallback;
}

function processingMode(value: unknown): ProcessingMode {
  const mode = text(value).toLowerCase();
  return ['vps', 'external', 'disabled'].includes(mode) ? mode as ProcessingMode : 'unknown';
}

function nullableId(value: unknown): string | null {
  const parsed = text(value);
  return parsed && parsed !== 'null' && parsed !== 'root' ? parsed : null;
}

function arrayFrom(payload: unknown, ...keys: string[]): unknown[] {
  if (Array.isArray(payload)) return payload;
  const source = record(payload);
  for (const key of ['data', ...keys]) {
    const value = source[key];
    if (Array.isArray(value)) return value;
    if (isRecord(value)) {
      const nested = arrayFrom(value, ...keys);
      if (nested.length) return nested;
    }
  }
  return [];
}

function unwrap(payload: unknown, ...keys: string[]): unknown {
  let value = payload;
  for (let depth = 0; depth < 3 && isRecord(value); depth += 1) {
    const source = value;
    const key = keys.find((candidate) => source[candidate] !== undefined);
    if (key) return source[key];
    if (source.data !== undefined) value = source.data;
    else break;
  }
  return value;
}

function readCookie(name: string): string {
  if (typeof document === 'undefined') return '';
  const item = document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith(`${encodeURIComponent(name)}=`));
  return item ? decodeURIComponent(item.slice(item.indexOf('=') + 1)) : '';
}

function currentCsrfToken(): string {
  return (
    csrfToken ||
    readCookie('csrf_token') ||
    readCookie('csrf-token') ||
    readCookie('XSRF-TOKEN')
  );
}

async function parseResponse(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const type = response.headers.get('content-type') ?? '';
  if (type.includes('application/json')) return response.json();
  const body = await response.text();
  return body || null;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body: jsonBody, rawBody, ...requestOptions } = options;
  const method = requestOptions.method?.toUpperCase() ?? 'GET';
  const headers = new Headers(requestOptions.headers);
  const scopedResource = isScopedResourcePath(path);
  const requestSupportVersion = supportContextVersion;
  if (scopedResource) applySupportHeaders(headers, activeSupportContext);
  headers.set('Accept', 'application/json');
  const token = currentCsrfToken();
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method) && token) {
    headers.set('X-CSRF-Token', token);
    headers.set('X-XSRF-Token', token);
  }

  let body: BodyInit | undefined;
  if (rawBody !== undefined) {
    body = rawBody;
  } else if (jsonBody !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(jsonBody);
  }

  const response = await fetch(path, {
    ...requestOptions,
    body,
    headers,
    credentials: 'include',
  });
  const payload = await parseResponse(response);
  if (scopedResource && requestSupportVersion !== supportContextVersion) {
    throw new ApiError('Support context changed while the request was in progress.', 409, { code: 'stale_support_context' });
  }
  if (!response.ok) {
    if (response.status === 401) replaceSupportContext(null);
    const source = record(payload);
    const nestedError = record(source.error);
    const message = text(
      first(source, 'message', 'detail') ?? first(nestedError, 'message', 'detail', 'code'),
      response.status === 401 ? 'Your session has expired. Please sign in again.' : `Request failed (${response.status}).`,
    );
    throw new ApiError(message, response.status, payload);
  }
  return payload as T;
}

function nullableNumber(value: unknown): number | null {
  if (value === undefined || value === null || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeHostedDelivery(value: unknown): HostedEmailDelivery {
  const source = record(value);
  const notificationPayload = first(source, 'notificationPayload', 'notification_payload');
  return {
    id: text(source.id),
    templateKey: text(first(source, 'templateKey', 'template_key')),
    state: text(source.state),
    attemptCount: numberValue(first(source, 'attemptCount', 'attempt_count')),
    deliveryDetail: text(first(source, 'deliveryDetail', 'delivery_detail')) || null,
    createdAt: text(first(source, 'createdAt', 'created_at')),
    deliveredAt: text(first(source, 'deliveredAt', 'delivered_at')) || null,
    planChangeId: text(first(source, 'planChangeId', 'plan_change_id')) || null,
    notificationPayload: notificationPayload ? normalizeHostedNotificationPayload(notificationPayload) : null,
    providerResult: record(first(source, 'providerResult', 'provider_result')),
  };
}

function normalizeHostedNotificationPayload(value: unknown): HostedNotificationPayload {
  const source = record(value);
  return {
    schemaVersion: numberValue(first(source, 'schemaVersion', 'schema_version'), 1),
    headline: text(source.headline),
    summary: text(source.summary),
    dates: arrayFrom(source.dates).map((value) => {
      const item = record(value);
      return { label: text(item.label), value: text(item.value) };
    }).filter((item) => item.label && item.value),
    blockers: arrayFrom(source.blockers).map((value) => {
      const item = record(value);
      return { type: text(item.type), label: text(item.label), detail: text(item.detail) };
    }).filter((item) => item.type || item.label),
    actions: arrayFrom(source.actions).map((value) => {
      const item = record(value);
      return { code: text(item.code), label: text(item.label) };
    }).filter((item) => item.code || item.label),
    ...(source.receipt && typeof source.receipt === 'object' ? { receipt: record(source.receipt) } : {}),
  };
}

function normalizeHostedMember(value: unknown): HostedMember {
  const source = record(value);
  const latestDelivery = first(source, 'latestDelivery', 'latest_delivery');
  const entitlement = source.entitlement;
  const pendingEmailChangeSource = first(source, 'pendingEmailChange', 'pending_email_change');
  const pendingEmailChange = record(pendingEmailChangeSource);
  return {
    id: text(source.id),
    email: text(source.email),
    displayName: text(first(source, 'displayName', 'display_name'), text(source.email)),
    role: 'member',
    lifecycleState: text(first(source, 'lifecycleState', 'lifecycle_state')),
    disabledAt: text(first(source, 'disabledAt', 'disabled_at')) || null,
    operatorNote: text(first(source, 'operatorNote', 'operator_note')),
    createdAt: text(first(source, 'createdAt', 'created_at')),
    latestDelivery: latestDelivery ? normalizeHostedDelivery(latestDelivery) : null,
    supportFlags: (() => {
      const flags = record(first(source, 'supportFlags', 'support_flags'));
      return {
        pendingDowngrade: booleanValue(first(flags, 'pendingDowngrade', 'pending_downgrade')),
        remediation: booleanValue(flags.remediation),
        unresolvedOverage: booleanValue(first(flags, 'unresolvedOverage', 'unresolved_overage')),
        suspendedPlayback: booleanValue(first(flags, 'suspendedPlayback', 'suspended_playback')),
        expiringAddons: booleanValue(first(flags, 'expiringAddons', 'expiring_addons')),
        failedBillingProjection: booleanValue(first(flags, 'failedBillingProjection', 'failed_billing_projection')),
        failedBillingProjectionEventId: text(first(flags, 'failedBillingProjectionEventId', 'failed_billing_projection_event_id')) || null,
        activityReview: booleanValue(first(flags, 'activityReview', 'activity_review')),
      };
    })(),
    policyReviewSignalCount: numberValue(first(source, 'policyReviewSignalCount', 'policy_review_signal_count')),
    unreviewedPolicySignalCount: numberValue(first(source, 'unreviewedPolicySignalCount', 'unreviewed_policy_signal_count')),
    pendingEmailChange: pendingEmailChangeSource ? {
      id: text(pendingEmailChange.id),
      oldEmail: text(first(pendingEmailChange, 'oldEmail', 'old_email')),
      newEmail: text(first(pendingEmailChange, 'newEmail', 'new_email')),
      expiresAt: text(first(pendingEmailChange, 'expiresAt', 'expires_at')),
      createdAt: text(first(pendingEmailChange, 'createdAt', 'created_at')),
    } : null,
    entitlement: entitlement ? normalizeHostedEntitlement(entitlement) : null,
  };
}

function normalizeHostedPolicyReview(value: unknown): HostedPolicyReview {
  const source = record(value);
  const owner = record(source.owner);
  return {
    id: text(source.id),
    signalKey: text(first(source, 'signalKey', 'signal_key')),
    signalType: text(first(source, 'signalType', 'signal_type')) as HostedPolicyReview['signalType'],
    outcome: text(source.outcome) as HostedPolicyReviewOutcome,
    note: text(source.note) || null,
    windowStart: text(first(source, 'windowStart', 'window_start')),
    windowEnd: text(first(source, 'windowEnd', 'window_end')),
    evidence: record(source.evidence),
    owner: {
      id: text(owner.id),
      displayName: text(first(owner, 'displayName', 'display_name')),
      email: text(owner.email),
    },
    createdAt: text(first(source, 'createdAt', 'created_at')),
  };
}

function normalizeHostedPolicySignal(value: unknown): HostedPolicySignal {
  const source = record(value);
  const latestReview = first(source, 'latestReview', 'latest_review');
  return {
    signalKey: text(first(source, 'signalKey', 'signal_key')),
    type: text(source.type) as HostedPolicySignal['type'],
    title: text(source.title),
    summary: text(source.summary),
    guidance: text(source.guidance),
    windowStart: text(first(source, 'windowStart', 'window_start')),
    windowEnd: text(first(source, 'windowEnd', 'window_end')),
    evidence: arrayFrom(source.evidence).map((value) => {
      const item = record(value);
      return {
        id: text(item.id), kind: text(item.kind), label: text(item.label),
        occurredAt: text(first(item, 'occurredAt', 'occurred_at')),
        endedAt: text(first(item, 'endedAt', 'ended_at')) || null,
        status: text(item.status) || undefined,
        sizeBytes: item.sizeBytes === undefined && item.size_bytes === undefined
          ? undefined : numberValue(first(item, 'sizeBytes', 'size_bytes')),
      };
    }),
    latestReview: latestReview ? normalizeHostedPolicyReview(latestReview) : null,
  };
}

function normalizeHostedMemberPolicyReview(value: unknown): HostedMemberPolicyReview {
  const source = record(value);
  const member = record(source.member);
  return {
    member: {
      id: text(member.id),
      displayName: text(first(member, 'displayName', 'display_name')),
      email: text(member.email),
    },
    policy: {
      scope: 'one_content_owner', reviewOnly: true,
      learnerPlaybackAffected: false, automaticEnforcement: false,
    },
    signals: arrayFrom(source.signals).map(normalizeHostedPolicySignal),
    reviews: arrayFrom(source.reviews).map(normalizeHostedPolicyReview),
  };
}

function normalizeHostedDashboardSession(value: unknown): HostedDashboardSession {
  const source = record(value);
  return {
    id: text(source.id),
    clientLabel: text(first(source, 'clientLabel', 'client_label'), 'Browser session'),
    lastSeenAt: text(first(source, 'lastSeenAt', 'last_seen_at')),
    createdAt: text(first(source, 'createdAt', 'created_at')),
    expiresAt: text(first(source, 'expiresAt', 'expires_at')),
    current: booleanValue(source.current),
  };
}

function normalizeHostedIntegrationKey(value: unknown): HostedIntegrationKey {
  const source = record(value);
  return { id: text(source.id), label: text(source.label), tokenPrefix: text(first(source, 'tokenPrefix', 'token_prefix')),
    createdAt: text(first(source, 'createdAt', 'created_at')), lastUsedAt: text(first(source, 'lastUsedAt', 'last_used_at')) || null,
    revokedAt: text(first(source, 'revokedAt', 'revoked_at')) || null };
}

function normalizeHostedEntitlement(value: unknown): HostedEntitlement {
  const source = record(unwrap(value, 'entitlement'));
  const storage = record(source.storage);
  const r2DeliverySource = first(source, 'r2Delivery', 'r2_delivery');
  const r2Delivery = record(r2DeliverySource ?? source.storage);
  const protectedArchive = record(first(source, 'protectedArchive', 'protected_archive'));
  const processing = record(source.processing);
  const subtitle = record(source.subtitle);
  const scheduledPlanChangeSource = first(source, 'scheduledPlanChange', 'scheduled_plan_change');
  const scheduledPlanChange = record(scheduledPlanChangeSource);
  const planRemediationSource = first(source, 'planRemediation', 'plan_remediation');
  const planRemediation = record(planRemediationSource);
  return {
    memberId: text(first(source, 'memberId', 'member_id')),
    planCode: text(first(source, 'planCode', 'plan_code')),
    catalogPlanCode: text(first(source, 'catalogPlanCode', 'catalog_plan_code')),
    planVersionId: text(first(source, 'planVersionId', 'plan_version_id')),
    planVersion: numberValue(first(source, 'planVersion', 'plan_version')),
    planTitle: text(first(source, 'planTitle', 'plan_title')),
    billingInterval: text(first(source, 'billingInterval', 'billing_interval')),
    deletedVideoRecoveryDays: numberValue(first(source, 'deletedVideoRecoveryDays', 'deleted_video_recovery_days')),
    features: normalizeHostedPlanFeatures(source.features),
    includedFeatures: Array.isArray(first(source, 'includedFeatures', 'included_features'))
      ? (first(source, 'includedFeatures', 'included_features') as unknown[]).map((value) => text(value)).filter(Boolean)
      : [],
    subscriptionStatus: text(first(source, 'subscriptionStatus', 'subscription_status')),
    currentPeriodStart: text(first(source, 'currentPeriodStart', 'current_period_start')) || null,
    currentPeriodEnd: text(first(source, 'currentPeriodEnd', 'current_period_end')) || null,
    usagePeriodAnchor: text(first(source, 'usagePeriodAnchor', 'usage_period_anchor')),
    scheduledPlanChange: scheduledPlanChangeSource ? {
      id: text(scheduledPlanChange.id),
      kind: text(scheduledPlanChange.kind),
      status: text(scheduledPlanChange.status),
      targetOfferCode: text(first(scheduledPlanChange, 'targetOfferCode', 'target_offer_code')),
      targetPlanCode: text(first(scheduledPlanChange, 'targetPlanCode', 'target_plan_code')),
      scheduledFor: text(first(scheduledPlanChange, 'scheduledFor', 'scheduled_for')) || null,
    } : null,
    planRemediation: planRemediationSource ? {
      planChangeId: text(first(planRemediation, 'planChangeId', 'plan_change_id')),
      state: text(planRemediation.state),
      remediationDeadline: text(first(planRemediation, 'remediationDeadline', 'remediation_deadline')),
      blockers: normalizeHostedDowngradeBlockers(first(planRemediation, 'blockers', 'latest_blockers')),
    } : null,
    serviceState: text(first(source, 'serviceState', 'service_state')),
    activeFrom: text(first(source, 'activeFrom', 'active_from')) || null,
    activeUntil: text(first(source, 'activeUntil', 'active_until')) || null,
    graceUntil: text(first(source, 'graceUntil', 'grace_until')) || null,
    notifications: arrayFrom(source.notifications).map((value) => {
      const item = record(value);
      return {
        id: text(item.id), eventType: text(first(item, 'eventType', 'event_type')),
        state: text(item.state), payload: normalizeHostedNotificationPayload(item.payload),
        scheduledFor: text(first(item, 'scheduledFor', 'scheduled_for')),
        deliveredAt: text(first(item, 'deliveredAt', 'delivered_at')) || null,
        createdAt: text(first(item, 'createdAt', 'created_at')),
      };
    }).filter((item) => item.id),
    sourceRecommendedBytes: numberValue(first(source, 'sourceRecommendedBytes', 'source_recommended_bytes')),
    sourceMaxBytes: numberValue(first(source, 'sourceMaxBytes', 'source_max_bytes')),
    sourceMaxDurationSeconds: numberValue(first(source, 'sourceMaxDurationSeconds', 'source_max_duration_seconds')),
    publicationEstimateBytesPerHour: numberValue(
      first(source, 'publicationEstimateBytesPerHour', 'publication_estimate_bytes_per_hour'),
      4_700_000_000,
    ),
    quotaContractVersion: numberValue(first(source, 'quotaContractVersion', 'quota_contract_version'),
      protectedArchive.quotaBytes === undefined ? 1 : 2),
    r2Delivery: {
      usedBytes: numberValue(first(r2Delivery, 'usedBytes', 'used_bytes')),
      reservedBytes: numberValue(first(r2Delivery, 'reservedBytes', 'reserved_bytes')),
      quotaBytes: numberValue(first(r2Delivery, 'quotaBytes', 'quota_bytes')),
      availableBytes: numberValue(first(r2Delivery, 'availableBytes', 'available_bytes'), Math.max(0,
        numberValue(first(r2Delivery, 'quotaBytes', 'quota_bytes'))
          - numberValue(first(r2Delivery, 'usedBytes', 'used_bytes'))
          - numberValue(first(r2Delivery, 'reservedBytes', 'reserved_bytes')))),
      overQuotaBytes: numberValue(first(r2Delivery, 'overQuotaBytes', 'over_quota_bytes')),
    },
    protectedArchive: {
      usedBytes: numberValue(first(protectedArchive, 'usedBytes', 'used_bytes')),
      reservedBytes: numberValue(first(protectedArchive, 'reservedBytes', 'reserved_bytes')),
      quotaBytes: numberValue(first(protectedArchive, 'quotaBytes', 'quota_bytes')),
      availableBytes: numberValue(first(protectedArchive, 'availableBytes', 'available_bytes')),
      overQuotaBytes: numberValue(first(protectedArchive, 'overQuotaBytes', 'over_quota_bytes')),
    },
    storage: {
      usedBytes: numberValue(first(storage, 'usedBytes', 'used_bytes')),
      reservedBytes: numberValue(first(storage, 'reservedBytes', 'reserved_bytes')),
      quotaBytes: numberValue(first(storage, 'quotaBytes', 'quota_bytes')),
    },
    processing: {
      consumedSeconds: numberValue(first(processing, 'consumedSeconds', 'consumed_seconds')),
      reservedSeconds: numberValue(first(processing, 'reservedSeconds', 'reserved_seconds')),
      allowanceSeconds: numberValue(first(processing, 'allowanceSeconds', 'allowance_seconds')),
      creditConsumedSeconds: numberValue(first(processing, 'creditConsumedSeconds', 'credit_consumed_seconds')),
      creditReservedSeconds: numberValue(first(processing, 'creditReservedSeconds', 'credit_reserved_seconds')),
      creditAvailableSeconds: numberValue(first(processing, 'creditAvailableSeconds', 'credit_available_seconds')),
      creditEarliestExpiry: text(first(processing, 'creditEarliestExpiry', 'credit_earliest_expiry')) || null,
      periodStart: text(first(processing, 'periodStart', 'period_start')) || undefined,
      periodEnd: text(first(processing, 'periodEnd', 'period_end')) || undefined,
    },
    subtitle: {
      consumedSeconds: numberValue(first(subtitle, 'consumedSeconds', 'consumed_seconds')),
      reservedSeconds: numberValue(first(subtitle, 'reservedSeconds', 'reserved_seconds')),
      allowanceSeconds: numberValue(first(subtitle, 'allowanceSeconds', 'allowance_seconds')),
      creditConsumedSeconds: numberValue(first(subtitle, 'creditConsumedSeconds', 'credit_consumed_seconds')),
      creditReservedSeconds: numberValue(first(subtitle, 'creditReservedSeconds', 'credit_reserved_seconds')),
      creditAvailableSeconds: numberValue(first(subtitle, 'creditAvailableSeconds', 'credit_available_seconds')),
      creditEarliestExpiry: text(first(subtitle, 'creditEarliestExpiry', 'credit_earliest_expiry')) || null,
      periodStart: text(first(subtitle, 'periodStart', 'period_start')) || undefined,
      periodEnd: text(first(subtitle, 'periodEnd', 'period_end')) || undefined,
    },
    effectiveEntitlement: Object.keys(record(first(source, 'effectiveEntitlement', 'effective_entitlement'))).length
      ? record(first(source, 'effectiveEntitlement', 'effective_entitlement')) : undefined,
  };
}

function normalizeHostedDowngradeBlockers(value: unknown): HostedDowngradeBlocker[] {
  return arrayFrom(value).map((entry) => {
    const blocker = record(entry);
    const origins = arrayFrom(blocker.origins).map((origin) => text(origin)).filter(Boolean);
    return {
      type: text(blocker.type),
      videoId: text(first(blocker, 'videoId', 'video_id')) || undefined,
      videoTitle: text(first(blocker, 'videoTitle', 'video_title')) || undefined,
      actual: blocker.actual === undefined ? undefined : numberValue(blocker.actual),
      limit: blocker.limit === undefined ? undefined : numberValue(blocker.limit),
      origins: origins.length ? origins : undefined,
      actualBytes: blocker.actualBytes === undefined ? undefined : numberValue(first(blocker, 'actualBytes', 'actual_bytes')),
      reservedBytes: blocker.reservedBytes === undefined ? undefined : numberValue(first(blocker, 'reservedBytes', 'reserved_bytes')),
      limitBytes: blocker.limitBytes === undefined ? undefined : numberValue(first(blocker, 'limitBytes', 'limit_bytes')),
      excessBytes: blocker.excessBytes === undefined ? undefined : numberValue(first(blocker, 'excessBytes', 'excess_bytes')),
    };
  });
}

function normalizeHostedDowngradeImpact(value: unknown): HostedDowngradeImpact {
  const source = record(unwrap(value, 'impact'));
  const target = record(source.target);
  const actual = record(source.actual);
  const internalCleanup = record(first(source, 'internalCleanup', 'internal_cleanup'));
  return {
    id: text(source.id), memberUserId: text(first(source, 'memberUserId', 'member_user_id')),
    status: text(source.status), remediationState: text(first(source, 'remediationState', 'remediation_state')),
    scheduledFor: text(first(source, 'scheduledFor', 'scheduled_for')) || null,
    effectiveAt: text(first(source, 'effectiveAt', 'effective_at')) || null,
    remediationDeadline: text(first(source, 'remediationDeadline', 'remediation_deadline')),
    target: {
      planCode: text(first(target, 'planCode', 'plan_code')),
      planTitle: text(first(target, 'planTitle', 'plan_title')),
      offerCode: text(first(target, 'offerCode', 'offer_code')),
      billingInterval: text(first(target, 'billingInterval', 'billing_interval')),
      nextRenewalPriceIdr: numberValue(first(target, 'nextRenewalPriceIdr', 'next_renewal_price_idr')),
      r2DeliveryQuotaBytes: numberValue(first(target,
        'r2DeliveryQuotaBytes', 'r2_delivery_quota_bytes', 'storageQuotaBytes', 'storage_quota_bytes')),
      protectedArchiveQuotaBytes: numberValue(first(target,
        'protectedArchiveQuotaBytes', 'protected_archive_quota_bytes')),
      storageQuotaBytes: numberValue(first(target,
        'storageQuotaBytes', 'storage_quota_bytes', 'r2DeliveryQuotaBytes', 'r2_delivery_quota_bytes')),
      processingAllowanceSeconds: numberValue(first(target, 'processingAllowanceSeconds', 'processing_allowance_seconds')),
      subtitleAllowanceSeconds: numberValue(first(target, 'subtitleAllowanceSeconds', 'subtitle_allowance_seconds')),
      features: normalizeHostedPlanFeatures(target.features),
    },
    actual: {
      storageUsedBytes: numberValue(first(actual, 'storageUsedBytes', 'storage_used_bytes')),
      storageReservedBytes: numberValue(first(actual, 'storageReservedBytes', 'storage_reserved_bytes')),
      protectedArchiveUsedBytes: numberValue(first(actual,
        'protectedArchiveUsedBytes', 'protected_archive_used_bytes')),
      protectedArchiveReservedBytes: numberValue(first(actual,
        'protectedArchiveReservedBytes', 'protected_archive_reserved_bytes')),
      processingConsumedSeconds: numberValue(first(actual, 'processingConsumedSeconds', 'processing_consumed_seconds')),
      processingReservedSeconds: numberValue(first(actual, 'processingReservedSeconds', 'processing_reserved_seconds')),
      subtitleConsumedSeconds: numberValue(first(actual, 'subtitleConsumedSeconds', 'subtitle_consumed_seconds')),
      subtitleReservedSeconds: numberValue(first(actual, 'subtitleReservedSeconds', 'subtitle_reserved_seconds')),
    },
    blockers: normalizeHostedDowngradeBlockers(source.blockers),
    internalCleanup: {
      candidateCount: numberValue(first(internalCleanup, 'candidateCount', 'candidate_count')),
      bytesPending: numberValue(first(internalCleanup, 'bytesPending', 'bytes_pending')),
      prioritizedBeforeCustomerMedia: true,
    },
    cleanupCandidates: arrayFrom(first(source, 'cleanupCandidates', 'cleanup_candidates')).map((entry) => {
      const candidate = record(entry);
      return {
        kind: text(candidate.kind), videoId: text(first(candidate, 'videoId', 'video_id')),
        title: text(candidate.title), trashedAt: text(first(candidate, 'trashedAt', 'trashed_at')) || null,
        recoverableUntil: text(first(candidate, 'recoverableUntil', 'recoverable_until')) || null,
        bytesRecoverable: numberValue(first(candidate, 'bytesRecoverable', 'bytes_recoverable')),
        publicationStates: arrayFrom(first(candidate, 'publicationStates', 'publication_states')).map((state) => text(state)),
        embedImpact: numberValue(first(candidate, 'embedImpact', 'embed_impact')),
        lastPlayedAt: text(first(candidate, 'lastPlayedAt', 'last_played_at')) || null,
        recommendedAction: text(first(candidate, 'recommendedAction', 'recommended_action')),
        movingToTrashFreesQuota: false as const,
        permanentDeleteRequiresExplicitConfirmation: true as const,
      };
    }),
    trashNotice: text(first(source, 'trashNotice', 'trash_notice')),
  };
}

export function normalizeHostedCapacity(value: unknown): HostedCapacityOverview {
  const source = record(unwrap(value, 'capacity'));
  const config = record(source.config);
  const provider = record(source.provider);
  const archives = record(source.archives);
  const commitments = record(source.commitments);
  const evaluation = record(source.evaluation);
  const controls = record(source.controls);
  const protectedControl = record(controls.protected);
  const processingControl = record(controls.processing);
  const r2Control = record(controls.r2);
  const r2Observation = r2Control.observation === null ? null : record(r2Control.observation);
  const normalizeLegacyHistory = (value: unknown): HostedCapacityOverview['history'] => arrayFrom(value).map((entry) => {
    const event = record(entry);
    return {
      id: text(event.id), operationKind: text(first(event, 'operationKind', 'operation_kind')),
      outcome: text(event.outcome), memberUserId: text(first(event, 'memberUserId', 'member_user_id')) || null,
      memberName: text(first(event, 'memberName', 'member_name')) || null,
      currentCommitmentUnits: Math.max(0, numberValue(first(event, 'currentCommitmentUnits', 'current_commitment_units'))),
      candidateCommitmentUnits: Math.max(0, numberValue(first(event, 'candidateCommitmentUnits', 'candidate_commitment_units'))),
      utilizationBasisPoints: Math.max(0, numberValue(first(event, 'utilizationBasisPoints', 'utilization_basis_points'))),
      sourceReference: text(first(event, 'sourceReference', 'source_reference')) || null,
      reason: text(event.reason) || null,
      createdAt: text(first(event, 'createdAt', 'created_at')),
    };
  }).filter((event) => event.id);
  return {
    config: {
      configuredProviderCapacityBytes: nullableNumber(first(config, 'configuredProviderCapacityBytes', 'configured_provider_capacity_bytes')),
      operationalReserveBytes: Math.max(0, numberValue(first(config, 'operationalReserveBytes', 'operational_reserve_bytes'))),
      commitmentUnitBytes: Math.max(1, numberValue(first(config, 'commitmentUnitBytes', 'commitment_unit_bytes'), 50_000_000_000)),
      archiveMultiplierBasisPoints: Math.max(10_000, numberValue(first(config, 'archiveMultiplierBasisPoints', 'archive_multiplier_basis_points'), 15_000)),
      salesCommitmentCeilingBytes: Math.max(1, numberValue(first(config, 'salesCommitmentCeilingBytes', 'sales_commitment_ceiling_bytes'))),
      warningBasisPoints: numberValue(first(config, 'warningBasisPoints', 'warning_basis_points'), 7_000),
      salesFreezeBasisPoints: numberValue(first(config, 'salesFreezeBasisPoints', 'sales_freeze_basis_points'), 8_000),
      criticalBasisPoints: numberValue(first(config, 'criticalBasisPoints', 'critical_basis_points'), 9_000),
      telemetryStaleAfterHours: numberValue(first(config, 'telemetryStaleAfterHours', 'telemetry_stale_after_hours'), 48),
      enforcementEnabled: booleanValue(first(config, 'enforcementEnabled', 'enforcement_enabled')),
      protectedAllocationCeilingBytes: Math.max(1, numberValue(first(config,
        'protectedAllocationCeilingBytes', 'protected_allocation_ceiling_bytes'), 2_000_000_000_000)),
      processingCommitmentCeilingSeconds: Math.max(1, numberValue(first(config,
        'processingCommitmentCeilingSeconds', 'processing_commitment_ceiling_seconds'), 720_000)),
      processingTopupSellableMinutes: numberValue(config.processingTopupSellableMinutes),
      independentWarningBasisPoints: numberValue(first(config,
        'independentWarningBasisPoints', 'independent_warning_basis_points'), 8_000),
      r2MonthlyBudgetIdr: nullableNumber(first(config, 'r2MonthlyBudgetIdr', 'r2_monthly_budget_idr')),
      r2StorageCostIdrPerGbMonth: Math.max(1, numberValue(first(config,
        'r2StorageCostIdrPerGbMonth', 'r2_storage_cost_idr_per_gb_month'), 248)),
      r2WarningBasisPoints: numberValue(first(config, 'r2WarningBasisPoints', 'r2_warning_basis_points'), 7_000),
      r2PauseBasisPoints: numberValue(first(config, 'r2PauseBasisPoints', 'r2_pause_basis_points'), 9_000),
      r2TelemetryStaleAfterHours: numberValue(first(config,
        'r2TelemetryStaleAfterHours', 'r2_telemetry_stale_after_hours'), 72),
      r2AddonGrantsPaused: booleanValue(first(config, 'r2AddonGrantsPaused', 'r2_addon_grants_paused')),
      r2BudgetEnforcementEnabled: booleanValue(first(config,
        'r2BudgetEnforcementEnabled', 'r2_budget_enforcement_enabled'), true),
    },
    provider: {
      observedCapacityBytes: nullableNumber(first(provider, 'observedCapacityBytes', 'observed_capacity_bytes')),
      effectiveCapacityBytes: nullableNumber(first(provider, 'effectiveCapacityBytes', 'effective_capacity_bytes')),
      usageBytes: nullableNumber(first(provider, 'usageBytes', 'usage_bytes')),
      driveUsageBytes: nullableNumber(first(provider, 'driveUsageBytes', 'drive_usage_bytes')),
      trashUsageBytes: nullableNumber(first(provider, 'trashUsageBytes', 'trash_usage_bytes')),
      observedAt: safeTimestamp(first(provider, 'observedAt', 'observed_at')) ?? null,
      telemetryFresh: booleanValue(first(provider, 'telemetryFresh', 'telemetry_fresh')),
    },
    archives: {
      pendingBytes: Math.max(0, numberValue(first(archives, 'pendingBytes', 'pending_bytes'))),
      verifiedBytes: Math.max(0, numberValue(first(archives, 'verifiedBytes', 'verified_bytes'))),
      retainedAfterPurgeBytes: Math.max(0, numberValue(first(archives, 'retainedAfterPurgeBytes', 'retained_after_purge_bytes'))),
      retainedSupersededBytes: Math.max(0, numberValue(first(archives, 'retainedSupersededBytes', 'retained_superseded_bytes'))),
      failedBytes: Math.max(0, numberValue(first(archives, 'failedBytes', 'failed_bytes'))),
      failedPublications: Math.max(0, numberValue(first(archives, 'failedPublications', 'failed_publications'))),
      failedOperations: Math.max(0, numberValue(first(archives, 'failedOperations', 'failed_operations'))),
    },
    commitments: {
      units: Math.max(0, numberValue(commitments.units)),
      bytes: Math.max(0, numberValue(commitments.bytes)),
      members: arrayFrom(commitments.members).map((entry) => {
        const member = record(entry);
        return {
          memberUserId: text(first(member, 'memberUserId', 'member_user_id')),
          memberName: text(first(member, 'memberName', 'member_name')),
          memberEmail: text(first(member, 'memberEmail', 'member_email')),
          subscriptionStatus: text(first(member, 'subscriptionStatus', 'subscription_status')),
          targetQuotaBytes: Math.max(0, numberValue(first(member, 'targetQuotaBytes', 'target_quota_bytes'))),
          actualRetainedBytes: Math.max(0, numberValue(first(member, 'actualRetainedBytes', 'actual_retained_bytes'))),
          reservedBytes: Math.max(0, numberValue(first(member, 'reservedBytes', 'reserved_bytes'))),
          commitmentUnits: Math.max(0, numberValue(first(member, 'commitmentUnits', 'commitment_units'))),
          protectedCommitmentBytes: Math.max(0, numberValue(first(member,
            'protectedCommitmentBytes', 'protected_commitment_bytes'))),
          processingCommitmentSeconds: Math.max(0, numberValue(first(member,
            'processingCommitmentSeconds', 'processing_commitment_seconds'))),
          overTargetQuota: booleanValue(first(member, 'overTargetQuota', 'over_target_quota')),
        };
      }).filter((member) => member.memberUserId),
    },
    controls: {
      protected: {
        commitmentBytes: Math.max(0, numberValue(first(protectedControl, 'commitmentBytes', 'commitment_bytes'))),
        ceilingBytes: Math.max(1, numberValue(first(protectedControl, 'ceilingBytes', 'ceiling_bytes'), 2_000_000_000_000)),
        availableBytes: Math.max(0, numberValue(first(protectedControl, 'availableBytes', 'available_bytes'))),
        utilizationBasisPoints: Math.max(0, numberValue(first(protectedControl,
          'utilizationBasisPoints', 'utilization_basis_points'))),
        level: text(protectedControl.level, 'normal') as HostedCapacityOverview['controls']['protected']['level'],
        blocked: booleanValue(protectedControl.blocked),
      },
      processing: {
        baseCommitmentSeconds: numberValue(processingControl.baseCommitmentSeconds, numberValue(processingControl.commitmentSeconds)),
        sellableReserveSeconds: numberValue(processingControl.sellableReserveSeconds),
        orderHeldSeconds: numberValue(processingControl.orderHeldSeconds),
        fulfilledTopupSeconds: numberValue(processingControl.fulfilledTopupSeconds),
        availableSellableSeconds: numberValue(processingControl.availableSellableSeconds),
        commitmentSeconds: Math.max(0, numberValue(first(processingControl,
          'commitmentSeconds', 'commitment_seconds'))),
        ceilingSeconds: Math.max(1, numberValue(first(processingControl,
          'ceilingSeconds', 'ceiling_seconds'), 720_000)),
        availableSeconds: Math.max(0, numberValue(first(processingControl,
          'availableSeconds', 'available_seconds'))),
        utilizationBasisPoints: Math.max(0, numberValue(first(processingControl,
          'utilizationBasisPoints', 'utilization_basis_points'))),
        level: text(processingControl.level, 'normal') as HostedCapacityOverview['controls']['processing']['level'],
        blocked: booleanValue(processingControl.blocked),
      },
      r2: {
        retainedBytes: Math.max(0, numberValue(first(r2Control, 'retainedBytes', 'retained_bytes'))),
        retainedForForecast: Math.max(0, numberValue(first(r2Control,
          'retainedForForecast', 'retained_for_forecast'))),
        billableStorageBytes: Math.max(0, numberValue(first(r2Control,
          'billableStorageBytes', 'billable_storage_bytes'))),
        storageForecastIdr: Math.max(0, numberValue(first(r2Control,
          'storageForecastIdr', 'storage_forecast_idr'))),
        observedVariableCostIdr: nullableNumber(first(r2Control,
          'observedVariableCostIdr', 'observed_variable_cost_idr')),
        variableCostForecastIdr: nullableNumber(first(r2Control,
          'variableCostForecastIdr', 'variable_cost_forecast_idr')),
        forecastIdr: Math.max(0, numberValue(first(r2Control, 'forecastIdr', 'forecast_idr'))),
        forecastComplete: booleanValue(first(r2Control, 'forecastComplete', 'forecast_complete')),
        monthlyBudgetIdr: nullableNumber(first(r2Control, 'monthlyBudgetIdr', 'monthly_budget_idr')),
        budgetUtilizationBasisPoints: nullableNumber(first(r2Control,
          'budgetUtilizationBasisPoints', 'budget_utilization_basis_points')),
        telemetryObserved: booleanValue(first(r2Control, 'telemetryObserved', 'telemetry_observed')),
        telemetryFresh: booleanValue(first(r2Control, 'telemetryFresh', 'telemetry_fresh')),
        observedAt: safeTimestamp(first(r2Control, 'observedAt', 'observed_at')) ?? null,
        newGrantsPaused: booleanValue(first(r2Control, 'newGrantsPaused', 'new_grants_paused')),
        manualPause: booleanValue(first(r2Control, 'manualPause', 'manual_pause')),
        automaticPause: booleanValue(first(r2Control, 'automaticPause', 'automatic_pause')),
        level: text(r2Control.level, 'unobserved') as HostedCapacityOverview['controls']['r2']['level'],
        reasons: arrayFrom(r2Control.reasons).map((reason) => text(reason)).filter(Boolean),
        observation: r2Observation && Object.keys(r2Observation).length > 0 ? {
          id: text(r2Observation.id),
          periodStart: text(first(r2Observation, 'periodStart', 'period_start')),
          retainedBytes: Math.max(0, numberValue(first(r2Observation, 'retainedBytes', 'retained_bytes'))),
          classAOperations: Math.max(0, numberValue(first(r2Observation, 'classAOperations', 'class_a_operations'))),
          classBOperations: Math.max(0, numberValue(first(r2Observation, 'classBOperations', 'class_b_operations'))),
          workerRequests: Math.max(0, numberValue(first(r2Observation, 'workerRequests', 'worker_requests'))),
          workerCpuMilliseconds: Math.max(0, numberValue(first(r2Observation,
            'workerCpuMilliseconds', 'worker_cpu_milliseconds'))),
          operationCostIdr: Math.max(0, numberValue(first(r2Observation, 'operationCostIdr', 'operation_cost_idr'))),
          workerCostIdr: Math.max(0, numberValue(first(r2Observation, 'workerCostIdr', 'worker_cost_idr'))),
          observedAt: text(first(r2Observation, 'observedAt', 'observed_at')),
          source: text(r2Observation.source),
          note: text(r2Observation.note) || null,
          createdAt: text(first(r2Observation, 'createdAt', 'created_at')),
        } : null,
      },
    },
    evaluation: {
      committedBytes: Math.max(0, numberValue(first(evaluation, 'committedBytes', 'committed_bytes'))),
      accountedArchiveBytes: Math.max(0, numberValue(first(evaluation, 'accountedArchiveBytes', 'accounted_archive_bytes'))),
      externalProviderBytes: Math.max(0, numberValue(first(evaluation, 'externalProviderBytes', 'external_provider_bytes'))),
      projectedArchiveBytes: Math.max(0, numberValue(first(evaluation, 'projectedArchiveBytes', 'projected_archive_bytes'))),
      projectedProviderBytes: Math.max(0, numberValue(first(evaluation, 'projectedProviderBytes', 'projected_provider_bytes'))),
      providerUtilizationBasisPoints: nullableNumber(first(evaluation, 'providerUtilizationBasisPoints', 'provider_utilization_basis_points')),
      salesUtilizationBasisPoints: Math.max(0, numberValue(first(evaluation, 'salesUtilizationBasisPoints', 'sales_utilization_basis_points'))),
      utilizationBasisPoints: Math.max(0, numberValue(first(evaluation, 'utilizationBasisPoints', 'utilization_basis_points'))),
      telemetryObserved: booleanValue(first(evaluation, 'telemetryObserved', 'telemetry_observed')),
      telemetryFresh: booleanValue(first(evaluation, 'telemetryFresh', 'telemetry_fresh')),
      salesFrozen: booleanValue(first(evaluation, 'salesFrozen', 'sales_frozen')),
      level: text(evaluation.level, 'unobserved') as HostedCapacityOverview['evaluation']['level'],
      reasons: arrayFrom(evaluation.reasons).map((reason) => text(reason)).filter(Boolean),
    },
    history: normalizeLegacyHistory(source.history),
    legacyHistory: normalizeLegacyHistory(first(source, 'legacyHistory', 'legacy_history', 'history')),
    decisions: arrayFrom(source.decisions).map((entry) => {
      const decision = record(entry);
      return {
        id: text(decision.id),
        operationKind: text(first(decision, 'operationKind', 'operation_kind')),
        outcome: text(decision.outcome),
        memberUserId: text(first(decision, 'memberUserId', 'member_user_id')) || null,
        memberName: text(first(decision, 'memberName', 'member_name')) || null,
        actorUserId: text(first(decision, 'actorUserId', 'actor_user_id')) || null,
        actorName: text(first(decision, 'actorName', 'actor_name')) || null,
        sourceReference: text(first(decision, 'sourceReference', 'source_reference')) || null,
        gateResults: record(first(decision, 'gateResults', 'gate_results')),
        metadata: record(decision.metadata),
        createdAt: text(first(decision, 'createdAt', 'created_at')),
      };
    }).filter((decision) => decision.id),
  };
}

function normalizeHostedPlanFeatures(value: unknown): Record<string, boolean | number | string> {
  const source = record(value);
  return Object.fromEntries(Object.entries(source).filter((entry): entry is [string, boolean | number | string] => {
    const featureValue = entry[1];
    return typeof featureValue === 'boolean' || typeof featureValue === 'number' || typeof featureValue === 'string';
  }));
}

function normalizeHostedPlan(value: unknown): HostedPlan {
  const source = record(value);
  const r2DeliveryQuotaBytes = numberValue(first(source,
    'r2DeliveryQuotaBytes', 'r2_delivery_quota_bytes', 'storageQuotaBytes', 'storage_quota_bytes'));
  return {
    code: text(source.code),
    title: text(source.title),
    status: text(source.status),
    displayOrder: numberValue(first(source, 'displayOrder', 'display_order')),
    versionId: text(first(source, 'versionId', 'version_id')),
    version: numberValue(source.version),
    quotaContractVersion: numberValue(first(source, 'quotaContractVersion', 'quota_contract_version'), 1),
    r2DeliveryQuotaBytes,
    protectedArchiveQuotaBytes: numberValue(first(source,
      'protectedArchiveQuotaBytes', 'protected_archive_quota_bytes'), Math.floor(r2DeliveryQuotaBytes / 2)),
    storageQuotaBytes: r2DeliveryQuotaBytes,
    processingAllowanceSeconds: numberValue(first(source, 'processingAllowanceSeconds', 'processing_allowance_seconds')),
    subtitleAllowanceSeconds: numberValue(first(source, 'subtitleAllowanceSeconds', 'subtitle_allowance_seconds')),
    deletedVideoRecoveryDays: numberValue(first(source, 'deletedVideoRecoveryDays', 'deleted_video_recovery_days')),
    sourceRecommendedBytes: numberValue(first(source, 'sourceRecommendedBytes', 'source_recommended_bytes')),
    sourceMaxBytes: numberValue(first(source, 'sourceMaxBytes', 'source_max_bytes')),
    sourceMaxDurationSeconds: numberValue(first(source, 'sourceMaxDurationSeconds', 'source_max_duration_seconds')),
    features: normalizeHostedPlanFeatures(source.features),
    offers: arrayFrom(source.offers).map((value) => {
      const offer = record(value);
      return {
        code: text(offer.code),
        billingInterval: text(first(offer, 'billingInterval', 'billing_interval')),
        status: text(offer.status),
        displayOrder: numberValue(first(offer, 'displayOrder', 'display_order')),
        priceIdr: numberValue(first(offer, 'priceIdr', 'price_idr')),
        currency: text(offer.currency, 'IDR'),
      };
    }),
    versions: arrayFrom(source.versions).map((value) => {
      const version = record(value);
      const prices = record(version.prices);
      const versionR2DeliveryQuotaBytes = numberValue(first(version,
        'r2DeliveryQuotaBytes', 'r2_delivery_quota_bytes', 'storageQuotaBytes', 'storage_quota_bytes'));
      return {
        id: text(version.id),
        version: numberValue(version.version),
        publishedAt: text(first(version, 'publishedAt', 'published_at')),
        r2DeliveryQuotaBytes: versionR2DeliveryQuotaBytes,
        protectedArchiveQuotaBytes: numberValue(first(version,
          'protectedArchiveQuotaBytes', 'protected_archive_quota_bytes'), Math.floor(versionR2DeliveryQuotaBytes / 2)),
        storageQuotaBytes: versionR2DeliveryQuotaBytes,
        processingAllowanceSeconds: numberValue(first(version, 'processingAllowanceSeconds', 'processing_allowance_seconds')),
        subtitleAllowanceSeconds: numberValue(first(version, 'subtitleAllowanceSeconds', 'subtitle_allowance_seconds')),
        deletedVideoRecoveryDays: numberValue(first(version, 'deletedVideoRecoveryDays', 'deleted_video_recovery_days')),
        sourceRecommendedBytes: numberValue(first(version, 'sourceRecommendedBytes', 'source_recommended_bytes')),
        sourceMaxBytes: numberValue(first(version, 'sourceMaxBytes', 'source_max_bytes')),
        sourceMaxDurationSeconds: numberValue(first(version, 'sourceMaxDurationSeconds', 'source_max_duration_seconds')),
        prices: { monthly: numberValue(prices.monthly), annual: numberValue(prices.annual) },
        features: normalizeHostedPlanFeatures(version.features),
      };
    }),
    productMappings: arrayFrom(source, 'productMappings', 'product_mappings').map((value) => {
      const mapping = record(value);
      return {
        id: text(mapping.id),
        provider: text(mapping.provider),
        externalProductId: text(first(mapping, 'externalProductId', 'external_product_id')),
        offerCode: text(first(mapping, 'offerCode', 'offer_code')),
        billingInterval: text(first(mapping, 'billingInterval', 'billing_interval')),
        planVersionId: text(first(mapping, 'planVersionId', 'plan_version_id')),
        state: text(mapping.state),
        createdAt: text(first(mapping, 'createdAt', 'created_at')),
        retiredAt: text(first(mapping, 'retiredAt', 'retired_at')) || null,
      };
    }),
    affectedFutureRenewals: numberValue(first(source, 'affectedFutureRenewals', 'affected_future_renewals')),
  };
}

function normalizeHostedPlanPreview(value: unknown): HostedPlanPublicationPreview {
  const source = record(unwrap(value, 'preview'));
  const rollout = record(first(source, 'existingMemberRollout', 'existing_member_rollout'));
  return {
    planCode: text(first(source, 'planCode', 'plan_code')),
    currentVersion: numberValue(first(source, 'currentVersion', 'current_version')),
    nextVersion: numberValue(first(source, 'nextVersion', 'next_version')),
    currentStatus: text(first(source, 'currentStatus', 'current_status')),
    nextStatus: text(first(source, 'nextStatus', 'next_status')),
    affectedFutureRenewals: numberValue(first(source, 'affectedFutureRenewals', 'affected_future_renewals')),
    mappings: arrayFrom(source.mappings).map((value) => {
      const mapping = record(value);
      return {
        id: text(mapping.id), provider: text(mapping.provider),
        externalProductId: text(first(mapping, 'externalProductId', 'external_product_id')),
        offerCode: text(first(mapping, 'offerCode', 'offer_code')),
        billingInterval: text(first(mapping, 'billingInterval', 'billing_interval')),
        planVersionId: text(first(mapping, 'planVersionId', 'plan_version_id')),
        state: 'active', createdAt: '', retiredAt: null,
        action: ((): 'roll_forward' | 'retire' | 'activate' | 'unchanged' => {
          const action = text(mapping.action);
          return action === 'retire' || action === 'activate' || action === 'unchanged' ? action : 'roll_forward';
        })(),
      };
    }),
    historicalAssignmentsPreserved: booleanValue(first(source, 'historicalAssignmentsPreserved', 'historical_assignments_preserved')),
    subscriptionPeriodsPreserved: booleanValue(first(source, 'subscriptionPeriodsPreserved', 'subscription_periods_preserved')),
    existingMemberRollout: {
      requested: booleanValue(rollout.requested),
      eligible: booleanValue(rollout.eligible),
      memberCount: numberValue(first(rollout, 'memberCount', 'member_count')),
      improvements: arrayFrom(rollout.improvements).map((entry) => text(entry)),
      blockers: arrayFrom(rollout.blockers).map((entry) => text(entry)),
      appliedMemberCount: numberValue(first(rollout, 'appliedMemberCount', 'applied_member_count')),
    },
    publishedVersionId: text(first(source, 'publishedVersionId', 'published_version_id')) || undefined,
  };
}

function normalizeHostedEntitlementManagement(value: unknown): HostedEntitlementManagement {
  const source = record(unwrap(value, 'management'));
  return {
    entitlement: normalizeHostedEntitlement(source.entitlement),
    grants: arrayFrom(source.grants).map((value) => {
      const grant = record(value);
      return {
        id: text(grant.id), grantCode: text(first(grant, 'grantCode', 'grant_code')),
        values: normalizeHostedPlanFeatures(grant.values), state: text(grant.state),
        effectiveFrom: text(first(grant, 'effectiveFrom', 'effective_from')),
        effectiveUntil: text(first(grant, 'effectiveUntil', 'effective_until')) || null,
        sourceProvider: text(first(grant, 'sourceProvider', 'source_provider')),
        sourceReference: text(first(grant, 'sourceReference', 'source_reference')) || null,
        quantity: numberValue(grant.quantity) || null,
        sourceOrderId: text(first(grant, 'sourceOrderId', 'source_order_id')) || null,
        sourceSiteId: text(grant.sourceSiteId) || null,
        sourceParentOrderId: text(grant.sourceParentOrderId) || null,
        sourceLineIdentity: text(grant.sourceLineIdentity) || null,
        addonVersion: nullableNumber(grant.addonVersion),
        creditConsumedSeconds: numberValue(grant.creditConsumedSeconds),
        creditReservedSeconds: numberValue(grant.creditReservedSeconds),
        reason: text(grant.reason), createdAt: text(first(grant, 'createdAt', 'created_at')),
        revokedAt: text(first(grant, 'revokedAt', 'revoked_at')) || null,
        actorName: text(first(grant, 'actorName', 'actor_name')) || null,
        actorEmail: text(first(grant, 'actorEmail', 'actor_email')) || null,
      };
    }),
    adjustments: arrayFrom(source.adjustments).map((value) => {
      const adjustment = record(value);
      const adjustmentValue = adjustment.value;
      return {
        id: text(adjustment.id), valueKey: text(first(adjustment, 'valueKey', 'value_key')),
        publicLabel: text(first(adjustment, 'publicLabel', 'public_label')),
        value: typeof adjustmentValue === 'boolean' || typeof adjustmentValue === 'number' || typeof adjustmentValue === 'string'
          ? adjustmentValue : '',
        priority: numberValue(adjustment.priority), state: text(adjustment.state),
        effectiveFrom: text(first(adjustment, 'effectiveFrom', 'effective_from')),
        effectiveUntil: text(first(adjustment, 'effectiveUntil', 'effective_until')) || null,
        reason: text(adjustment.reason), createdAt: text(first(adjustment, 'createdAt', 'created_at')),
        revokedAt: text(first(adjustment, 'revokedAt', 'revoked_at')) || null,
        actorName: text(first(adjustment, 'actorName', 'actor_name')) || null,
        actorEmail: text(first(adjustment, 'actorEmail', 'actor_email')) || null,
      };
    }),
    audit: arrayFrom(source.audit).map((value) => {
      const event = record(value);
      return {
        id: text(event.id), eventType: text(first(event, 'eventType', 'event_type')),
        reason: text(event.reason), metadata: record(event.metadata),
        createdAt: text(first(event, 'createdAt', 'created_at')),
        actorName: text(first(event, 'actorName', 'actor_name')) || null,
        actorEmail: text(first(event, 'actorEmail', 'actor_email')) || null,
      };
    }),
    planChanges: arrayFrom(source, 'planChanges', 'plan_changes').map((value) => {
      const change = record(value);
      return {
        id: text(change.id), kind: text(change.kind), status: text(change.status),
        sourceOfferCode: text(first(change, 'sourceOfferCode', 'source_offer_code')),
        targetOfferCode: text(first(change, 'targetOfferCode', 'target_offer_code')),
        requestReason: text(first(change, 'requestReason', 'request_reason')),
        requestedAt: text(first(change, 'requestedAt', 'requested_at')),
        scheduledFor: text(first(change, 'scheduledFor', 'scheduled_for')) || null,
        effectiveAt: text(first(change, 'effectiveAt', 'effective_at')) || null,
        amountPaidIdr: nullableNumber(first(change, 'amountPaidIdr', 'amount_paid_idr')),
        calculationNote: text(first(change, 'calculationNote', 'calculation_note')) || null,
        sourceSiteId: text(first(change, 'sourceSiteId', 'source_site_id')) || null,
        sourceOrderId: text(first(change, 'sourceOrderId', 'source_order_id')) || null,
        sourceOrderStatus: text(first(change, 'sourceOrderStatus', 'source_order_status')) || null,
        sourcePlanTitle: text(first(change, 'sourcePlanTitle', 'source_plan_title')),
        targetPlanTitle: text(first(change, 'targetPlanTitle', 'target_plan_title')),
        targetBillingInterval: text(first(change, 'targetBillingInterval', 'target_billing_interval')),
        nextRenewalPriceIdr: numberValue(first(change, 'nextRenewalPriceIdr', 'next_renewal_price_idr')),
        preservedPeriodEnd: text(first(change, 'preservedPeriodEnd', 'preserved_period_end')) || null,
        preservedUsagePeriodAnchor: text(first(change, 'preservedUsagePeriodAnchor', 'preserved_usage_period_anchor')) || null,
        remediationState: text(first(change, 'remediationState', 'remediation_state')) || null,
        remediationDeadline: text(first(change, 'remediationDeadline', 'remediation_deadline')) || null,
        remediationBlockers: normalizeHostedDowngradeBlockers(first(change, 'remediationBlockers', 'remediation_blockers')),
      };
    }),
  };
}

export function normalizeCaptionTrack(value: unknown): CaptionTrack {
  const source = record(value);
  return {
    id: text(source.id),
    videoId: text(first(source, 'videoId', 'video_id')),
    label: text(source.label, 'Captions'),
    languageCode: text(first(source, 'languageCode', 'language_code'), 'en'),
    kind: text(source.kind) === 'captions' ? 'captions' : 'subtitles',
    isDefault: booleanValue(first(source, 'isDefault', 'is_default')),
    source: text(source.source) === 'transcription' ? 'transcription' : 'upload',
    sizeBytes: numberValue(first(source, 'sizeBytes', 'size_bytes')),
    cueCount: numberValue(first(source, 'cueCount', 'cue_count')),
    durationSeconds: numberValue(first(source, 'durationSeconds', 'duration_seconds')),
    createdAt: text(first(source, 'createdAt', 'created_at')) || undefined,
    updatedAt: text(first(source, 'updatedAt', 'updated_at')) || undefined,
  };
}

export function normalizeCaptionCue(value: unknown): CaptionCue {
  const source = record(value);
  return {
    startSeconds: numberValue(first(source, 'startSeconds', 'start_seconds')),
    endSeconds: numberValue(first(source, 'endSeconds', 'end_seconds')),
    text: text(source.text),
    speaker: text(source.speaker) || null,
  };
}

export function normalizeTranscriptionJob(value: unknown): TranscriptionJob {
  const source = record(value);
  const status = text(source.status);
  const rawModel = text(source.model);
  const model = rawModel === 'gpt-4o-transcribe-diarize'
    ? 'gpt-4o-transcribe-diarize' : rawModel === 'whisper-1' ? 'whisper-1' : 'gpt-transcribe';
  return {
    id: text(source.id),
    videoId: text(first(source, 'videoId', 'video_id')),
    captionTrackId: nullableId(first(source, 'captionTrackId', 'caption_track_id')),
    provider: 'openai',
    model,
    languageCode: text(first(source, 'languageCode', 'language_code')) || null,
    label: text(source.label, 'Generated subtitles'),
    identifySpeakers: booleanValue(first(source, 'identifySpeakers', 'identify_speakers')),
    status: ['queued', 'running', 'succeeded', 'failed', 'cancelled'].includes(status)
      ? status as TranscriptionJob['status'] : 'failed',
    stage: text(source.stage, 'queued'),
    progress: numberValue(source.progress),
    attempts: numberValue(source.attempts),
    audioSeconds: nullableNumber(first(source, 'audioSeconds', 'audio_seconds')),
    inputTokens: nullableNumber(first(source, 'inputTokens', 'input_tokens')),
    outputTokens: nullableNumber(first(source, 'outputTokens', 'output_tokens')),
    totalTokens: nullableNumber(first(source, 'totalTokens', 'total_tokens')),
    costUsd: nullableNumber(first(source, 'costUsd', 'cost_usd')),
    errorCode: text(first(source, 'errorCode', 'error_code')) || null,
    errorMessage: text(first(source, 'errorMessage', 'error_message')) || null,
    createdAt: text(first(source, 'createdAt', 'created_at')),
    startedAt: text(first(source, 'startedAt', 'started_at')) || null,
    finishedAt: text(first(source, 'finishedAt', 'finished_at')) || null,
    updatedAt: text(first(source, 'updatedAt', 'updated_at')),
  };
}

export function normalizeVideoChapter(value: unknown): VideoChapter {
  const source = record(value);
  return {
    id: text(source.id),
    videoId: text(first(source, 'videoId', 'video_id')),
    title: text(source.title, 'Untitled chapter'),
    startSeconds: numberValue(first(source, 'startSeconds', 'start_seconds')),
    createdAt: text(first(source, 'createdAt', 'created_at')) || undefined,
    updatedAt: text(first(source, 'updatedAt', 'updated_at')) || undefined,
  };
}

export function normalizeUploadSession(payload: unknown): UploadSession {
  const source = record(unwrap(payload, 'uploadSession', 'upload_session'));
  const duplicateSource = source.duplicateVideo === null || source.duplicate_video === null
    ? null
    : record(first(source, 'duplicateVideo', 'duplicate_video'));
  return {
    id: text(source.id),
    filename: text(source.filename),
    sizeBytes: numberValue(first(source, 'sizeBytes', 'size_bytes')),
    status: text(source.status),
    videoId: nullableId(first(source, 'videoId', 'video_id')),
    expiresAt: text(first(source, 'expiresAt', 'expires_at')),
    createdAt: text(first(source, 'createdAt', 'created_at')),
    protectionMode: (() => {
      const value = text(first(source, 'protectionMode', 'protection_mode'));
      return value === 'r2_only' || value === 'protected' ? value : 'automatic';
    })(),
    estimatedOutputBytes: nullableNumber(first(source, 'estimatedOutputBytes', 'estimated_output_bytes')),
    duplicateVideo: duplicateSource && Object.keys(duplicateSource).length > 0 ? {
      id: text(duplicateSource.id),
      title: text(duplicateSource.title),
      sourceFilename: text(first(duplicateSource, 'sourceFilename', 'source_filename')) || null,
      sizeBytes: nullableNumber(first(duplicateSource, 'sizeBytes', 'size_bytes')),
      createdAt: text(first(duplicateSource, 'createdAt', 'created_at')),
    } : null,
  };
}

export function normalizeUploadSessions(payload: unknown): UploadSession[] {
  return arrayFrom(payload, 'uploadSessions', 'upload_sessions').map(normalizeUploadSession);
}

async function requestUploadSession(id: string): Promise<UploadSession> {
  return normalizeUploadSession(await request<unknown>(`/api/upload-sessions/${encodeURIComponent(id)}`));
}

export function normalizeUser(payload: unknown): User {
  const source = record(unwrap(payload, 'user', 'account', 'me'));
  const name = text(first(source, 'displayName', 'display_name', 'name'));
  const email = text(source.email);
  const token = text(first(source, 'csrfToken', 'csrf_token')) || text(first(record(payload), 'csrfToken', 'csrf_token'));
  if (token) csrfToken = token;
  return {
    id: text(source.id),
    email,
    displayName: name || email.split('@')[0] || 'User',
    role: text(source.role, 'viewer'),
  };
}

function rawFolder(value: unknown): Folder {
  const source = record(value);
  return {
    id: text(source.id),
    name: text(source.name, 'Untitled folder'),
    parentId: nullableId(first(source, 'parentId', 'parent_id', 'parentFolderId', 'parent_folder_id')),
    ownerUserId: text(first(source, 'ownerUserId', 'owner_user_id')) || undefined,
    ownerDisplayName: text(first(source, 'ownerDisplayName', 'owner_display_name')) || undefined,
    ownerEmail: text(first(source, 'ownerEmail', 'owner_email')) || undefined,
    children: arrayFrom(source, 'children', 'folders').map(rawFolder),
    videoCount: numberValue(first(source, 'videoCount', 'video_count', 'videos_count'), 0),
    imageCount: numberValue(first(source, 'imageCount', 'image_count'), 0),
    mediaCount: nullableNumber(first(source, 'mediaCount', 'media_count')) ?? undefined,
    childCount: numberValue(first(source, 'childCount', 'child_count', 'folders_count'), 0),
    trackedDirectBytes: numberValue(first(source, 'trackedDirectBytes', 'tracked_direct_bytes'), 0) || undefined,
    trackedSubtreeBytes: numberValue(first(source, 'trackedSubtreeBytes', 'tracked_subtree_bytes'), 0) || undefined,
    createdAt: text(first(source, 'createdAt', 'created_at')) || undefined,
  };
}

export function normalizeFolders(payload: unknown): Folder[] {
  const values = arrayFrom(payload, 'folders', 'items', 'tree', 'results');
  const folders = values.map(rawFolder).filter((folder) => folder.id);
  if (folders.some((folder) => folder.children.length)) return folders;

  const map = new Map(folders.map((folder) => [folder.id, { ...folder, children: [] as Folder[] }]));
  const roots: Folder[] = [];
  for (const folder of map.values()) {
    const parent = folder.parentId ? map.get(folder.parentId) : undefined;
    if (parent) parent.children.push(folder);
    else roots.push(folder);
  }
  const sort = (items: Folder[]) => {
    items.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    items.forEach((item) => sort(item.children));
  };
  sort(roots);
  return roots;
}

export function normalizeVideo(value: unknown): VideoItem {
  const source = record(value);
  const job = record(first(source, 'job', 'transcodeJob', 'transcode_job'));
  const progress = numberValue(first(source, 'progress', 'transcodeProgress', 'transcode_progress') ?? job.progress, 0);
  const id = text(source.id);
  const status = text(source.status, text(job.status, 'queued')).toLowerCase();
  const updatedAt = text(first(source, 'updatedAt', 'updated_at')) || undefined;
  const hasThumbnail = booleanValue(first(source, 'hasThumbnail', 'has_thumbnail'));
  const rawThumbnailUrl = text(first(source, 'thumbnailUrl', 'thumbnail_url', 'posterUrl', 'poster_url', 'thumbnail'))
    || (hasThumbnail && id ? `/thumbnails/${id}.jpg` : undefined);
  const thumbnailUrl = rawThumbnailUrl && updatedAt
    ? `${rawThumbnailUrl}${rawThumbnailUrl.includes('?') ? '&' : '?'}v=${encodeURIComponent(updatedAt)}`
    : rawThumbnailUrl;
  const watermarkEnabled = first(source, 'watermarkEnabled', 'watermark_enabled');
  const watermarkMotion = text(first(source, 'watermarkMotion', 'watermark_motion'));
  const watermarkPosition = text(first(source, 'watermarkPosition', 'watermark_position'));
  const primaryColor = text(first(source, 'playerPrimaryColor', 'player_primary_color', 'primaryColor', 'primary_color'));
  const captionTextColor = text(first(source, 'captionTextColor', 'caption_text_color'));
  const captionBackgroundColor = text(first(source, 'captionBackgroundColor', 'caption_background_color'));
  const captionSize = text(first(source, 'captionSize', 'caption_size'));
  return {
    id,
    title: text(first(source, 'title', 'name'), text(first(source, 'sourceFilename', 'source_filename', 'filename'), 'Untitled video')),
    ownerUserId: text(first(source, 'ownerUserId', 'owner_user_id')) || undefined,
    ownerDisplayName: text(first(source, 'ownerDisplayName', 'owner_display_name')) || undefined,
    ownerEmail: text(first(source, 'ownerEmail', 'owner_email')) || undefined,
    description: text(source.description),
    folderId: nullableId(first(source, 'folderId', 'folder_id')),
    sourceFilename: text(first(source, 'sourceFilename', 'source_filename', 'originalFilename', 'original_filename', 'filename')) || null,
    sourceKind: text(first(source, 'sourceKind', 'source_kind')) || undefined,
    sourceProvider: text(first(source, 'sourceProvider', 'source_provider')) || undefined,
    processingStage: text(first(source, 'processingStage', 'processing_stage', 'stage') ?? job.stage).toLowerCase() || undefined,
    status,
    progress: Math.min(100, Math.max(0, progress)),
    durationSeconds: numberValue(first(source, 'durationSeconds', 'duration_seconds', 'duration'), NaN) || undefined,
    width: numberValue(source.width, NaN) || undefined,
    height: numberValue(source.height, NaN) || undefined,
    sizeBytes: numberValue(first(source, 'sizeBytes', 'size_bytes', 'sourceSizeBytes', 'source_size_bytes', 'sourceSize', 'source_size'), NaN) || undefined,
    importedSizeBytes: numberValue(first(source, 'importedSizeBytes', 'imported_size_bytes'), NaN) || undefined,
    thumbnailUrl,
    playbackUrl: text(first(source, 'playbackUrl', 'playback_url', 'hlsUrl', 'hls_url', 'url')) || undefined,
    errorMessage: text(first(source, 'errorMessage', 'error_message', 'lastError', 'last_error') ?? job.last_error) || undefined,
    createdAt: text(first(source, 'createdAt', 'created_at')) || undefined,
    updatedAt,
    trashedAt: text(first(source, 'trashedAt', 'trashed_at')) || undefined,
    recoverableUntil: text(first(source, 'recoverableUntil', 'recoverable_until')) || undefined,
    protectionMode: (() => {
      const value = text(first(source, 'protectionMode', 'protection_mode'));
      return value === 'r2_only' || value === 'protected' ? value : value === 'automatic' ? value : undefined;
    })(),
    protectionState: (() => {
      const value = text(first(source, 'protectionState', 'protection_state'));
      return ['r2_only', 'protected_reserved', 'protected_copying', 'protected_verified', 'protected_retryable_failure'].includes(value)
        ? value as NonNullable<VideoItem['protectionState']> : undefined;
    })(),
    protectionExpectedBytes: nullableNumber(first(source, 'protectionExpectedBytes', 'protection_expected_bytes')) ?? undefined,
    protectionActualBytes: nullableNumber(first(source, 'protectionActualBytes', 'protection_actual_bytes')) ?? undefined,
    protectionAttempts: nullableNumber(first(source, 'protectionAttempts', 'protection_attempts')) ?? undefined,
    protectionVerifiedAt: text(first(source, 'protectionVerifiedAt', 'protection_verified_at')) || undefined,
    protectionUpdatedAt: text(first(source, 'protectionUpdatedAt', 'protection_updated_at')) || undefined,
    embedPolicy: {
      allowedOrigins: Array.isArray(first(source, 'embedAllowedOrigins', 'embed_allowed_origins'))
        ? (first(source, 'embedAllowedOrigins', 'embed_allowed_origins') as unknown[]).filter((item): item is string => typeof item === 'string')
        : [],
      allowedExactPaths: Array.isArray(first(source, 'embedAllowedExactPaths', 'embed_allowed_exact_paths'))
        ? (first(source, 'embedAllowedExactPaths', 'embed_allowed_exact_paths') as unknown[]).filter((item): item is string => typeof item === 'string')
        : [],
      allowedPathPrefixes: Array.isArray(first(source, 'embedAllowedPathPrefixes', 'embed_allowed_path_prefixes'))
        ? (first(source, 'embedAllowedPathPrefixes', 'embed_allowed_path_prefixes') as unknown[]).filter((item): item is string => typeof item === 'string')
        : [],
    },
    playerSettings: {
      watermarkEnabled: typeof watermarkEnabled === 'boolean' ? watermarkEnabled : true,
      ...(['disabled','optional','required'].includes(text(first(source,'viewerIdentityMode','viewer_identity_mode')))?{viewerIdentityMode:text(first(source,'viewerIdentityMode','viewer_identity_mode')) as 'disabled'|'optional'|'required'}:{}),
      watermarkText: text(first(source, 'watermarkText', 'watermark_text'), 'DS {code} · {time}'),
      watermarkMotion: watermarkMotion === 'fixed' ? 'fixed' : 'moving',
      watermarkPosition: ['top-left', 'top-right', 'center-left', 'center-right', 'bottom-left', 'bottom-right'].includes(watermarkPosition)
        ? watermarkPosition as NonNullable<VideoItem['playerSettings']>['watermarkPosition']
        : 'top-left',
      primaryColor: /^#[0-9a-f]{6}$/i.test(primaryColor) ? primaryColor.toUpperCase() : '#5EE6BD',
      captionTextColor: /^#[0-9a-f]{6}$/i.test(captionTextColor) ? captionTextColor.toUpperCase() : '#FFFFFF',
      captionBackgroundColor: /^#[0-9a-f]{6}$/i.test(captionBackgroundColor) ? captionBackgroundColor.toUpperCase() : '#000000',
      captionSize: captionSize === 'small' || captionSize === 'large' ? captionSize : 'medium',
    },
  };
}

export function normalizeVideos(payload: unknown): VideoListResult {
  const videos = arrayFrom(payload, 'videos', 'items', 'results').map(normalizeVideo).filter((video) => video.id);
  const outer = record(payload);
  const source = record(outer.data ?? payload);
  const pagination = record(first(source, 'pagination', 'meta') ?? first(outer, 'pagination', 'meta'));
  return {
    videos,
    total: numberValue(first(source, 'total', 'count', 'totalCount', 'total_count') ?? first(pagination, 'total', 'count'), videos.length),
  };
}

export function imageLibraryItem(image: ImageItem): VideoItem {
  const protection = { not_protected: 'r2_only', pending: 'protected_copying', verified: 'protected_verified', failed: 'protected_retryable_failure' } as const;
  return { id: image.id, mediaKind: 'image', image, title: image.name, folderId: image.folderId,
    ownerUserId: image.ownerUserId, ownerDisplayName: image.ownerDisplayName, ownerEmail: image.ownerEmail,
    sourceFilename: image.originalFilename, status: image.status, progress: image.status === 'ready' ? 100 : 0,
    sizeBytes: image.sourceBytes, width: image.width ?? undefined, height: image.height ?? undefined,
    thumbnailUrl: image.variants.find(variant => variant.preset === 240)?.url,
    errorMessage: image.error ?? undefined, createdAt: image.createdAt, updatedAt: image.updatedAt,
    trashedAt: image.trashedAt ?? undefined, protectionMode: image.protectionMode,
    protectionState: protection[image.protectionState] };
}

export function normalizeStats(payload: unknown): Stats {
  const source = record(unwrap(payload, 'stats', 'statistics'));
  const videos = record(source.videos);
  const images = record(source.images);
  const storage = record(first(source, 'storage', 'disk'));
  const jobs = record(source.jobs);
  const processing = record(source.processing);
  const thresholds = record(storage.thresholds);
  const backup = record(source.backup);
  const totalBytes = numberValue(
    first(source, 'totalBytes', 'total_bytes', 'mediaBytes', 'media_bytes')
      ?? first(storage, 'mediaBytes', 'media_bytes', 'sourceBytes', 'source_bytes', 'usedByMedia', 'used_by_media'),
  );
  const usedBytes = numberValue(first(source, 'usedBytes', 'used_bytes') ?? first(storage, 'usedBytes', 'used_bytes', 'used'), totalBytes);
  const freeBytes = numberValue(first(source, 'freeBytes', 'free_bytes') ?? first(storage, 'freeBytes', 'free_bytes', 'availableBytes', 'available_bytes', 'available'));
  const capacityBytes = numberValue(first(source, 'capacityBytes', 'capacity_bytes') ?? first(storage, 'capacityBytes', 'capacity_bytes', 'totalBytes', 'total_bytes', 'total'), usedBytes + freeBytes);
  const calculatedPercent = capacityBytes ? (usedBytes / capacityBytes) * 100 : 0;
  const diskLevel = text(storage.level, 'normal');
  return {
    images: Object.keys(images).length ? { total: numberValue(images.total), ready: numberValue(images.ready), processing: numberValue(images.processing), failed: numberValue(images.failed) } : undefined,
    storageScope: text(first(storage, 'scope')) === 'member' ? 'member' : 'installation',
    videoCount: numberValue(first(source, 'videoCount', 'video_count') ?? first(videos, 'total', 'count')),
    readyCount: numberValue(first(source, 'readyCount', 'ready_count') ?? videos.ready),
    processingCount: numberValue(first(source, 'processingCount', 'processing_count') ?? first(videos, 'processing', 'transcoding')),
    failedCount: numberValue(first(source, 'failedCount', 'failed_count') ?? videos.failed),
    totalBytes,
    usedBytes,
    freeBytes,
    capacityBytes,
    activeJobs: numberValue(first(source, 'activeJobs', 'active_jobs') ?? first(jobs, 'active', 'running')),
    processingMode: processingMode(first(source, 'processingMode', 'processing_mode') ?? processing.mode),
    onlineWorkers: numberValue(first(source, 'onlineWorkers', 'online_workers') ?? first(processing, 'onlineWorkers', 'online_workers')),
    totalActiveWorkers: numberValue(first(source, 'totalActiveWorkers', 'total_active_workers') ?? first(processing, 'totalActiveWorkers', 'total_active_workers')),
    queuedJobs: numberValue(first(source, 'queuedJobs', 'queued_jobs') ?? first(processing, 'queuedJobs', 'queued_jobs') ?? jobs.queued),
    diskPercent: numberValue(storage.percent, calculatedPercent),
    diskLevel: ['normal', 'warning', 'critical', 'reject'].includes(diskLevel) ? diskLevel as Stats['diskLevel'] : 'normal',
    diskThresholds: {
      warningPercent: numberValue(first(thresholds, 'warningPercent', 'warning_percent'), 80),
      criticalPercent: numberValue(first(thresholds, 'criticalPercent', 'critical_percent'), 90),
      rejectPercent: numberValue(first(thresholds, 'rejectPercent', 'reject_percent'), 95),
    },
    backup: Object.keys(backup).length ? {
      status: text(backup.status, 'unknown'),
      phase: text(backup.phase, 'unknown'),
      message: text(backup.message, 'No backup details available.'),
      startedAt: text(first(backup, 'startedAt', 'started_at')) || undefined,
      finishedAt: text(first(backup, 'finishedAt', 'finished_at')) || undefined,
      verificationStatus: text(first(record(backup.verification), 'status')) || undefined,
    } : null,
  };
}

export function normalizeMediaStorage(payload: unknown): MediaStorageOverview {
  const source = record(unwrap(payload, 'mediaStorage', 'media_storage'));
  const r2 = record(source.r2);
  const usage = record(source.usage);
  const localUsage = record(usage.local);
  const r2Usage = record(usage.r2);
  const defaultBackend = text(first(source, 'defaultBackend', 'default_backend'));
  const storageStatus = text(first(r2, 'storageStatus', 'storage_status'));
  const workerStatus = text(first(r2, 'workerStatus', 'worker_status'));
  return {
    defaultBackend: defaultBackend === 'r2' ? 'r2' : 'local',
    credentialEncryptionAvailable: booleanValue(first(source, 'credentialEncryptionAvailable', 'credential_encryption_available')),
    playbackSigningConfigured: booleanValue(first(source, 'playbackSigningConfigured', 'playback_signing_configured')),
    r2PublishingAvailable: booleanValue(first(source, 'r2PublishingAvailable', 'r2_publishing_available')),
    r2: {
      configured: booleanValue(r2.configured),
      accountId: text(first(r2, 'accountId', 'account_id')) || null,
      bucketName: text(first(r2, 'bucketName', 'bucket_name')) || null,
      accessKeyId: text(first(r2, 'accessKeyId', 'access_key_id')) || null,
      secretConfigured: booleanValue(first(r2, 'secretConfigured', 'secret_configured')),
      mediaHostname: text(first(r2, 'mediaHostname', 'media_hostname')) || null,
      workerUrl: text(first(r2, 'workerUrl', 'worker_url')) || null,
      storageStatus: ['connected', 'attention_required'].includes(storageStatus)
        ? storageStatus as MediaStorageOverview['r2']['storageStatus'] : 'not_configured',
      workerStatus: ['setup_required', 'connected'].includes(workerStatus)
        ? workerStatus as MediaStorageOverview['r2']['workerStatus'] : 'not_configured',
      workerVersion: text(first(r2, 'workerVersion', 'worker_version')) || null,
      lastVerifiedAt: text(first(r2, 'lastVerifiedAt', 'last_verified_at')) || null,
      lastError: text(first(r2, 'lastError', 'last_error')) || null,
    },
    usage: {
      local: { bytes: numberValue(localUsage.bytes), videos: numberValue(localUsage.videos) },
      r2: { bytes: numberValue(r2Usage.bytes), videos: numberValue(r2Usage.videos) },
    },
  };
}

export function normalizeSourceCleanupOverview(payload: unknown): SourceCleanupOverview {
  const source = record(unwrap(payload, 'overview', 'sourceCleanup', 'source_cleanup'));
  const summary = record(source.summary);
  return {
    enabled: booleanValue(source.enabled),
    retryIntervalMinutes: numberValue(first(source, 'retryIntervalMinutes', 'retry_interval_minutes'), 15),
    summary: {
      total: numberValue(summary.total),
      bytes: numberValue(summary.bytes),
      failed: numberValue(summary.failed),
      waiting: numberValue(summary.waiting),
    },
    items: arrayFrom(source, 'items').map((value) => {
      const item = record(value);
      return {
        videoId: text(first(item, 'videoId', 'video_id')),
        videoTitle: text(first(item, 'videoTitle', 'video_title'), 'Untitled video'),
        originalFilename: text(first(item, 'originalFilename', 'original_filename')),
        sourceBytes: numberValue(first(item, 'sourceBytes', 'source_bytes')),
        memberName: text(first(item, 'memberName', 'member_name'), 'Member'),
        memberEmail: text(first(item, 'memberEmail', 'member_email')),
        publicationId: text(first(item, 'publicationId', 'publication_id')),
        publicationActivatedAt: text(first(item, 'publicationActivatedAt', 'publication_activated_at')),
        attemptedAt: text(first(item, 'attemptedAt', 'attempted_at')) || null,
        error: text(item.error) || null,
        failures: numberValue(item.failures),
        activeTranscription: booleanValue(first(item, 'activeTranscription', 'active_transcription')),
      };
    }),
  };
}

function normalizeProcessingWorker(value: unknown): ProcessingWorker {
  const source = record(value);
  const activeJob = record(source.activeJob);
  return {
    id: text(source.id),
    name: text(source.name).trim() || 'Unnamed worker',
    status: text(source.status, 'offline').toLowerCase(),
    online: booleanValue(source.online),
    capabilities: Array.isArray(source.capabilities)
      ? [...new Set(source.capabilities.map((capability) => text(capability).trim()).filter(Boolean))]
      : [],
    version: text(source.version),
    lastSeenAt: text(first(source, 'lastSeenAt', 'last_seen_at')) || null,
    revokedAt: text(first(source, 'revokedAt', 'revoked_at')) || null,
    createdAt: text(first(source, 'createdAt', 'created_at')),
    updatedAt: text(first(source, 'updatedAt', 'updated_at')),
    activeJob: text(first(activeJob, 'jobId', 'job_id')) ? {
      jobId: text(first(activeJob, 'jobId', 'job_id')),
      videoId: text(first(activeJob, 'videoId', 'video_id')),
      kind: text(first(activeJob, 'kind', 'jobKind', 'job_kind')) === 'transcription' ? 'transcription' : 'transcode',
      stage: text(activeJob.stage),
      leaseExpiresAt: text(first(activeJob, 'leaseExpiresAt', 'lease_expires_at')) || null,
      cancelRequested: booleanValue(first(activeJob, 'cancelRequested', 'cancel_requested')),
    } : null,
  };
}

function normalizeHostedProcessingOverview(payload: unknown): HostedProcessingOverview {
  const source = record(payload);
  const control = record(source.control);
  const heavy = record(source.heavyWork);
  const queued = arrayFrom(source, 'queued').map((value) => {
    const row = record(value);
    return {
      kind: text(row.kind) === 'transcription' ? 'transcription' as const : 'transcode' as const,
      jobId: text(first(row, 'jobId', 'job_id')),
      videoId: text(first(row, 'videoId', 'video_id')),
      ownerUserId: text(first(row, 'ownerUserId', 'owner_user_id')),
      memberName: text(first(row, 'memberName', 'member_name')),
      memberEmail: text(first(row, 'memberEmail', 'member_email')),
      videoTitle: text(first(row, 'videoTitle', 'video_title'), 'Untitled video'),
      folderPath: text(first(row, 'folderPath', 'folder_path'), 'Library root'),
      createdAt: text(first(row, 'createdAt', 'created_at')),
      queueAction: ((): 'convert_now' | 'owner_retry' | null => {
        const action = text(first(row, 'queueAction', 'queue_action'));
        return action === 'convert_now' || action === 'owner_retry' ? action : null;
      })(),
      manualRequestedAt: text(first(row, 'manualRequestedAt', 'manual_requested_at')) || null,
    };
  }).filter((job) => job.jobId && job.videoId);
  const recentActions = arrayFrom(source, 'recentActions', 'recent_actions').map((value) => {
    const row = record(value);
    const rawJobKind = text(first(row, 'jobKind', 'job_kind'));
    const jobKind: 'transcode' | 'transcription' | null = rawJobKind === 'transcode' || rawJobKind === 'transcription' ? rawJobKind : null;
    return {
      id: text(row.id),
      eventType: text(first(row, 'eventType', 'event_type')),
      actorName: text(first(row, 'actorName', 'actor_name'), 'Owner'),
      memberName: text(first(row, 'memberName', 'member_name')),
      memberEmail: text(first(row, 'memberEmail', 'member_email')),
      jobKind,
      jobId: text(first(row, 'jobId', 'job_id')) || null,
      videoId: text(first(row, 'videoId', 'video_id')) || null,
      videoTitle: text(first(row, 'videoTitle', 'video_title'), 'Queue control'),
      reason: text(row.reason),
      createdAt: text(first(row, 'createdAt', 'created_at')),
    };
  }).filter((event) => event.id);
  return {
    control: {
      pauseAfterCurrent: booleanValue(first(control, 'pauseAfterCurrent', 'pause_after_current')),
      updatedAt: text(first(control, 'updatedAt', 'updated_at')) || null,
    },
    heavyWork: text(first(heavy, 'jobId', 'job_id')) ? {
      jobId: text(first(heavy, 'jobId', 'job_id')),
      videoId: '',
      kind: text(first(heavy, 'jobKind', 'job_kind')) === 'transcription' ? 'transcription' : 'transcode',
      stage: 'running',
      leaseExpiresAt: null,
      cancelRequested: false,
      ownerUserId: text(first(heavy, 'ownerUserId', 'owner_user_id')),
      claimedAt: text(first(heavy, 'claimedAt', 'claimed_at')),
      updatedAt: text(first(heavy, 'updatedAt', 'updated_at')),
      queueAction: ((): 'convert_now' | 'owner_retry' | null => {
        const action = text(first(heavy, 'queueAction', 'queue_action'));
        return action === 'convert_now' || action === 'owner_retry' ? action : null;
      })(),
      manualRequestedAt: text(first(heavy, 'manualRequestedAt', 'manual_requested_at')) || null,
    } : null,
    queued,
    recentActions,
  };
}

export function normalizeProcessingAvailability(payload: unknown): ProcessingAvailability {
  const availability = record(unwrap(payload, 'availability'));
  const availabilityMode = text(availability.mode);
  const availabilityReason = text(availability.reason);
  return {
    mode: availabilityMode === 'scheduled' || availabilityMode === 'paused' ? availabilityMode : 'always',
    timezone: text(availability.timezone, 'Asia/Jakarta'),
    windowStart: text(first(availability, 'windowStart', 'window_start'), '00:00'),
    windowEnd: text(first(availability, 'windowEnd', 'window_end'), '06:00'),
    manualOverrideUntil: text(first(availability, 'manualOverrideUntil', 'manual_override_until')) || null,
    claimEligible: booleanValue(first(availability, 'claimEligible', 'claim_eligible')),
    reason: (['always_available', 'inside_schedule', 'outside_schedule', 'paused', 'manual_override'].includes(availabilityReason)
      ? availabilityReason : 'always_available') as ProcessingAvailability['reason'],
    nextClaimAt: text(first(availability, 'nextClaimAt', 'next_claim_at')) || null,
    windowEndsAt: text(first(availability, 'windowEndsAt', 'window_ends_at')) || null,
    updatedAt: text(first(availability, 'updatedAt', 'updated_at')),
  };
}

export function normalizeProcessingWorkers(payload: unknown): ProcessingWorkersOverview {
  const source = record(unwrap(payload, 'processingWorkers', 'processing_workers'));
  const counts = record(source.counts);
  return {
    processingMode: processingMode(first(source, 'processingMode', 'processing_mode')),
    jobClaimEnabled: booleanValue(first(source, 'jobClaimEnabled', 'job_claim_enabled')),
    availability: normalizeProcessingAvailability(source.availability),
    workers: arrayFrom(source, 'workers', 'items').map(normalizeProcessingWorker).filter((worker) => worker.id),
    counts: {
      onlineWorkers: numberValue(first(counts, 'onlineWorkers', 'online_workers')),
      totalActiveWorkers: numberValue(first(counts, 'totalActiveWorkers', 'total_active_workers')),
      queuedJobs: numberValue(first(counts, 'queuedJobs', 'queued_jobs')),
    },
    serverTime: text(first(source, 'serverTime', 'server_time')),
  };
}

export function normalizeWorkerEnrollment(payload: unknown): WorkerEnrollment {
  const source = record(unwrap(payload, 'enrollment'));
  const code = text(source.code);
  const expiresAt = text(first(source, 'expiresAt', 'expires_at'));
  if (!code || !expiresAt) throw new ApiError('The server returned an invalid enrollment code.', 502, payload);
  return { code, expiresAt };
}

function safeAuthorizationUrl(value: unknown): string | undefined {
  const candidate = text(value).trim();
  if (!candidate) return undefined;
  try {
    const url = new URL(candidate);
    return url.protocol === 'https:' && url.hostname === 'accounts.google.com' && !url.port &&
      !url.username && !url.password && url.pathname === '/o/oauth2/v2/auth' && !url.hash
      ? url.href
      : undefined;
  } catch {
    return undefined;
  }
}

function safeRecoveryInstructionsUrl(value: unknown): string | undefined {
  const candidate = text(value).trim();
  if (!candidate || typeof window === 'undefined') return undefined;
  try {
    const url = new URL(candidate, window.location.origin);
    if (url.origin !== window.location.origin || url.username || url.password || url.search || url.hash) return undefined;
    return url.href;
  } catch {
    return undefined;
  }
}

export function sanitizeBackupMessage(value: unknown): string | undefined {
  const message = text(value).trim();
  if (!message) return undefined;
  const sensitive = /(password|passphrase|secret|access[_ -]?token|refresh[_ -]?token|client[_ -]?secret)\s*[:=]/i;
  const repositoryLocation = /(?:^|\s)(?:\/[\w.-]+){2,}|[a-z]:\\|\b(?:rest|rclone|s3|sftp|b2|azure):[^\s]+/i;
  return sensitive.test(message) || repositoryLocation.test(message)
    ? 'Additional controller details were redacted.'
    : message;
}

function optionalNumber(value: unknown): number | undefined {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function normalizeEncryptedRecoveryKit(value: unknown) {
  const source = record(value);
  const encryptedKey = text(source.encryptedKey);
  const iv = text(source.iv);
  const ciphertext = text(source.ciphertext);
  const tag = text(source.tag);
  if (source.schemaVersion !== 1 || source.algorithm !== 'RSA-OAEP-3072+AES-256-GCM' ||
      !/^[A-Za-z0-9_-]{400,1024}$/.test(encryptedKey) || !/^[A-Za-z0-9_-]{16,32}$/.test(iv) ||
      !/^[A-Za-z0-9_-]{100,32768}$/.test(ciphertext) || !/^[A-Za-z0-9_-]{20,32}$/.test(tag)) return undefined;
  return {
    schemaVersion: 1 as const,
    algorithm: 'RSA-OAEP-3072+AES-256-GCM' as const,
    encryptedKey,
    iv,
    ciphertext,
    tag,
  };
}

function normalizeBackupOperation(value: unknown): BackupOperation {
  const outer = record(value);
  const source = record(unwrap(value, 'operation'));
  const result = record(source.result);
  const progress = record(source.progress);
  const rawProgress = first(source, 'progressPercent', 'progress_percent') ?? first(progress, 'percent', 'percentage', 'value') ?? source.progress;
  const parsedProgress = optionalNumber(rawProgress);
  const rawTransitionSubstate = text(first(source, 'transitionSubstate', 'transition_substate', 'reactivationTransitionSubstate', 'reactivation_transition_substate') ?? first(result, 'transitionSubstate', 'transition_substate'));
  const transitionSubstate = /^[a-z][a-z0-9_]{0,63}$/.test(rawTransitionSubstate) ? rawTransitionSubstate : undefined;
  return {
    id: text(first(source, 'id', 'operationId', 'operation_id')),
    type: text(first(source, 'type', 'operationType', 'operation_type'), 'unknown'),
    state: text(first(source, 'state', 'status'), 'queued').toLowerCase(),
    generationId: text(first(source, 'generationId', 'generation_id', 'repositoryGenerationId', 'repository_generation_id')) || undefined,
    settingsRevision: optionalNumber(first(source, 'settingsRevision', 'settings_revision')),
    phase: text(first(source, 'phase', 'currentPhase', 'current_phase')) || undefined,
    transitionSubstate,
    message: sanitizeBackupMessage(first(source, 'message', 'statusMessage', 'status_message', 'errorMessage', 'error_message')),
    progress: parsedProgress === undefined ? null : Math.min(100, Math.max(0, parsedProgress)),
    cancellable: booleanValue(first(source, 'cancellable', 'canCancel', 'can_cancel', 'cancellationAllowed', 'cancellation_allowed')),
    createdAt: text(first(source, 'createdAt', 'created_at', 'queuedAt', 'queued_at', 'requestedAt', 'requested_at')) || undefined,
    startedAt: text(first(source, 'startedAt', 'started_at')) || undefined,
    finishedAt: text(first(source, 'finishedAt', 'finished_at', 'completedAt', 'completed_at')) || undefined,
    authorizationUrl: safeAuthorizationUrl(
      first(source, 'authorizationUrl', 'authorization_url')
        ?? first(result, 'authorizationUrl', 'authorization_url')
        ?? first(outer, 'authorizationUrl', 'authorization_url')
        ?? first(record(outer.data), 'authorizationUrl', 'authorization_url'),
    ),
    repositoryId: /^[0-9a-f]{64}$/.test(text(first(result, 'repositoryId', 'repository_id'))) ? text(first(result, 'repositoryId', 'repository_id')) : undefined,
    recoveryKit: normalizeEncryptedRecoveryKit(first(result, 'recoveryKit', 'recovery_kit')),
  };
}

function normalizeBackupSnapshot(value: unknown): BackupSnapshot {
  const source = record(value);
  const verification = record(source.verification);
  return {
    id: text(first(source, 'id', 'snapshotId', 'snapshot_id')),
    createdAt: text(first(source, 'createdAt', 'created_at', 'snapshotCreatedAt', 'snapshot_created_at', 'time', 'timestamp')) || undefined,
    sizeBytes: optionalNumber(first(source, 'sizeBytes', 'size_bytes', 'dataSizeBytes', 'data_size_bytes')),
    durationSeconds: optionalNumber(first(source, 'durationSeconds', 'duration_seconds')),
    verificationStatus: text(first(source, 'verificationStatus', 'verification_status') ?? verification.status, 'unknown').toLowerCase(),
    retentionClass: text(first(source, 'retentionClass', 'retention_class', 'policyClass', 'policy_class')) || undefined,
  };
}

function normalizeBackupRestoreTest(value: unknown): BackupRestoreTest {
  const source = record(value);
  return {
    id: text(first(source, 'id', 'testId', 'test_id')),
    state: text(first(source, 'state', 'status'), 'unknown').toLowerCase(),
    sampleName: text(first(source, 'sampleName', 'sample_name', 'mediaName', 'media_name')) || undefined,
    message: sanitizeBackupMessage(first(source, 'message', 'statusMessage', 'status_message')),
    createdAt: text(first(source, 'createdAt', 'created_at', 'startedAt', 'started_at')) || undefined,
    finishedAt: text(first(source, 'finishedAt', 'finished_at', 'completedAt', 'completed_at')) || undefined,
  };
}

const lifecycleActionAliases: Record<BackupLifecycleOperationType, string[]> = {
  reauthorize_google: ['reauthorize_google', 'reauthorizeGoogle', 'reauthorize'],
  reset_google_before_initialization: ['reset_google_before_initialization', 'resetGoogleBeforeInitialization', 'chooseDifferentAccount', 'choose_different_account'],
  disconnect_google: ['disconnect_google', 'disconnectGoogle', 'disconnect'],
  detach_repository_generation: ['detach_repository_generation', 'detachRepositoryGeneration', 'startFresh', 'start_fresh'],
  reactivate_repository_generation: ['reactivate_repository_generation', 'reactivateRepositoryGeneration', 'reactivateArchivedIntegration', 'reactivate_archived_integration'],
};

function lifecycleActionSource(actions: JsonRecord, type: BackupLifecycleOperationType): unknown {
  for (const alias of lifecycleActionAliases[type]) {
    if (actions[alias] !== undefined) return actions[alias];
    const capability = `can${alias[0]?.toUpperCase()}${alias.slice(1)}`;
    if (actions[capability] !== undefined) return actions[capability];
  }
  return undefined;
}

function normalizeLifecycleAction(value: unknown, fallback: BackupLifecycleAction): BackupLifecycleAction {
  if (typeof value === 'boolean') return { available: value, blockedReason: value ? undefined : fallback.blockedReason };
  const source = record(value);
  if (!Object.keys(source).length) return fallback;
  const availableValue = first(source, 'available', 'allowed', 'enabled', 'canRun', 'can_run');
  const available = booleanValue(availableValue, fallback.available);
  return {
    available,
    blockedReason: available ? undefined : sanitizeBackupMessage(first(source, 'blockedReason', 'blocked_reason', 'reason', 'message')) ?? fallback.blockedReason,
  };
}

function boundedText(value: unknown, maximum: number, fallback = ''): string {
  const valueText = text(value).trim();
  return valueText && valueText.length <= maximum ? valueText : fallback;
}

function boundedDisplayText(value: unknown, maximum: number, fallback: string): string {
  const valueText = boundedText(value, maximum);
  return valueText && !/[\u0000-\u001f\u007f-\u009f\u202a-\u202e\u2066-\u2069]/u.test(valueText)
    ? valueText
    : fallback;
}

function safeTimestamp(value: unknown): string | undefined {
  const timestamp = boundedText(value, 64);
  return timestamp && Number.isFinite(Date.parse(timestamp)) ? timestamp : undefined;
}

function boundedBackupMessage(value: unknown, maximum = 500): string | undefined {
  const message = sanitizeBackupMessage(value);
  return message && message.length <= maximum ? message : undefined;
}

function normalizeArchivedIntegration(value: unknown) {
  const source = record(value);
  const id = boundedText(first(source, 'id', 'generationId', 'generation_id'), 128);
  const repositoryIdPrefix = boundedText(first(source, 'repositoryIdPrefix', 'repository_id_prefix'), 64);
  const reactivationAvailable = booleanValue(first(source, 'reactivationAvailable', 'reactivation_available', 'available'));
  return {
    id,
    number: (() => {
      const number = optionalNumber(first(source, 'number', 'generationNumber', 'generation_number'));
      return number !== undefined && Number.isSafeInteger(number) && number > 0 ? number : undefined;
    })(),
    provider: boundedText(source.provider, 64, 'google_drive').toLowerCase(),
    accountEmail: boundedDisplayText(first(source, 'accountEmail', 'account_email'), 320, 'Google account'),
    folderDisplayName: boundedDisplayText(first(source, 'folderDisplayName', 'folder_display_name', 'folderName', 'folder_name'), 180, 'Google Drive folder'),
    repositoryIdPrefix: /^[0-9a-f]{4,64}$/.test(repositoryIdPrefix) ? repositoryIdPrefix : '',
    detachedAt: safeTimestamp(first(source, 'detachedAt', 'detached_at')),
    lastSuccessfulBackupAt: safeTimestamp(first(source, 'lastSuccessfulBackupAt', 'last_successful_backup_at')),
    lastVerifiedBackupAt: safeTimestamp(first(source, 'lastVerifiedBackupAt', 'last_verified_backup_at')),
    snapshotCount: Math.min(1_000_000_000, Math.max(0, numberValue(first(source, 'snapshotCount', 'snapshot_count')))),
    restoreTestCount: Math.min(1_000_000_000, Math.max(0, numberValue(first(source, 'restoreTestCount', 'restore_test_count')))),
    reactivationAvailable,
    blockedReason: reactivationAvailable
      ? undefined
      : boundedBackupMessage(first(source, 'blockedReason', 'blocked_reason', 'reason')) ?? 'This archived integration cannot be reactivated right now.',
  };
}

export function normalizeBackupOverview(payload: unknown): BackupOverview {
  const source = record(unwrap(payload, 'overview', 'backup'));
  const controller = record(source.controller);
  const config = record(first(source, 'config', 'configuration', 'settings'));
  const google = record(first(config, 'google', 'googleDrive', 'google_drive', 'provider'));
  const destination = record(first(config, 'destination', 'driveFolder', 'drive_folder'));
  const schedule = record(config.schedule);
  const retention = record(config.retention);
  const recoveryKit = record(first(config, 'recoveryKit', 'recovery_kit'));
  const lifecycle = record(first(source, 'lifecycle', 'accountLifecycle', 'account_lifecycle'));
  const lifecycleActions = record(first(source, 'lifecycleActions', 'lifecycle_actions') ?? first(lifecycle, 'actions', 'availability'));
  const activeValue = first(source, 'activeOperation', 'active_operation');
  const activeOperation = isRecord(activeValue) ? normalizeBackupOperation(activeValue) : null;
  const history = arrayFrom(source, 'history', 'operations').map(normalizeBackupOperation).filter((operation) => operation.id);
  const snapshots = arrayFrom(source, 'snapshots').map(normalizeBackupSnapshot).filter((snapshot) => snapshot.id);
  const restoreTests = arrayFrom(source, 'restoreTests', 'restore_tests').map(normalizeBackupRestoreTest).filter((test) => test.id);
  const archivedIntegrations = arrayFrom(source, 'archivedIntegrations', 'archived_integrations')
    .map(normalizeArchivedIntegration)
    .filter((integration) => integration.id && integration.repositoryIdPrefix)
    .slice(0, 50);
  const activation = record(first(source, 'currentActivation', 'current_activation'));
  const latestCatchUpValue = first(activation, 'latestCatchUpOperation', 'latest_catch_up_operation');
  const activationFreshnessValue = text(first(activation, 'freshness', 'backupFreshness', 'backup_freshness')).toLowerCase();
  const activationKindValue = text(first(activation, 'kind', 'activationKind', 'activation_kind', 'type', 'activationType', 'activation_type')).toLowerCase();
  const rawCheck = record(first(source, 'lastRepositoryCheck', 'last_repository_check', 'repositoryCheck', 'repository_check'));
  const reportedControllerStatus = text(controller.status).toLowerCase();
  const controllerReady = booleanValue(
    first(controller, 'ready', 'available', 'healthy', 'online'),
    ['ready', 'online', 'healthy'].includes(reportedControllerStatus),
  );
  const controllerStatus = reportedControllerStatus || (controllerReady ? 'online' : 'unavailable');
  const repositoryStatus = text(first(source, 'repositoryStatus', 'repository_status') ?? first(config, 'repositoryStatus', 'repository_status')).toLowerCase();
  const connectionStatus = text(first(source, 'connectionStatus', 'connection_status') ?? first(config, 'connectionStatus', 'connection_status')).toLowerCase();
  const repositoryInitialized = booleanValue(
    first(config, 'repositoryInitialized', 'repository_initialized', 'initialized'),
    ['ready', 'initialized', 'connected'].includes(repositoryStatus),
  );
  const googleConnected = booleanValue(
    first(google, 'connected', 'authorized') ?? first(config, 'googleConnected', 'google_connected'),
    connectionStatus === 'connected',
  );
  const compatibleValue = first(controller, 'compatible', 'versionCompatible', 'version_compatible');
  const recoveryAcknowledgement = first(recoveryKit, 'acknowledged', 'acknowledgedAt', 'acknowledged_at')
    ?? first(config, 'recoveryKitAcknowledged', 'recovery_kit_acknowledged');
  const recoveryKitAcknowledged = booleanValue(
    recoveryAcknowledgement ?? first(config, 'recoveryKitAcknowledgedAt', 'recovery_kit_acknowledged_at'),
    Boolean(recoveryAcknowledgement ?? first(config, 'recoveryKitAcknowledgedAt', 'recovery_kit_acknowledged_at')),
  );
  const scheduleConfigured = booleanValue(first(schedule, 'configured', 'enabled'), Boolean(first(schedule, 'time', 'localTime', 'local_time')));
  const scheduleEnabled = booleanValue(schedule.enabled, scheduleConfigured);
  const retentionConfigured = booleanValue(retention.configured, Object.keys(retention).length > 0);
  const setupComplete = booleanValue(
    first(source, 'setupComplete', 'setup_complete') ?? first(config, 'setupComplete', 'setup_complete'),
    repositoryInitialized && (connectionStatus === 'disconnected' || (recoveryKitAcknowledged && scheduleConfigured && retentionConfigured)),
  );
  const freshStartProofAcknowledged = booleanValue(
    first(lifecycle, 'freshStartProofAcknowledged', 'fresh_start_proof_acknowledged', 'freshStartProofValid', 'fresh_start_proof_valid'),
  );
  const integrationSwitchProofAcknowledged = booleanValue(
    first(source, 'integrationSwitchProofAcknowledged', 'integration_switch_proof_acknowledged')
      ?? first(lifecycle, 'integrationSwitchProofAcknowledged', 'integration_switch_proof_acknowledged'),
  );
  const hasPinnedGoogleDestination = Boolean(
    text(first(google, 'accountId', 'account_id') ?? first(config, 'googleDriveAccountId', 'google_drive_account_id')) &&
    text(first(destination, 'folderId', 'folder_id', 'id') ?? first(google, 'folderId', 'folder_id') ?? first(config, 'googleDriveFolderId', 'google_drive_folder_id')),
  );
  const noOperationInFlight = !activeOperation;
  const sourceRepositoryId = text(first(config, 'repositoryId', 'repository_id'));
  const sourceReactivationEligible = repositoryStatus !== 'initializing' && connectionStatus !== 'attention_required' &&
    (!repositoryInitialized || /^[0-9a-f]{64}$/.test(sourceRepositoryId)) &&
    (repositoryInitialized || !googleConnected);
  const fallbackActions: BackupLifecycleActions = {
    reauthorize_google: {
      available: hasPinnedGoogleDestination && noOperationInFlight,
      blockedReason: activeOperation ? 'Wait for the active backup operation to finish.' : 'A pinned Google account and folder are required.',
    },
    reset_google_before_initialization: {
      available: ['connected', 'attention_required'].includes(connectionStatus || (googleConnected ? 'connected' : '')) && hasPinnedGoogleDestination && !repositoryInitialized && noOperationInFlight,
      blockedReason: activeOperation ? 'Wait for the active backup operation to finish.' : repositoryInitialized ? 'The repository has already been initialized.' : 'Connect Google Drive before choosing a different account.',
    },
    disconnect_google: {
      available: ['connected', 'attention_required'].includes(connectionStatus || (googleConnected ? 'connected' : '')) && repositoryInitialized && hasPinnedGoogleDestination && noOperationInFlight,
      blockedReason: activeOperation ? 'Wait for the active backup operation to finish.' : !repositoryInitialized ? 'Initialize the repository before disconnecting it.' : 'Google Drive is already disconnected.',
    },
    detach_repository_generation: {
      available: repositoryInitialized && Boolean(text(first(config, 'repositoryId', 'repository_id'))) && freshStartProofAcknowledged && noOperationInFlight,
      blockedReason: activeOperation ? 'Wait for the active backup operation to finish.' : !freshStartProofAcknowledged ? 'Export and acknowledge a fresh-start recovery kit in this session first.' : 'An initialized repository identity is required.',
    },
    reactivate_repository_generation: {
      available: archivedIntegrations.some((integration) => integration.reactivationAvailable) && noOperationInFlight && sourceReactivationEligible,
      blockedReason: activeOperation
        ? 'Wait for the active backup operation to finish.'
        : repositoryStatus === 'initializing'
          ? 'Wait for repository initialization to finish.'
          : connectionStatus === 'attention_required'
            ? 'Reauthorize or disconnect the current Google Drive integration first.'
            : repositoryInitialized && !/^[0-9a-f]{64}$/.test(sourceRepositoryId)
              ? 'The current repository identity is incomplete.'
              : googleConnected && !repositoryInitialized
                ? 'Reset the uninitialized Google connection before reactivation.'
                : 'No archived integration is currently eligible for reactivation.',
    },
  };
  const normalizedLifecycleActions = (Object.keys(fallbackActions) as BackupLifecycleOperationType[]).reduce<BackupLifecycleActions>((actions, type) => {
    actions[type] = normalizeLifecycleAction(lifecycleActionSource(lifecycleActions, type), fallbackActions[type]);
    return actions;
  }, { ...fallbackActions });
  const generationValue = first(source, 'currentGeneration', 'current_generation') ?? first(lifecycle, 'currentGeneration', 'current_generation') ?? first(config, 'currentGeneration', 'current_generation');
  const generation = record(generationValue);
  const generationNumber = optionalNumber(first(generation, 'number', 'generationNumber', 'generation_number') ?? first(config, 'generationNumber', 'generation_number'));
  const generationId = text(first(generation, 'id', 'generationId', 'generation_id') ?? first(config, 'generationId', 'generation_id')) || undefined;
  const hasGeneration = Object.keys(generation).length > 0 || generationNumber !== undefined || Boolean(generationId);
  return {
    overallState: text(first(source, 'overallState', 'overall_state', 'status'), config.configured || repositoryInitialized ? 'attention_required' : 'unconfigured').toLowerCase(),
    connectionStatus: connectionStatus || (googleConnected ? 'connected' : hasPinnedGoogleDestination ? 'attention_required' : 'unconfigured'),
    repositoryStatus: repositoryStatus || (repositoryInitialized ? 'ready' : 'uninitialized'),
    setupComplete,
    freshStartProofAcknowledged,
    integrationSwitchProofAcknowledged,
    lifecycleActions: normalizedLifecycleActions,
    currentGeneration: hasGeneration ? {
      id: generationId,
      number: generationNumber,
      status: text(generation.status, 'current').toLowerCase(),
      repositoryId: /^[0-9a-f]{64}$/.test(text(first(generation, 'repositoryId', 'repository_id') ?? first(config, 'repositoryId', 'repository_id')))
        ? text(first(generation, 'repositoryId', 'repository_id') ?? first(config, 'repositoryId', 'repository_id'))
        : undefined,
      createdAt: text(first(generation, 'createdAt', 'created_at')) || undefined,
    } : null,
    archivedIntegrations,
    currentActivation: Object.keys(activation).length ? {
      ...(['initial', 'fresh_start', 'reactivated'].includes(activationKindValue)
        ? { kind: activationKindValue as 'initial' | 'fresh_start' | 'reactivated' }
        : {}),
      activatedAt: safeTimestamp(first(activation, 'activatedAt', 'activated_at')),
      ...(['fresh', 'catch_up_required'].includes(activationFreshnessValue)
        ? { freshness: activationFreshnessValue as 'fresh' | 'catch_up_required' }
        : {}),
      freshnessConfirmedAt: safeTimestamp(first(activation, 'freshnessConfirmedAt', 'freshness_confirmed_at')),
      lastSuccessfulBackupAt: safeTimestamp(first(activation, 'lastSuccessfulBackupAt', 'last_successful_backup_at')),
      lastVerifiedBackupAt: safeTimestamp(first(activation, 'lastVerifiedBackupAt', 'last_verified_backup_at')),
      scheduleReviewed: booleanValue(first(activation, 'scheduleReviewed', 'schedule_reviewed')),
      retentionReviewed: booleanValue(first(activation, 'retentionReviewed', 'retention_reviewed')),
      latestCatchUpOperation: isRecord(latestCatchUpValue) ? normalizeBackupOperation(latestCatchUpValue) : null,
    } : null,
    backupNowAllowed: booleanValue(
      first(source, 'backupNowAllowed', 'backup_now_allowed'),
      repositoryInitialized && googleConnected && connectionStatus !== 'disconnected',
    ),
    backupNowBlockedReason: boundedBackupMessage(first(source, 'backupNowBlockedReason', 'backup_now_blocked_reason')),
    controller: {
      ready: controllerReady,
      status: controllerStatus,
      compatible: compatibleValue === undefined ? null : booleanValue(compatibleValue),
      protocolVersion: optionalNumber(first(controller, 'protocolVersion', 'protocol_version')),
      version: text(controller.version) || undefined,
      heartbeatAt: text(first(controller, 'heartbeatAt', 'heartbeat_at', 'lastSeenAt', 'last_seen_at')) || undefined,
      message: sanitizeBackupMessage(controller.message),
      publicKey: /^[A-Za-z0-9_-]{400,1024}$/.test(text(first(controller, 'publicKey', 'public_key')))
        ? text(first(controller, 'publicKey', 'public_key'))
        : undefined,
      capabilities: Object.keys(record(controller.capabilities)).length ? {
        databaseContractAvailable: booleanValue(first(record(controller.capabilities), 'databaseContractAvailable', 'database_contract_available')),
        googleOauthConfigured: booleanValue(first(record(controller.capabilities), 'googleOauthConfigured', 'google_oauth_configured')),
        credentialsWritable: booleanValue(first(record(controller.capabilities), 'credentialsWritable', 'credentials_writable')),
        mediaReadable: booleanValue(first(record(controller.capabilities), 'mediaReadable', 'media_readable')),
        resticVersion: text(first(record(controller.capabilities), 'resticVersion', 'restic_version')) || undefined,
        rcloneVersion: text(first(record(controller.capabilities), 'rcloneVersion', 'rclone_version')) || undefined,
      } : undefined,
      resourceGovernance: normalizeBackupResourceGovernance(first(controller, 'resourceGovernance', 'resource_governance')),
    },
    config: {
      configured: booleanValue(config.configured, repositoryInitialized && googleConnected),
      repositoryInitialized,
      google: {
        connected: googleConnected,
        account: text(first(google, 'account', 'email', 'accountEmail', 'account_email') ?? first(config, 'connectedAccountEmail', 'connected_account_email')) || undefined,
        accountId: text(first(google, 'accountId', 'account_id') ?? first(config, 'googleDriveAccountId', 'google_drive_account_id')) || undefined,
        folderName: text(first(destination, 'folderName', 'folder_name', 'name') ?? first(google, 'folderName', 'folder_name') ?? first(config, 'googleDriveFolderName', 'google_drive_folder_name')) || undefined,
        folderId: text(first(destination, 'folderId', 'folder_id', 'id') ?? first(google, 'folderId', 'folder_id') ?? first(config, 'googleDriveFolderId', 'google_drive_folder_id')) || undefined,
      },
      repositoryId: /^[0-9a-f]{64}$/.test(text(first(config, 'repositoryId', 'repository_id')))
        ? text(first(config, 'repositoryId', 'repository_id'))
        : undefined,
      estimatedLocalBytes: optionalNumber(first(config, 'estimatedLocalBytes', 'estimated_local_bytes', 'localDataBytes', 'local_data_bytes')),
      recoveryKitAcknowledged,
      recoveryInstructionsUrl: safeRecoveryInstructionsUrl(first(recoveryKit, 'instructionsUrl', 'instructions_url') ?? first(config, 'recoveryInstructionsUrl', 'recovery_instructions_url')),
      schedule: {
        configured: scheduleConfigured,
        enabled: scheduleEnabled,
        frequency: text(schedule.frequency, 'daily').toLowerCase(),
        time: text(first(schedule, 'time', 'localTime', 'local_time'), '03:00'),
        timezone: text(schedule.timezone, 'UTC'),
        nextRunAt: text(first(schedule, 'nextRunAt', 'next_run_at', 'nextScheduledAt', 'next_scheduled_at')) || undefined,
      },
      retention: {
        configured: retentionConfigured,
        daily: numberValue(first(retention, 'daily', 'keepDaily', 'keep_daily'), 7),
        weekly: numberValue(first(retention, 'weekly', 'keepWeekly', 'keep_weekly'), 4),
        monthly: numberValue(first(retention, 'monthly', 'keepMonthly', 'keep_monthly'), 6),
      },
    },
    activeOperation,
    history,
    snapshots,
    restoreTests,
    lastVerifiedAt: text(first(source, 'lastVerifiedAt', 'last_verified_at')) || snapshots
      .filter((snapshot) => ['passed', 'verified', 'success'].includes(snapshot.verificationStatus) && snapshot.createdAt && Number.isFinite(Date.parse(snapshot.createdAt)))
      .sort((left, right) => Date.parse(right.createdAt ?? '') - Date.parse(left.createdAt ?? ''))[0]?.createdAt,
    lastRepositoryCheck: Object.keys(rawCheck).length ? {
      status: text(first(rawCheck, 'status', 'state'), 'unknown').toLowerCase(),
      message: sanitizeBackupMessage(rawCheck.message),
      finishedAt: text(first(rawCheck, 'finishedAt', 'finished_at', 'checkedAt', 'checked_at')) || undefined,
    } : null,
    scheduleOverlapWarning: normalizeBackupScheduleOverlapWarning(first(source, 'scheduleOverlapWarning', 'schedule_overlap_warning')),
  };
}

const MEDIA_ARCHIVE_STATES = new Set<MediaArchiveState>([
  'pending', 'copying', 'verifying', 'verified', 'failed',
  'retained_until', 'delete_pending', 'deleted',
]);

function safeGoogleAuthorizationUrl(value: unknown): string | undefined {
  const candidate = boundedText(value, 4096);
  if (!candidate) return undefined;
  try {
    const url = new URL(candidate);
    return url.origin === 'https://accounts.google.com' && url.pathname === '/o/oauth2/v2/auth'
      ? url.toString()
      : undefined;
  } catch {
    return undefined;
  }
}

function normalizeMediaArchiveOperation(value: unknown): MediaArchiveOperation {
  const source = record(value);
  const error = record(source.error);
  return {
    id: boundedText(source.id, 64),
    operationType: boundedText(first(source, 'operationType', 'operation_type'), 64, 'unknown'),
    status: boundedText(first(source, 'status', 'state'), 32, 'unknown'),
    phase: boundedText(source.phase, 64, 'unknown'),
    progress: Math.min(100, Math.max(0, numberValue(source.progress))),
    transitionCheckpoint: boundedText(first(source, 'transitionCheckpoint', 'transition_checkpoint'), 64) || undefined,
    authorizationUrl: safeGoogleAuthorizationUrl(first(source, 'authorizationUrl', 'authorization_url')),
    authorizationExpiresAt: safeTimestamp(first(source, 'authorizationExpiresAt', 'authorization_expires_at')),
    errorCode: boundedText(first(error, 'code') ?? first(source, 'errorCode', 'error_code'), 96) || undefined,
    createdAt: safeTimestamp(first(source, 'createdAt', 'created_at')),
    startedAt: safeTimestamp(first(source, 'startedAt', 'started_at')),
    finishedAt: safeTimestamp(first(source, 'finishedAt', 'finished_at')),
    updatedAt: safeTimestamp(first(source, 'updatedAt', 'updated_at')),
  };
}

function normalizeMediaArchiveSettings(value: unknown): MediaArchiveSettings {
  const source = record(value);
  const drive = record(source.drive);
  const driveQuota = record(drive.quota);
  const r2Source = record(first(source, 'r2Source', 'r2_source'));
  const policy = record(source.policy);
  const health = record(source.health);
  return {
    enabled: booleanValue(source.enabled),
    credentialEncryptionAvailable: booleanValue(first(source, 'credentialEncryptionAvailable', 'credential_encryption_available')),
    configurationRevision: Math.max(0, numberValue(first(source, 'configurationRevision', 'configuration_revision'))),
    drive: {
      status: boundedText(drive.status, 32, 'not_configured'),
      connectedAccountEmail: boundedDisplayText(first(drive, 'connectedAccountEmail', 'connected_account_email'), 320, '') || undefined,
      accountIdPrefix: boundedText(first(drive, 'accountIdPrefix', 'account_id_prefix'), 16) || undefined,
      folderIdPrefix: boundedText(first(drive, 'folderIdPrefix', 'folder_id_prefix'), 16) || undefined,
      folderName: boundedDisplayText(first(drive, 'folderName', 'folder_name'), 180, '') || undefined,
      connectedAt: safeTimestamp(first(drive, 'connectedAt', 'connected_at')),
      credentialRevision: Math.max(0, numberValue(first(drive, 'credentialRevision', 'credential_revision'))),
      credentialUpdatedAt: safeTimestamp(first(drive, 'credentialUpdatedAt', 'credential_updated_at')),
      quota: {
        configuredCapacityBytes: nullableNumber(first(driveQuota, 'configuredCapacityBytes', 'configured_capacity_bytes')),
        providerCapacityBytes: nullableNumber(first(driveQuota, 'providerCapacityBytes', 'provider_capacity_bytes')),
        providerUsageBytes: nullableNumber(first(driveQuota, 'providerUsageBytes', 'provider_usage_bytes')),
        driveUsageBytes: nullableNumber(first(driveQuota, 'driveUsageBytes', 'drive_usage_bytes')),
        trashUsageBytes: nullableNumber(first(driveQuota, 'trashUsageBytes', 'trash_usage_bytes')),
        operationalReserveBytes: Math.max(0, numberValue(first(driveQuota, 'operationalReserveBytes', 'operational_reserve_bytes'))),
        observedAt: safeTimestamp(first(driveQuota, 'observedAt', 'observed_at')),
      },
    },
    r2Source: {
      status: boundedText(r2Source.status, 32, 'not_configured'),
      accountId: boundedText(first(r2Source, 'accountId', 'account_id'), 64) || undefined,
      bucketName: boundedDisplayText(first(r2Source, 'bucketName', 'bucket_name'), 255, '') || undefined,
      accessKeyIdPrefix: boundedText(first(r2Source, 'accessKeyIdPrefix', 'access_key_id_prefix'), 16) || undefined,
      secretConfigured: booleanValue(first(r2Source, 'secretConfigured', 'secret_configured')),
      lastVerifiedAt: safeTimestamp(first(r2Source, 'lastVerifiedAt', 'last_verified_at')),
      credentialRevision: Math.max(0, numberValue(first(r2Source, 'credentialRevision', 'credential_revision'))),
      credentialUpdatedAt: safeTimestamp(first(r2Source, 'credentialUpdatedAt', 'credential_updated_at')),
    },
    policy: {
      retentionAfterPurgeDays: Math.min(3650, Math.max(1, numberValue(first(policy, 'retentionAfterPurgeDays', 'retention_after_purge_days'), 90))),
      scheduleMode: 'continuous',
      maxConcurrency: Math.min(4, Math.max(1, numberValue(first(policy, 'maxConcurrency', 'max_concurrency'), 1))),
      bandwidthLimitKbps: nullableNumber(first(policy, 'bandwidthLimitKbps', 'bandwidth_limit_kbps')),
      verificationIntervalHours: Math.min(720, Math.max(1, numberValue(first(policy, 'verificationIntervalHours', 'verification_interval_hours'), 168))),
      healthCheckIntervalHours: Math.min(168, Math.max(1, numberValue(first(policy, 'healthCheckIntervalHours', 'health_check_interval_hours'), 24))),
      supersededRetentionDays: Math.min(3650, Math.max(1, numberValue(first(policy, 'supersededRetentionDays', 'superseded_retention_days'), 30))),
    },
    lastSuccessfulArchiveAt: safeTimestamp(first(source, 'lastSuccessfulArchiveAt', 'last_successful_archive_at')),
    lastSuccessfulVerificationAt: safeTimestamp(first(source, 'lastSuccessfulVerificationAt', 'last_successful_verification_at')),
    health: {
      checkedAt: safeTimestamp(first(health, 'checkedAt', 'checked_at')),
      status: boundedText(health.status, 32) || undefined,
    },
    lastErrorCode: boundedText(first(source, 'lastErrorCode', 'last_error_code'), 96) || undefined,
    updatedAt: safeTimestamp(first(source, 'updatedAt', 'updated_at')),
  };
}

function normalizeMediaArchiveRecoveryTarget(value: unknown): MediaArchiveRecoveryTarget | null {
  if (!isRecord(value)) return null;
  const source = record(value);
  const coverage = record(source.coverage);
  const id = boundedText(source.id, 64);
  if (!id) return null;
  return {
    id,
    state: boundedText(source.state, 32, 'failed'),
    accountId: boundedText(first(source, 'accountId', 'account_id'), 64) || undefined,
    bucketName: boundedDisplayText(first(source, 'bucketName', 'bucket_name'), 255, '') || undefined,
    mediaHostname: boundedText(first(source, 'mediaHostname', 'media_hostname'), 255) || undefined,
    workerVersion: boundedText(first(source, 'workerVersion', 'worker_version'), 64) || undefined,
    verificationStatus: boundedText(first(source, 'verificationStatus', 'verification_status'), 32) || undefined,
    storageVerifiedAt: safeTimestamp(first(source, 'storageVerifiedAt', 'storage_verified_at')),
    deliveryVerifiedAt: safeTimestamp(first(source, 'deliveryVerifiedAt', 'delivery_verified_at')),
    coverageFrozenAt: safeTimestamp(first(source, 'coverageFrozenAt', 'coverage_frozen_at')),
    coverage: {
      requiredPublications: Math.max(0, numberValue(first(coverage, 'requiredPublications', 'required_publications'))),
      requiredObjects: Math.max(0, numberValue(first(coverage, 'requiredObjects', 'required_objects'))),
      requiredBytes: Math.max(0, numberValue(first(coverage, 'requiredBytes', 'required_bytes'))),
      restoredPublications: Math.max(0, numberValue(first(coverage, 'restoredPublications', 'restored_publications'))),
      verifiedPublications: Math.max(0, numberValue(first(coverage, 'verifiedPublications', 'verified_publications'))),
    },
    errorCode: boundedText(first(source, 'errorCode', 'error_code'), 96) || undefined,
    createdAt: safeTimestamp(first(source, 'createdAt', 'created_at')),
    updatedAt: safeTimestamp(first(source, 'updatedAt', 'updated_at')),
  };
}

function normalizeMediaArchiveRecoveryCoverage(value: unknown): MediaArchiveRecoveryCoverage {
  const source = record(value);
  return {
    activePublications: Math.max(0, numberValue(first(source, 'activePublications', 'active_publications'))),
    coveredPublications: Math.max(0, numberValue(first(source, 'coveredPublications', 'covered_publications'))),
    requiredObjects: Math.max(0, numberValue(first(source, 'requiredObjects', 'required_objects'))),
    requiredBytes: Math.max(0, numberValue(first(source, 'requiredBytes', 'required_bytes'))),
    missing: arrayFrom(source, 'missing').map((item) => {
      const row = record(item);
      return {
        publicationId: boundedText(first(row, 'publicationId', 'publication_id'), 64),
        videoId: boundedText(first(row, 'videoId', 'video_id'), 64),
        videoTitle: boundedDisplayText(first(row, 'videoTitle', 'video_title'), 500, 'Video'),
      };
    }).filter((item) => item.publicationId && item.videoId),
  };
}

export function normalizeMediaArchiveOverview(payload: unknown): MediaArchiveOverview {
  const source = record(unwrap(payload, 'mediaArchive', 'archive'));
  const summary = record(source.summary);
  const publications = record(summary.publications);
  const active = first(source, 'activeOperation', 'active_operation');
  return {
    settings: normalizeMediaArchiveSettings(source.settings),
    recoveryTarget: normalizeMediaArchiveRecoveryTarget(first(source, 'recoveryTarget', 'recovery_target')),
    summary: {
      publications: {
        total: Math.max(0, numberValue(publications.total)),
        verified: Math.max(0, numberValue(publications.verified)),
        pending: Math.max(0, numberValue(publications.pending)),
        copying: Math.max(0, numberValue(publications.copying)),
        verifying: Math.max(0, numberValue(publications.verifying)),
        failed: Math.max(0, numberValue(publications.failed)),
        retained: Math.max(0, numberValue(publications.retained)),
      },
      archivedBytes: Math.max(0, numberValue(first(summary, 'archivedBytes', 'archived_bytes'))),
      verifiedBytes: Math.max(0, numberValue(first(summary, 'verifiedBytes', 'verified_bytes'))),
      retainedAfterPurgeBytes: Math.max(0, numberValue(first(summary, 'retainedAfterPurgeBytes', 'retained_after_purge_bytes'))),
      retainedSupersededBytes: Math.max(0, numberValue(first(summary, 'retainedSupersededBytes', 'retained_superseded_bytes'))),
      pendingBytes: Math.max(0, numberValue(first(summary, 'pendingBytes', 'pending_bytes'))),
      failedBytes: Math.max(0, numberValue(first(summary, 'failedBytes', 'failed_bytes'))),
      oldestUnverifiedAt: safeTimestamp(first(summary, 'oldestUnverifiedAt', 'oldest_unverified_at')),
      archiveLagSeconds: Math.max(0, numberValue(first(summary, 'archiveLagSeconds', 'archive_lag_seconds'))),
      cleanupBlocked: Math.max(0, numberValue(first(summary, 'cleanupBlocked', 'cleanup_blocked'))),
      cleanupDeferred: Math.max(0, numberValue(first(summary, 'cleanupDeferred', 'cleanup_deferred'))),
      attention: Math.max(0, numberValue(summary.attention)),
    },
    activeOperation: isRecord(active) ? normalizeMediaArchiveOperation(active) : null,
    operations: arrayFrom(source, 'operations').map(normalizeMediaArchiveOperation).filter((operation) => operation.id),
  };
}

function normalizeMediaArchiveRecord(value: unknown): MediaArchiveRecord {
  const source = record(value);
  const owner = record(source.owner);
  const stateText = boundedText(source.state, 32) as MediaArchiveState;
  const error = record(source.error);
  return {
    id: boundedText(source.id, 64),
    publicationId: boundedText(first(source, 'publicationId', 'publication_id'), 64),
    sourceVideoId: boundedText(first(source, 'sourceVideoId', 'source_video_id'), 64),
    videoId: boundedText(first(source, 'videoId', 'video_id'), 64) || undefined,
    videoAvailable: booleanValue(first(source, 'videoAvailable', 'video_available')),
    retainedRestoreAvailable: booleanValue(first(
      source, 'retainedRestoreAvailable', 'retained_restore_available',
    )),
    videoTitle: boundedDisplayText(first(source, 'videoTitle', 'video_title'), 500, '') || undefined,
    owner: Object.keys(owner).length ? {
      displayName: boundedDisplayText(first(owner, 'displayName', 'display_name'), 200, '') || undefined,
      email: boundedDisplayText(owner.email, 320, '') || undefined,
    } : null,
    state: MEDIA_ARCHIVE_STATES.has(stateText) ? stateText : 'failed',
    attempts: Math.max(0, numberValue(source.attempts)),
    expectedObjectCount: Math.max(0, numberValue(first(source, 'expectedObjectCount', 'expected_object_count'))),
    expectedTotalBytes: Math.max(0, numberValue(first(source, 'expectedTotalBytes', 'expected_total_bytes'))),
    destinationObjectCount: nullableNumber(first(source, 'destinationObjectCount', 'destination_object_count')),
    destinationTotalBytes: nullableNumber(first(source, 'destinationTotalBytes', 'destination_total_bytes')),
    verificationMethod: boundedText(first(source, 'verificationMethod', 'verification_method'), 64) || undefined,
    requestedAt: safeTimestamp(first(source, 'requestedAt', 'requested_at')),
    startedAt: safeTimestamp(first(source, 'startedAt', 'started_at')),
    copiedAt: safeTimestamp(first(source, 'copiedAt', 'copied_at')),
    verifiedAt: safeTimestamp(first(source, 'verifiedAt', 'verified_at')),
    sourcePurgedAt: safeTimestamp(first(source, 'sourcePurgedAt', 'source_purged_at')),
    retainedUntil: safeTimestamp(first(source, 'retainedUntil', 'retained_until')),
    deletedAt: safeTimestamp(first(source, 'deletedAt', 'deleted_at')),
    lastRestoreTestAt: safeTimestamp(first(source, 'lastRestoreTestAt', 'last_restore_test_at')),
    lastRestoreTestResult: boundedText(first(source, 'lastRestoreTestResult', 'last_restore_test_result'), 32) || undefined,
    errorCode: boundedText(first(error, 'code') ?? first(source, 'errorCode', 'error_code'), 96) || undefined,
    updatedAt: safeTimestamp(first(source, 'updatedAt', 'updated_at')),
  };
}

function normalizeMediaArchiveRestorePlan(value: unknown): MediaArchiveRestorePlan {
  const source = record(value);
  const from = record(source.source);
  const target = record(source.target);
  const inventory = record(source.inventory);
  const actionText = boundedText(source.action, 32);
  const action = [
    'repair_current_prefix', 'recovery_prefix', 'verify_current_prefix', 'restore_retained_video',
  ].includes(actionText)
    ? actionText as MediaArchiveRestorePlan['action'] : 'recovery_prefix';
  return {
    archiveId: boundedText(first(source, 'archiveId', 'archive_id'), 64),
    videoId: boundedText(first(source, 'videoId', 'video_id'), 64),
    videoTitle: boundedDisplayText(first(source, 'videoTitle', 'video_title'), 500, 'Video'),
    sourcePublicationId: boundedText(first(source, 'sourcePublicationId', 'source_publication_id'), 64),
    source: {
      provider: 'google_drive',
      folderName: boundedDisplayText(first(from, 'folderName', 'folder_name'), 180, 'Drive B'),
      accountEmail: boundedDisplayText(first(from, 'accountEmail', 'account_email'), 320, ''),
    },
    target: {
      provider: 'r2',
      bucketName: boundedDisplayText(first(target, 'bucketName', 'bucket_name'), 255, ''),
      publicationId: boundedText(first(target, 'publicationId', 'publication_id'), 64),
    },
    inventory: {
      objectCount: Math.max(0, numberValue(first(inventory, 'objectCount', 'object_count'))),
      totalBytes: Math.max(0, numberValue(first(inventory, 'totalBytes', 'total_bytes'))),
      missingObjects: Math.max(0, numberValue(first(inventory, 'missingObjects', 'missing_objects'))),
      conflictingObjects: Math.max(0, numberValue(first(inventory, 'conflictingObjects', 'conflicting_objects'))),
      unexpectedObjects: Math.max(0, numberValue(first(inventory, 'unexpectedObjects', 'unexpected_objects'))),
    },
    action,
  };
}

function normalizeMediaArchiveAuditEvent(value: unknown): MediaArchiveAuditEvent {
  const source = record(value);
  const actor = record(source.actor);
  return {
    id: boundedText(source.id, 64),
    operationId: boundedText(first(source, 'operationId', 'operation_id'), 64) || undefined,
    archivePublicationId: boundedText(first(source, 'archivePublicationId', 'archive_publication_id'), 64) || undefined,
    actorUserId: boundedText(first(source, 'actorUserId', 'actor_user_id'), 64) || undefined,
    actor: Object.keys(actor).length ? {
      displayName: boundedDisplayText(first(actor, 'displayName', 'display_name'), 200, '') || undefined,
      email: boundedDisplayText(actor.email, 320, '') || undefined,
    } : null,
    eventType: boundedText(first(source, 'eventType', 'event_type'), 96, 'unknown'),
    reason: boundedDisplayText(source.reason, 1000, '') || undefined,
    createdAt: safeTimestamp(first(source, 'createdAt', 'created_at')),
  };
}

export function normalizeMediaArchiveRecordPage(payload: unknown): MediaArchiveRecordPage {
  const source = record(payload);
  return {
    records: arrayFrom(source, 'records').map(normalizeMediaArchiveRecord).filter((item) => item.id),
    total: Math.max(0, numberValue(source.total)),
    limit: Math.min(100, Math.max(1, numberValue(source.limit, 50))),
    offset: Math.max(0, numberValue(source.offset)),
  };
}

export function normalizeMediaArchiveRecordDetail(payload: unknown): MediaArchiveRecordDetail {
  const source = record(payload);
  return {
    record: normalizeMediaArchiveRecord(source.record),
    operations: arrayFrom(source, 'operations').map(normalizeMediaArchiveOperation).filter((item) => item.id),
    audit: arrayFrom(source, 'audit').map(normalizeMediaArchiveAuditEvent).filter((item) => item.id),
  };
}

export function normalizeMediaArchiveAuditPage(payload: unknown): MediaArchiveAuditPage {
  const source = record(payload);
  return {
    events: arrayFrom(source, 'events').map(normalizeMediaArchiveAuditEvent).filter((item) => item.id),
    total: Math.max(0, numberValue(source.total)),
    limit: Math.min(100, Math.max(1, numberValue(source.limit, 50))),
    offset: Math.max(0, numberValue(source.offset)),
  };
}

function normalizeBackupResourceGovernance(payload: unknown): BackupControllerResourceGovernance | undefined {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return undefined;
  const source = record(payload);
  if (Object.keys(source).length === 0) return undefined;
  const pressureKind = text(first(source, 'lastResourcePressureKind', 'last_resource_pressure_kind')) || null;
  const validPressureKinds = new Set(['cpu_throttled', 'memory_pressure', 'upload_throttled', 'lease_heartbeat_lag']);
  return {
    cpuLimitCores: optionalNumber(first(source, 'cpuLimitCores', 'cpu_limit_cores')) ?? null,
    memoryLimitMb: optionalNumber(first(source, 'memoryLimitMb', 'memory_limit_mb')) ?? null,
    uploadLimitKbps: optionalNumber(first(source, 'uploadLimitKbps', 'upload_limit_kbps')) ?? null,
    cpuShares: optionalNumber(first(source, 'cpuShares', 'cpu_shares')) ?? null,
    resticCacheDirMb: optionalNumber(first(source, 'resticCacheDirMb', 'restic_cache_dir_mb')) ?? null,
    lastResourcePressureAt: text(first(source, 'lastResourcePressureAt', 'last_resource_pressure_at')) || null,
    lastResourcePressureKind: pressureKind && validPressureKinds.has(pressureKind)
      ? pressureKind as BackupControllerResourceGovernance['lastResourcePressureKind']
      : null,
  };
}

function normalizeBackupScheduleOverlapWarning(payload: unknown): BackupScheduleOverlapWarning | undefined {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return undefined;
  const source = record(payload);
  if (Object.keys(source).length === 0) return undefined;
  const active = booleanValue(first(source, 'active'));
  const activeTranscodes = optionalNumber(first(source, 'activeTranscodes', 'active_transcodes')) ?? 0;
  return {
    active,
    activeTranscodes,
    reason: sanitizeBackupMessage(first(source, 'reason')) || null,
  };
}

function exactBackupPayload(value: unknown, allowedKeys: readonly string[], field: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new ApiError(`${field} must be an object.`, 0);
  }
  const source = value as Record<string, unknown>;
  if (Object.keys(source).some((key) => !allowedKeys.includes(key))) {
    throw new ApiError(`${field} contains unsupported fields.`, 0);
  }
  return source;
}

function validatedReactivationPayload(payload: BackupOperationPayload | undefined): BackupReactivateRepositoryGenerationPayload {
  const source = exactBackupPayload(
    payload,
    ['targetGenerationId', 'kitIdentity', 'encryptedPassword', 'confirmation'],
    'Reactivation payload',
  );
  const identity = exactBackupPayload(
    source.kitIdentity,
    ['googleAccountId', 'googleDriveFolderId', 'repositoryId'],
    'Reactivation kit identity',
  );
  const targetGenerationId = text(source.targetGenerationId);
  const googleAccountId = text(identity.googleAccountId);
  const googleDriveFolderId = text(identity.googleDriveFolderId);
  const repositoryId = text(identity.repositoryId);
  const encryptedPassword = text(source.encryptedPassword);
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(targetGenerationId)) {
    throw new ApiError('The reactivation generation selector is invalid.', 0);
  }
  if (!/^[A-Za-z0-9_-]{1,255}$/.test(googleAccountId) || !/^[A-Za-z0-9_-]{1,255}$/.test(googleDriveFolderId) ||
      !/^[0-9a-f]{64}$/.test(repositoryId)) {
    throw new ApiError('The reactivation kit identity is invalid.', 0);
  }
  if (!/^[A-Za-z0-9_-]{400,1024}$/.test(encryptedPassword)) {
    throw new ApiError('The controller-wrapped reactivation password is invalid.', 0);
  }
  if (source.confirmation !== 'REACTIVATE') {
    throw new ApiError('The reactivation confirmation is invalid.', 0);
  }
  return {
    targetGenerationId,
    kitIdentity: { googleAccountId, googleDriveFolderId, repositoryId },
    encryptedPassword,
    confirmation: 'REACTIVATE',
  };
}

async function requestPlaybackSources(id: string): Promise<PlaybackSources> {
  const payload = await request<unknown>(`/api/videos/${encodeURIComponent(id)}/playback`);
  const source = record(unwrap(payload, 'playback'));
  const hlsUrl = text(first(source, 'hlsUrl', 'hls_url', 'url', 'playbackUrl', 'playback_url') ?? (typeof payload === 'string' ? payload : ''));
  if (!hlsUrl) throw new ApiError('The playback URL is not available yet.', 409, payload);
  const mp4Url = text(first(source, 'mp4Url', 'mp4_url'));
  const thumbnailUrl = text(first(source, 'thumbnailUrl', 'thumbnail_url'));
  return {
    ...(typeof source.playbackExpiresAt === 'number' ? { playbackExpiresAt: source.playbackExpiresAt } : {}),
    ...(typeof source.playbackRefreshUrl === 'string' ? { playbackRefreshUrl: source.playbackRefreshUrl } : {}),
    ...(typeof source.playbackSessionToken === 'string' ? { playbackSessionToken: source.playbackSessionToken } : {}),
    ...(Array.isArray(source.playbackTokenUrlPrefixes)
      && source.playbackTokenUrlPrefixes.every((value) => typeof value === 'string')
      ? { playbackTokenUrlPrefixes: source.playbackTokenUrlPrefixes as string[] }
      : {}),
    ...(typeof source.watermarkCode === 'string' ? { watermarkCode: source.watermarkCode } : {}),
    hlsUrl: new URL(hlsUrl, window.location.origin).href,
    mp4Url: mp4Url ? new URL(mp4Url, window.location.origin).href : null,
    thumbnailUrl: thumbnailUrl ? new URL(thumbnailUrl, window.location.origin).href : null,
  };
}

/** Resolve fresh/reset nullable storage fields without granting missing permissions. */
export function normalizeMemberPublicBranding(value: unknown): MemberPublicBranding {
  const source = record(value); const settings = record(source.settings); const colors = record(settings.colors);
  const permissions = record(source.permissions); const fallback = getBranding();
  const color = (key: keyof MemberPublicBranding['settings']['colors']) => {
    const candidate = text(colors[key]); return /^#[0-9a-f]{6}$/i.test(candidate) ? candidate : fallback.colors[key];
  };
  return {
    settings: {
      name: text(settings.name).trim() || fallback.productName,
      colors: { primary: color('primary'), primaryHover: color('primaryHover'), primarySoft: color('primarySoft') },
      hidePoweredBy: settings.hidePoweredBy === true,
      ...(typeof settings.showPlayerLogo === 'boolean' ? { showPlayerLogo: settings.showPlayerLogo } : {}),
      logoUrl: typeof settings.logoUrl === 'string' && settings.logoUrl ? settings.logoUrl : null,
      faviconUrl: typeof settings.faviconUrl === 'string' && settings.faviconUrl ? settings.faviconUrl : null,
    },
    permissions: {
      pageBranding: permissions.pageBranding === true, playerLogo: permissions.playerLogo === true,
      favicon: permissions.favicon === true, hidePoweredBy: permissions.hidePoweredBy === true,
      customDomainLimit: permissions.customDomainLimit === 1 ? 1 : 0,
    },
    domains: arrayFrom(source.domains).map((value) => {
      const domain = record(value); const state = text(domain.state);
      return { id: text(domain.id), hostname: text(domain.hostname), state: (['pending', 'active', 'rejected', 'disabled'].includes(state) ? state : 'disabled') as MemberPublicBranding['domains'][number]['state'],
        reason: typeof domain.reason === 'string' ? domain.reason : null, createdAt: text(domain.createdAt), updatedAt: text(domain.updatedAt),
        ...(domain.provider && typeof domain.provider === 'object' ? { provider: domain.provider as MemberPublicBranding['domains'][number]['provider'] } : {}) };
    }).filter((domain) => domain.id && domain.hostname),
    publicBaseUrl: text(source.publicBaseUrl) || window.location.origin,
    ...(source.domainSetup && typeof source.domainSetup === 'object' ? { domainSetup: source.domainSetup as MemberPublicBranding['domainSetup'] } : {}),
  };
}

export const api = {
  async imageArchives(): Promise<ImageArchiveRecord[]> {
    return (await request<{ archives: ImageArchiveRecord[] }>('/api/image-archive')).archives;
  },
  async imageArchiveAction(id: string, action: 'retry' | 'restore'): Promise<{ operationId: string; status: 'queued' }> {
    return request(`/api/image-archive/${encodeURIComponent(id)}/${action}`, { method: 'POST', body: {} });
  },
  async media(filters: { folderId: string | null; search: string; status: string; scope?: 'active' | 'trash'; ownerUserId?: string | null; memberCatalog?: boolean; kind?: 'all' | 'video' | 'image'; offset?: number }): Promise<VideoListResult> {
    const query = new URLSearchParams({ kind: filters.kind ?? 'all', limit: '100', offset: String(filters.offset ?? 0) });
    if (filters.folderId) query.set('folder_id', filters.folderId);
    if (filters.search) query.set('search', filters.search);
    if (filters.status !== 'all') query.set('status', filters.status);
    if (filters.scope === 'trash') query.set('scope', 'trash');
    if (filters.ownerUserId) query.set('owner_user_id', filters.ownerUserId);
    if (filters.memberCatalog) query.set('catalog', 'members');
    const result = await request<{ items: unknown[]; total: number }>(`/api/media?${query}`);
    return { videos: result.items.map(item => record(item).mediaKind === 'image' ? imageLibraryItem(item as ImageItem) : normalizeVideo(item)), total: result.total };
  },
  async image(id: string): Promise<ImageItem> {
    return unwrap(await request(`/api/images/${encodeURIComponent(id)}`), 'image') as ImageItem;
  },
  async imageVariant(id: string, preset: number, signal?: AbortSignal): Promise<Blob> {
    const version = supportContextVersion;
    const headers = new Headers();
    applySupportHeaders(headers, activeSupportContext);
    const response = await fetch(`/api/images/${encodeURIComponent(id)}/variants/${preset}`, { credentials: 'include', cache: 'no-store', headers, signal });
    if (version !== supportContextVersion) throw new ApiError('Support context changed while the image preview was loading.', 409);
    if (!response.ok) throw new ApiError('The optimized image preview could not be loaded.', response.status);
    const blob = await response.blob();
    if (version !== supportContextVersion) throw new ApiError('Support context changed while the image preview was loading.', 409);
    return blob;
  },
  async uploadImage(file: File, folderId: string | null, protectionMode: ImageItem['protectionMode'], signal?: AbortSignal): Promise<ImageItem> {
    const created = unwrap(await request('/api/images/uploads', { method: 'POST', body: { originalFilename: file.name, sourceBytes: file.size, folderId, protectionMode }, signal }), 'image') as ImageItem;
    return unwrap(await request(`/api/images/${encodeURIComponent(created.id)}/source`, { method: 'PUT', headers: { 'Content-Type': 'application/octet-stream' }, rawBody: file, signal }), 'image') as ImageItem;
  },
  async updateImage(id: string, changes: { name?: string; folderId?: string | null }): Promise<ImageItem> {
    return unwrap(await request(`/api/images/${encodeURIComponent(id)}`, { method: 'PATCH', body: changes }), 'image') as ImageItem;
  },
  async imageAction(id: string, action: 'publish' | 'unpublish' | 'trash' | 'restore' | 'retry'): Promise<ImageItem> {
    return unwrap(await request(`/api/images/${encodeURIComponent(id)}/${action}`, { method: 'POST' }), 'image') as ImageItem;
  },
  async purgeImage(id: string): Promise<void> {
    await request(`/api/images/${encodeURIComponent(id)}?confirm=${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
  async imageOriginal(id: string): Promise<Blob> {
    const response = await fetch(`/api/images/${encodeURIComponent(id)}/original`, { credentials: 'include' });
    if (!response.ok) throw new ApiError('Original image could not be downloaded.', response.status);
    return response.blob();
  },
  async imagePublicShareSnapshot(id: string): Promise<{ links: PublicShareLink[]; usage: HostedQuotaUsage | null }> {
    const source = record(await request(`/api/account-lifecycle/public-shares/images/${encodeURIComponent(id)}`));
    const usage = record(source.usage);
    return { links: Array.isArray(source.links) ? source.links as PublicShareLink[] : [], usage: Number.isSafeInteger(usage.active) && Number.isSafeInteger(usage.limit) && Number(usage.limit) > 0 ? usage as unknown as HostedQuotaUsage : null };
  },
  async createImagePublicShare(id: string, input: { kind: PublicShareLink['kind']; title: string; description: string; creatorName: string; allowOriginalDownload: boolean }): Promise<PublicShareLink> {
    return (await request<{ link: PublicShareLink }>(`/api/account-lifecycle/public-shares/images/${encodeURIComponent(id)}`, { method: 'POST', body: input })).link;
  },
  async memberPublicBranding(): Promise<MemberPublicBranding> {
    return normalizeMemberPublicBranding(await request<unknown>('/api/account-lifecycle/public-branding'));
  },
  async saveMemberPublicBranding(input: MemberPublicBrandingInput): Promise<MemberPublicBranding> {
    return normalizeMemberPublicBranding(await request<unknown>('/api/account-lifecycle/public-branding', { method: 'PUT', body: input }));
  },
  async resetMemberPublicBranding(): Promise<MemberPublicBranding> {
    return normalizeMemberPublicBranding(await request<unknown>('/api/account-lifecycle/public-branding/reset', { method: 'POST', body: {} }));
  },
  async requestMemberPublicDomain(hostname: string): Promise<void> {
    await request('/api/account-lifecycle/public-branding/domains', { method: 'POST', body: { hostname } });
  },
  async checkMemberPublicDomain(id: string): Promise<void> {
    await request(`/api/account-lifecycle/public-branding/domains/${encodeURIComponent(id)}/check`, { method: 'POST', body: {} });
  },
  async removeMemberPublicDomain(id: string): Promise<void> {
    await request(`/api/account-lifecycle/public-branding/domains/${encodeURIComponent(id)}/remove`, { method: 'POST', body: {} });
  },
  async memberPublicDomainRequests(): Promise<MemberPublicDomainRequest[]> {
    return arrayFrom(await request<unknown>('/api/account-lifecycle/public-domain-requests'), 'requests') as MemberPublicDomainRequest[];
  },
  async activateMemberPublicDomain(id: string, checks?: { dnsVerified: boolean; proxyVerified: boolean; httpsVerified: boolean }, replaceDomainId?: string): Promise<void> {
    await request(`/api/account-lifecycle/public-domain-requests/${encodeURIComponent(id)}/activate`, { method: 'POST', body: { ...checks, ...(replaceDomainId ? { replaceDomainId } : {}) } });
  },
  async rejectMemberPublicDomain(id: string, reason: string): Promise<void> {
    await request(`/api/account-lifecycle/public-domain-requests/${encodeURIComponent(id)}/reject`, { method: 'POST', body: { reason } });
  },
  async disableMemberPublicDomain(id: string, reason: string): Promise<void> {
    await request(`/api/account-lifecycle/public-domain-requests/${encodeURIComponent(id)}/disable`, { method: 'POST', body: { reason } });
  },
  async publicShareSnapshot(videoId: string): Promise<{ links: PublicShareLink[]; usage: HostedQuotaUsage | null }> {
    const source = record(await request<unknown>(`/api/account-lifecycle/public-shares/videos/${encodeURIComponent(videoId)}`));
    const usage = record(source.usage); const active = Number(usage.active); const limit = Number(usage.limit);
    return {
      links: Array.isArray(source.links) ? source.links as PublicShareLink[] : [],
      usage: Number.isSafeInteger(active) && Number.isSafeInteger(limit) && active >= 0 && limit > 0 ? { active, limit } : null,
    };
  },
  async publicShareLinks(videoId: string): Promise<PublicShareLink[]> {
    return (await api.publicShareSnapshot(videoId)).links;
  },
  async publicShareUsage(videoId: string): Promise<HostedQuotaUsage | null> {
    return (await api.publicShareSnapshot(videoId)).usage;
  },
  async createPublicShare(videoId: string, input: { kind: PublicShareLink['kind']; title: string; description: string; creatorName: string }): Promise<PublicShareLink> {
    return (await request<{ link: PublicShareLink }>(`/api/account-lifecycle/public-shares/videos/${encodeURIComponent(videoId)}`, { method: 'POST', body: input })).link;
  },
  async revokePublicShare(id: string): Promise<void> {
    await request(`/api/account-lifecycle/public-shares/${encodeURIComponent(id)}/revoke`, { method: 'POST' });
  },
  async branding(signal?: AbortSignal): Promise<PublicBranding> {
    return request<PublicBranding>('/api/branding', { signal });
  },
  async capabilities(): Promise<{ imageHosting?: boolean; accountLifecycle: boolean; publicSharePages?: boolean; corePublicBranding?: boolean; memberProvisioning: boolean; memberEntitlements?: boolean; effectiveEntitlementResolver?: boolean; hostedProcessingScheduling?: boolean; notificationSupportOperations?: boolean; memberOtpSessionsEmailChange?: boolean; memberPasswordBrowserTrust?: boolean }> {
    const source = record(await request<unknown>('/api/capabilities'));
    return {
      imageHosting: booleanValue(source.imageHosting),
      accountLifecycle: booleanValue(source.accountLifecycle),
      publicSharePages: booleanValue(source.publicSharePages),
      corePublicBranding: booleanValue(source.corePublicBranding),
      memberProvisioning: booleanValue(source.memberProvisioning),
      memberEntitlements: booleanValue(source.memberEntitlements),
      effectiveEntitlementResolver: booleanValue(source.effectiveEntitlementResolver),
      hostedProcessingScheduling: booleanValue(source.hostedProcessingScheduling),
      notificationSupportOperations: booleanValue(source.notificationSupportOperations),
      memberOtpSessionsEmailChange: booleanValue(source.memberOtpSessionsEmailChange),
      memberPasswordBrowserTrust: booleanValue(source.memberPasswordBrowserTrust),
    };
  },
  async activateAccount(token: string, password: string): Promise<void> {
    await request('/api/account-lifecycle/activation', { method: 'POST', body: { token, password } });
  },
  async requestPasswordRecovery(email: string): Promise<void> {
    await request('/api/account-lifecycle/recovery/request', { method: 'POST', body: { email } });
  },
  async hostedEntitlement(): Promise<HostedEntitlement> {
    return normalizeHostedEntitlement(await request<unknown>('/api/account-lifecycle/entitlement'));
  },
  async memberPurchaseOptions(): Promise<MemberPurchaseOptions> {
    const source = record(record(await request<unknown>('/api/account-lifecycle/purchase-options')).options);
    return Object.fromEntries(['storage', 'processing', 'subtitle'].map((resource) => {
      const option = record(source[resource]);
      // A missing/malformed option must not become an actionable purchase link.
      let href: string | null = null;
      try {
        const url = new URL(text(option.href));
        if (url.protocol === 'https:' && !url.username && !url.password && !url.search && !url.hash) href = url.href;
      } catch { /* Unavailable until a valid server-owned destination is returned. */ }
      const unavailableReason = text(option.unavailableReason) || null;
      return [resource, { label: text(option.label, 'Add allowance'), detail: text(option.detail),
        href: unavailableReason ? null : href,
        unavailableReason: unavailableReason ?? (href ? null : 'This top-up is not available yet.') }];
    })) as MemberPurchaseOptions;
  },
  async completePasswordRecovery(token: string, password: string): Promise<void> {
    await request('/api/account-lifecycle/recovery/complete', { method: 'POST', body: { token, password } });
  },
  async login(email: string, password: string): Promise<User> {
    replaceSupportContext(null);
    const payload = await request<unknown>('/api/auth/login', { method: 'POST', body: { email, password } });
    return normalizeUser(payload);
  },
  async requestMemberOtp(email: string): Promise<{ challengeId: string; message: string; expiresInSeconds: number }> {
    const source = record(await request<unknown>('/api/account-lifecycle/member-otp/request', {
      method: 'POST', body: { email },
    }));
    return {
      challengeId: text(first(source, 'challengeId', 'challenge_id')),
      message: text(source.message),
      expiresInSeconds: numberValue(first(source, 'expiresInSeconds', 'expires_in_seconds'), 600),
    };
  },
  async requestAccountOtp(email: string): Promise<{ challengeId: string; message: string; expiresInSeconds: number }> {
    const source = record(await request<unknown>('/api/account-lifecycle/account-otp/request', {
      method: 'POST', body: { email },
    }));
    return {
      challengeId: text(first(source, 'challengeId', 'challenge_id')),
      message: text(source.message),
      expiresInSeconds: numberValue(first(source, 'expiresInSeconds', 'expires_in_seconds'), 600),
    };
  },
  async verifyMemberOtp(email: string, challengeId: string, code: string): Promise<User> {
    replaceSupportContext(null);
    const payload = await request<unknown>('/api/account-lifecycle/member-otp/verify', {
      method: 'POST', body: { email, challengeId, code },
    });
    return normalizeUser(payload);
  },
  async signInWithBrowserTrust(email: string, password: string): Promise<{ user: User } | { challengeId: string; message: string; canTrustBrowser: boolean }> {
    replaceSupportContext(null);
    const payload = await request<unknown>('/api/account-lifecycle/account-password/login', { method: 'POST', body: { email, password } });
    const source = record(payload);
    if (booleanValue(source.requiresOtp)) return { challengeId: text(source.challengeId), message: text(source.message), canTrustBrowser: booleanValue(source.canTrustBrowser) };
    return { user: normalizeUser(payload) };
  },
  async verifyAccountOtp(email: string, challengeId: string, code: string, trustBrowser = false): Promise<User> {
    replaceSupportContext(null);
    const payload = await request<unknown>('/api/account-lifecycle/account-otp/verify', {
      method: 'POST', body: { email, challengeId, code, ...(trustBrowser ? { trustBrowser } : {}) },
    });
    return normalizeUser(payload);
  },
  async previewAccountMagicLink(token: string): Promise<{ email: string; canTrustBrowser?: boolean }> {
    const source = record(await request<unknown>('/api/account-lifecycle/account-magic-link/preview', {
      method: 'POST', body: { token }, referrerPolicy: 'no-referrer',
    }));
    return { email: text(source.email), ...(source.canTrustBrowser === undefined ? {} : { canTrustBrowser: booleanValue(source.canTrustBrowser) }) };
  },
  async verifyAccountMagicLink(token: string, trustBrowser = false): Promise<User> {
    replaceSupportContext(null);
    return normalizeUser(await request<unknown>('/api/account-lifecycle/account-magic-link/verify', {
      method: 'POST', body: { token, ...(trustBrowser ? { trustBrowser } : {}) }, referrerPolicy: 'no-referrer',
    }));
  },
  async me(): Promise<User> {
    return normalizeUser(await request<unknown>('/api/auth/me'));
  },
  async logout(): Promise<void> {
    await request('/api/auth/logout', { method: 'POST' });
    csrfToken = '';
    replaceSupportContext(null);
  },
  clearSupportContext(): void {
    replaceSupportContext(null);
  },
  async enterSupportContext(member: HostedMember, reason: string): Promise<SupportContext> {
    const source = record(await request<unknown>('/api/account-lifecycle/support-context', {
      method: 'POST',
      body: { memberId: member.id, reason },
    }));
    const context: SupportContext = {
      memberId: text(source.memberId, member.id),
      memberName: text(source.memberName, member.displayName),
      memberEmail: text(source.memberEmail, member.email),
      reason: text(source.reason, reason.trim()),
    };
    replaceSupportContext(context);
    return context;
  },
  async folders(options: { memberCatalog?: boolean } = {}): Promise<Folder[]> {
    return normalizeFolders(await request<unknown>(`/api/folders/tree${options.memberCatalog ? '?catalog=members' : ''}`));
  },
  async createFolder(name: string, parentId: string | null): Promise<Folder> {
    const payload = await request<unknown>('/api/folders', {
      method: 'POST',
      body: { name, parent_id: parentId },
    });
    return rawFolder(unwrap(payload, 'folder'));
  },
  async updateFolder(id: string, changes: { name?: string; parentId?: string | null }): Promise<Folder> {
    const body: JsonRecord = {};
    if (changes.name !== undefined) body.name = changes.name;
    if (changes.parentId !== undefined) body.parent_id = changes.parentId;
    return rawFolder(unwrap(await request<unknown>(`/api/folders/${encodeURIComponent(id)}`, { method: 'PATCH', body }), 'folder'));
  },
  async deleteFolder(id: string, recursive: boolean): Promise<void> {
    const query = new URLSearchParams({ recursive: String(recursive) });
    if (recursive) query.set('confirm', id);
    await request(`/api/folders/${encodeURIComponent(id)}?${query.toString()}`, { method: 'DELETE' });
  },
  async videos(filters: { folderId: string | null; search: string; status: string; scope?: 'active' | 'trash'; ownerUserId?: string | null; memberCatalog?: boolean }): Promise<VideoListResult> {
    const query = new URLSearchParams();
    if (filters.folderId) query.set('folder_id', filters.folderId);
    if (filters.search) query.set('search', filters.search);
    if (filters.status && filters.status !== 'all') query.set('status', filters.status);
    if (filters.scope === 'trash') query.set('scope', 'trash');
    if (filters.ownerUserId) query.set('owner_user_id', filters.ownerUserId);
    if (filters.memberCatalog) query.set('catalog', 'members');
    return normalizeVideos(await request<unknown>(`/api/videos?${query.toString()}`));
  },
  async pendingUploadSessions(): Promise<UploadSession[]> {
    return normalizeUploadSessions(await request<unknown>('/api/upload-sessions?status=awaiting_duplicate_decision'));
  },
  async uploadSession(id: string): Promise<UploadSession> {
    return requestUploadSession(id);
  },
  async decideUploadDuplicate(id: string, decision: 'continue' | 'cancel'): Promise<UploadSession> {
    return normalizeUploadSession(await request<unknown>(`/api/upload-sessions/${encodeURIComponent(id)}/decision`, {
      method: 'POST',
      body: { decision },
    }));
  },
  async video(id: string): Promise<VideoItem> {
    return normalizeVideo(unwrap(await request<unknown>(`/api/videos/${encodeURIComponent(id)}`), 'video'));
  },
  async importHls(input: {
    sourceUrl: string;
    title?: string;
    folderId: string | null;
    thumbnailUrl?: string;
    protectionMode?: 'automatic' | 'r2_only' | 'protected';
  }): Promise<VideoItem> {
    return normalizeVideo(unwrap(await request<unknown>('/api/videos/imports', {
      method: 'POST',
      body: {
        source_url: input.sourceUrl,
        ...(input.title ? { title: input.title } : {}),
        folder_id: input.folderId,
        ...(input.thumbnailUrl ? { thumbnail_url: input.thumbnailUrl } : {}),
      },
    }), 'video'));
  },
  async updateVideo(id: string, changes: {
    title?: string;
    description?: string;
    folderId?: string | null;
    embedPolicy?: NonNullable<VideoItem['embedPolicy']>;
    playerSettings?: NonNullable<VideoItem['playerSettings']>;
  }): Promise<VideoItem> {
    const body: JsonRecord = {};
    if (changes.title !== undefined) body.title = changes.title;
    if (changes.description !== undefined) body.description = changes.description;
    if (changes.folderId !== undefined) body.folder_id = changes.folderId;
    if (changes.embedPolicy !== undefined) body.embed_policy = changes.embedPolicy;
    if (changes.playerSettings !== undefined) body.player_settings = changes.playerSettings;
    return normalizeVideo(unwrap(await request<unknown>(`/api/videos/${encodeURIComponent(id)}`, { method: 'PATCH', body }), 'video'));
  },
  async videoMetadataDraft(id: string): Promise<VideoMetadataDraft> {
    const payload = record(await request<unknown>(`/api/videos/${encodeURIComponent(id)}/metadata-draft`, { method: 'POST', body: {} }));
    const draft = record(payload.draft);
    const sourceTrack = record(payload.sourceTrack);
    return {
      description: text(draft.description),
      sourceTrack: {
        id: text(sourceTrack.id),
        label: text(sourceTrack.label),
        languageCode: text(sourceTrack.languageCode),
      },
    };
  },
  async getVideoThumbnailOptions(id: string): Promise<VideoThumbnailOption[]> {
    return arrayFrom(await request<unknown>(`/api/videos/${encodeURIComponent(id)}/thumbnail-options`), 'options')
      .map((value) => {
        const source = record(value);
        return {
          positionPercent: numberValue(source.positionPercent),
          timeSeconds: numberValue(source.timeSeconds),
          dataUrl: text(source.dataUrl),
        };
      })
      .filter((option) => option.positionPercent > 0 && option.timeSeconds >= 0 && option.dataUrl.startsWith('data:image/jpeg;base64,'));
  },
  async updateVideoThumbnailFromVideo(id: string, timeSeconds: number): Promise<VideoItem> {
    return normalizeVideo(unwrap(await request<unknown>(`/api/videos/${encodeURIComponent(id)}/thumbnail/frame`, {
      method: 'PUT',
      body: { timeSeconds },
    }), 'video'));
  },
  async updateVideoThumbnail(id: string, file: File): Promise<VideoItem> {
    return normalizeVideo(unwrap(await request<unknown>(`/api/videos/${encodeURIComponent(id)}/thumbnail`, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      rawBody: file,
    }), 'video'));
  },
  async captionTracks(id: string): Promise<CaptionTrack[]> {
    return arrayFrom(await request<unknown>(`/api/videos/${encodeURIComponent(id)}/captions`), 'tracks')
      .map(normalizeCaptionTrack);
  },
  async captionTranscript(videoId: string, trackId: string): Promise<CaptionCue[]> {
    return arrayFrom(await request<unknown>(`/api/videos/${encodeURIComponent(videoId)}/captions/${encodeURIComponent(trackId)}/transcript`), 'cues')
      .map(normalizeCaptionCue);
  },
  async updateCaptionCue(videoId: string, trackId: string, cueIndex: number, textValue: string): Promise<{ cue: CaptionCue; track: CaptionTrack }> {
    const payload = record(await request<unknown>(`/api/videos/${encodeURIComponent(videoId)}/captions/${encodeURIComponent(trackId)}/cues/${cueIndex}`, {
      method: 'PATCH',
      body: { text: textValue },
    }));
    return {
      cue: normalizeCaptionCue(payload.cue),
      track: normalizeCaptionTrack(payload.track),
    };
  },
  async uploadCaptionTrack(id: string, input: {
    file: File;
    label: string;
    languageCode: string;
    kind: CaptionTrack['kind'];
    isDefault: boolean;
    replaceExisting: boolean;
  }): Promise<CaptionTrack> {
    const query = new URLSearchParams({
      label: input.label,
      language: input.languageCode,
      kind: input.kind,
      default: String(input.isDefault),
      replace: String(input.replaceExisting),
    });
    const extension = input.file.name.toLowerCase().split('.').pop();
    const contentType = extension === 'srt' ? 'application/x-subrip' : 'text/vtt';
    return normalizeCaptionTrack(unwrap(await request<unknown>(`/api/videos/${encodeURIComponent(id)}/captions?${query}`, {
      method: 'POST',
      headers: { 'Content-Type': contentType },
      rawBody: await input.file.text(),
    }), 'track'));
  },
  async updateCaptionTrack(videoId: string, trackId: string, changes: Partial<Pick<CaptionTrack, 'label' | 'languageCode' | 'kind' | 'isDefault'>>): Promise<CaptionTrack> {
    return normalizeCaptionTrack(unwrap(await request<unknown>(`/api/videos/${encodeURIComponent(videoId)}/captions/${encodeURIComponent(trackId)}`, {
      method: 'PATCH', body: changes,
    }), 'track'));
  },
  async deleteCaptionTrack(videoId: string, trackId: string): Promise<void> {
    await request(`/api/videos/${encodeURIComponent(videoId)}/captions/${encodeURIComponent(trackId)}`, { method: 'DELETE' });
  },
  async transcriptions(videoId: string): Promise<TranscriptionOverview> {
    const payload = record(await request<unknown>(`/api/videos/${encodeURIComponent(videoId)}/transcriptions`));
    const availability = record(payload.availability);
    const quota = payload.quota ? record(payload.quota) : null;
    return {
      jobs: arrayFrom(payload, 'jobs').map(normalizeTranscriptionJob),
      availability: {
        standard: booleanValue(availability.standard),
        diarization: booleanValue(availability.diarization),
      },
      quotaManaged: booleanValue(first(payload, 'quotaManaged', 'quota_managed')),
      quota: quota ? {
        consumedSeconds: numberValue(first(quota, 'consumedSeconds', 'consumed_seconds')),
        reservedSeconds: numberValue(first(quota, 'reservedSeconds', 'reserved_seconds')),
        allowanceSeconds: numberValue(first(quota, 'allowanceSeconds', 'allowance_seconds')),
        creditConsumedSeconds: numberValue(first(quota, 'creditConsumedSeconds', 'credit_consumed_seconds')),
        creditReservedSeconds: numberValue(first(quota, 'creditReservedSeconds', 'credit_reserved_seconds')),
        creditAvailableSeconds: numberValue(first(quota, 'creditAvailableSeconds', 'credit_available_seconds')),
        creditEarliestExpiry: text(first(quota, 'creditEarliestExpiry', 'credit_earliest_expiry')) || null,
        periodStart: text(first(quota, 'periodStart', 'period_start')) || undefined,
        periodEnd: text(first(quota, 'periodEnd', 'period_end')) || undefined,
      } : null,
    };
  },
  async requestTranscription(videoId: string, input: {
    languageCode: string | null;
    label: string;
    identifySpeakers: boolean;
    isDefault: boolean;
    replaceExisting: boolean;
  }): Promise<TranscriptionJob> {
    return normalizeTranscriptionJob(unwrap(await request<unknown>(`/api/videos/${encodeURIComponent(videoId)}/transcriptions`, {
      method: 'POST', body: input,
    }), 'job'));
  },
  async cancelTranscription(videoId: string, jobId: string): Promise<TranscriptionJob> {
    return normalizeTranscriptionJob(unwrap(await request<unknown>(`/api/videos/${encodeURIComponent(videoId)}/transcriptions/${encodeURIComponent(jobId)}/cancel`, {
      method: 'POST', body: {},
    }), 'job'));
  },
  async chapters(id: string): Promise<VideoChapter[]> {
    return arrayFrom(await request<unknown>(`/api/videos/${encodeURIComponent(id)}/chapters`), 'chapters')
      .map(normalizeVideoChapter);
  },
  async saveChapters(id: string, chapters: Array<Pick<VideoChapter, 'title' | 'startSeconds'>>): Promise<VideoChapter[]> {
    return arrayFrom(await request<unknown>(`/api/videos/${encodeURIComponent(id)}/chapters`, {
      method: 'PUT', body: { chapters },
    }), 'chapters').map(normalizeVideoChapter);
  },
  async videoAnalytics(id: string, days = 30): Promise<VideoAnalytics> {
    const payload = record(await request<unknown>(`/api/videos/${encodeURIComponent(id)}/analytics?days=${days}`));
    const summary = record(payload.summary);
    return {
      rangeDays: numberValue(payload.rangeDays, days),
      summary: {
        views: numberValue(summary.views),
        watchSeconds: numberValue(summary.watchSeconds),
        averageWatchSeconds: numberValue(summary.averageWatchSeconds),
        completions: numberValue(summary.completions),
        completionRate: numberValue(summary.completionRate),
      },
      daily: arrayFrom(payload, 'daily').map((value) => {
        const item = record(value);
        return { day: text(item.day), views: numberValue(item.views), watchSeconds: numberValue(item.watchSeconds), completions: numberValue(item.completions) };
      }),
      origins: arrayFrom(payload, 'origins').map((value) => {
        const item = record(value);
        return { origin: text(item.origin, 'unknown'), views: numberValue(item.views), watchSeconds: numberValue(item.watchSeconds) };
      }),
      retention: arrayFrom(payload, 'retention').map((value) => {
        const item = record(value);
        return { percent: numberValue(item.percent), viewers: numberValue(item.viewers) };
      }),
    };
  },
  async deleteVideo(id: string): Promise<void> {
    await request(`/api/videos/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
  async restoreVideo(id: string): Promise<VideoItem> {
    return normalizeVideo(unwrap(await request<unknown>(`/api/videos/${encodeURIComponent(id)}/restore`, { method: 'POST' }), 'video'));
  },
  async purgeVideo(id: string): Promise<void> {
    await request(`/api/videos/${encodeURIComponent(id)}/purge?confirm=${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
  async retryVideo(id: string): Promise<VideoItem> {
    return normalizeVideo(unwrap(await request<unknown>(`/api/videos/${encodeURIComponent(id)}/retry`, { method: 'POST' }), 'video'));
  },
  async playbackSources(id: string): Promise<PlaybackSources> {
    return requestPlaybackSources(id);
  },
  async playbackUrl(id: string): Promise<string> {
    return (await requestPlaybackSources(id)).hlsUrl;
  },
  async stats(options: { installation?: boolean } = {}): Promise<Stats> {
    return normalizeStats(await request<unknown>(`/api/stats${options.installation ? '?scope=installation' : ''}`));
  },
  async mediaStorage(): Promise<MediaStorageOverview> {
    return normalizeMediaStorage(await request<unknown>('/api/media-storage'));
  },
  async sourceCleanup(): Promise<SourceCleanupOverview> {
    return normalizeSourceCleanupOverview(await request<unknown>('/api/media-storage/source-cleanup'));
  },
  async retrySourceCleanup(videoId: string): Promise<SourceCleanupOverview> {
    return normalizeSourceCleanupOverview(await request<unknown>(`/api/media-storage/source-cleanup/${encodeURIComponent(videoId)}/retry`, { method: 'POST' }));
  },
  async retryAllSourceCleanup(): Promise<SourceCleanupOverview> {
    return normalizeSourceCleanupOverview(await request<unknown>('/api/media-storage/source-cleanup/retry', { method: 'POST' }));
  },
  async testR2(input: R2ConnectionInput): Promise<void> {
    await request('/api/media-storage/r2/test', { method: 'POST', body: input });
  },
  async saveR2(input: R2ConnectionInput): Promise<MediaStorageOverview> {
    return normalizeMediaStorage(await request<unknown>('/api/media-storage/r2', { method: 'PUT', body: input }));
  },
  async verifyR2Delivery(): Promise<MediaStorageOverview> {
    return normalizeMediaStorage(await request<unknown>('/api/media-storage/r2/verify-delivery', { method: 'POST' }));
  },
  async setDefaultMediaStorage(backend: 'local' | 'r2'): Promise<MediaStorageOverview> {
    return normalizeMediaStorage(await request<unknown>('/api/media-storage/default', { method: 'PATCH', body: { backend } }));
  },
  async disconnectR2(): Promise<void> {
    await request('/api/media-storage/r2', { method: 'DELETE' });
  },
  async backupOverview(): Promise<BackupOverview> {
    return normalizeBackupOverview(await request<unknown>('/api/backups'));
  },
  async reauthenticateBackups(password: string): Promise<void> {
    await request('/api/backups/reauthenticate', { method: 'POST', body: { password } });
  },
  async mediaArchiveOverview(): Promise<MediaArchiveOverview> {
    return normalizeMediaArchiveOverview(await request<unknown>('/api/media-archive'));
  },
  async mediaArchiveRecords(filters: MediaArchiveRecordFilters = {}): Promise<MediaArchiveRecordPage> {
    const query = new URLSearchParams();
    if (filters.member) query.set('member', filters.member);
    if (filters.video) query.set('video', filters.video);
    if (filters.recoverable) query.set('recoverable', 'true');
    if (filters.state && filters.state !== 'all') query.set('state', filters.state);
    if (filters.from) query.set('from', filters.from);
    if (filters.to) query.set('to', filters.to);
    if (filters.limit !== undefined) query.set('limit', String(filters.limit));
    if (filters.offset !== undefined) query.set('offset', String(filters.offset));
    const suffix = query.size ? `?${query.toString()}` : '';
    return normalizeMediaArchiveRecordPage(await request<unknown>(`/api/media-archive/records${suffix}`));
  },
  async mediaArchiveRecord(id: string): Promise<MediaArchiveRecordDetail> {
    return normalizeMediaArchiveRecordDetail(await request<unknown>(
      `/api/media-archive/records/${encodeURIComponent(id)}`,
    ));
  },
  async mediaArchiveAudit(limit = 50, offset = 0): Promise<MediaArchiveAuditPage> {
    return normalizeMediaArchiveAuditPage(await request<unknown>(
      `/api/media-archive/audit?limit=${encodeURIComponent(limit)}&offset=${encodeURIComponent(offset)}`,
    ));
  },
  async updateMediaArchiveSettings(input: MediaArchivePolicyInput): Promise<MediaArchiveSettings> {
    const payload = record(await request<unknown>('/api/media-archive/settings', {
      method: 'PATCH', body: input,
    }));
    return normalizeMediaArchiveSettings(payload.settings);
  },
  async mediaArchiveDriveOperation(
    operationType: 'connect_drive' | 'reauthorize_drive' | 'disconnect_drive' | 'reset_drive',
    body: { destinationFolderName?: string; reason?: string },
    idempotencyKey: string,
  ): Promise<MediaArchiveOperation> {
    const payload = record(await request<unknown>(
      `/api/media-archive/drive/operations/${encodeURIComponent(operationType)}`,
      { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body },
    ));
    return normalizeMediaArchiveOperation(payload.operation);
  },
  async cancelMediaArchiveDriveAuthorization(reason: string): Promise<MediaArchiveOperation> {
    const payload = record(await request<unknown>('/api/media-archive/drive/authorization/cancel', {
      method: 'POST', body: { reason },
    }));
    return normalizeMediaArchiveOperation(payload.operation);
  },
  async testMediaArchiveR2(input: MediaArchiveR2Input): Promise<void> {
    await request('/api/media-archive/r2/test', { method: 'POST', body: input });
  },
  async saveMediaArchiveR2(input: MediaArchiveR2Input): Promise<MediaArchiveSettings> {
    const payload = record(await request<unknown>('/api/media-archive/r2', { method: 'PUT', body: input }));
    return normalizeMediaArchiveSettings(payload.settings);
  },
  async removeMediaArchiveR2(reason: string): Promise<void> {
    await request('/api/media-archive/r2', { method: 'DELETE', body: { reason } });
  },
  async backfillMediaArchive(idempotencyKey: string): Promise<MediaArchiveOperation> {
    const payload = record(await request<unknown>('/api/media-archive/backfill', {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: {},
    }));
    return normalizeMediaArchiveOperation(payload.operation);
  },
  async retryMediaArchive(id: string, idempotencyKey: string): Promise<MediaArchiveOperation> {
    const payload = record(await request<unknown>(
      `/api/media-archive/records/${encodeURIComponent(id)}/retry`,
      { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: {} },
    ));
    return normalizeMediaArchiveOperation(payload.operation);
  },
  async verifyMediaArchive(id: string, idempotencyKey: string): Promise<MediaArchiveOperation> {
    const payload = record(await request<unknown>(
      `/api/media-archive/records/${encodeURIComponent(id)}/verify`,
      { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: {} },
    ));
    return normalizeMediaArchiveOperation(payload.operation);
  },
  async mediaArchiveRestorePlan(id: string): Promise<MediaArchiveRestorePlan> {
    const payload = record(await request<unknown>(
      `/api/media-archive/records/${encodeURIComponent(id)}/restore-plan`,
    ));
    return normalizeMediaArchiveRestorePlan(payload.plan);
  },
  async restoreMediaArchive(id: string, reason: string, idempotencyKey: string): Promise<MediaArchiveOperation> {
    const payload = record(await request<unknown>(
      `/api/media-archive/records/${encodeURIComponent(id)}/restore`,
      { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: { reason } },
    ));
    return normalizeMediaArchiveOperation(payload.operation);
  },
  async cancelMediaArchiveRestore(operationId: string, reason: string): Promise<void> {
    await request(`/api/media-archive/operations/${encodeURIComponent(operationId)}/cancel`, {
      method: 'POST', body: { reason },
    });
  },
  async mediaArchiveRecoveryCoverage(): Promise<MediaArchiveRecoveryCoverage> {
    const payload = record(await request<unknown>('/api/media-archive/recovery-targets/coverage'));
    return normalizeMediaArchiveRecoveryCoverage(payload.coverage);
  },
  async testMediaArchiveRecoveryTarget(input: MediaArchiveRecoveryTargetInput): Promise<void> {
    await request('/api/media-archive/recovery-targets/test', { method: 'POST', body: input });
  },
  async prepareMediaArchiveRecoveryTarget(
    input: MediaArchiveRecoveryTargetInput,
    idempotencyKey: string,
  ): Promise<MediaArchiveRecoveryTarget> {
    const payload = record(await request<unknown>('/api/media-archive/recovery-targets', {
      method: 'PUT', headers: { 'Idempotency-Key': idempotencyKey }, body: input,
    }));
    const target = normalizeMediaArchiveRecoveryTarget(payload.target);
    if (!target) throw new ApiError('Recovery target response was invalid', 502, { code: 'invalid_response' });
    return target;
  },
  async restoreMediaArchiveRecoveryTarget(
    targetId: string, reason: string, idempotencyKey: string,
  ): Promise<MediaArchiveOperation> {
    const payload = record(await request<unknown>(
      `/api/media-archive/recovery-targets/${encodeURIComponent(targetId)}/restore`,
      { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: { reason } },
    ));
    return normalizeMediaArchiveOperation(payload.operation);
  },
  async verifyMediaArchiveRecoveryTarget(
    targetId: string, reason: string, idempotencyKey: string,
  ): Promise<MediaArchiveOperation> {
    const payload = record(await request<unknown>(
      `/api/media-archive/recovery-targets/${encodeURIComponent(targetId)}/verify`,
      { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: { reason } },
    ));
    return normalizeMediaArchiveOperation(payload.operation);
  },
  async activateMediaArchiveRecoveryTarget(
    targetId: string, reason: string, confirmation: string, idempotencyKey: string,
  ): Promise<MediaArchiveRecoveryTarget> {
    const payload = record(await request<unknown>(
      `/api/media-archive/recovery-targets/${encodeURIComponent(targetId)}/activate`,
      { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: { reason, confirmation } },
    ));
    const target = normalizeMediaArchiveRecoveryTarget(payload.target);
    if (!target) throw new ApiError('Recovery activation response was invalid', 502, { code: 'invalid_response' });
    return target;
  },
  async abortMediaArchiveRecoveryTarget(targetId: string, reason: string): Promise<void> {
    await request(`/api/media-archive/recovery-targets/${encodeURIComponent(targetId)}/abort`, {
      method: 'POST', body: { reason },
    });
  },
  async cancelMediaArchiveRecoveryOperation(
    targetId: string, operationId: string, reason: string,
  ): Promise<void> {
    await request(
      `/api/media-archive/recovery-targets/${encodeURIComponent(targetId)}/operations/${encodeURIComponent(operationId)}/cancel`,
      { method: 'POST', body: { reason } },
    );
  },
  async acknowledgeRecoveryKit(operationId: string): Promise<void> {
    await request('/api/backups/recovery-kit/acknowledge', { method: 'POST', body: { operationId } });
  },
  async createBackupOperation(type: BackupOperationType, payload: BackupOperationPayload | undefined, idempotencyKey: string): Promise<BackupOperation> {
    const requestPayload = type === 'reactivate_repository_generation'
      ? validatedReactivationPayload(payload)
      : payload;
    const response = await request<unknown>('/api/backups/operations', {
      method: 'POST',
      body: { type, ...(requestPayload ? { payload: requestPayload } : {}), idempotencyKey },
    });
    return normalizeBackupOperation(response);
  },
  async cancelBackupOperation(id: string): Promise<void> {
    const path = `/api/backups/operations/${encodeURIComponent(id)}`;
    try {
      await request(path, { method: 'DELETE' });
    } catch (reason) {
      if (!(reason instanceof ApiError) || ![404, 405].includes(reason.status)) throw reason;
      await request(`${path}/cancel`, { method: 'POST' });
    }
  },
  async processingWorkers(): Promise<ProcessingWorkersOverview> {
    return normalizeProcessingWorkers(await request<unknown>('/api/processing-workers'));
  },
  async hostedProcessingOverview(): Promise<HostedProcessingOverview> {
    return normalizeHostedProcessingOverview(await request<unknown>('/api/account-lifecycle/processing'));
  },
  async requestHostedConvertNow(kind: 'transcode' | 'transcription', jobId: string, reason: string): Promise<void> {
    await request(`/api/account-lifecycle/processing/${kind}/${encodeURIComponent(jobId)}/convert-now`, {
      method: 'POST', body: { reason },
    });
  },
  async setHostedPauseAfterCurrent(pause: boolean, reason: string): Promise<void> {
    await request('/api/account-lifecycle/processing/pause-after-current', {
      method: pause ? 'POST' : 'DELETE', body: { reason },
    });
  },
  async cancelProcessingJob(kind: 'transcode' | 'transcription', jobId: string): Promise<void> {
    const path = kind === 'transcription'
      ? `/api/processing-workers/transcriptions/${encodeURIComponent(jobId)}/cancel`
      : `/api/processing-workers/jobs/${encodeURIComponent(jobId)}/cancel`;
    await request(path, { method: 'POST', body: {} });
  },
  async updateProcessingAvailability(input: ProcessingAvailabilityInput): Promise<ProcessingAvailability> {
    const response = record(await request<unknown>('/api/processing-workers/availability', {
      method: 'PUT',
      body: input,
    }));
    return normalizeProcessingAvailability(response.availability);
  },
  async startProcessingOverride(minutes = 60): Promise<ProcessingAvailability> {
    const response = record(await request<unknown>('/api/processing-workers/availability/override', {
      method: 'POST',
      body: { minutes },
    }));
    return normalizeProcessingAvailability(response.availability);
  },
  async stopProcessingOverride(): Promise<ProcessingAvailability> {
    const response = record(await request<unknown>('/api/processing-workers/availability/override', {
      method: 'DELETE',
    }));
    return normalizeProcessingAvailability(response.availability);
  },
  async createProcessingWorkerEnrollment(name: string): Promise<WorkerEnrollment> {
    return normalizeWorkerEnrollment(await request<unknown>('/api/processing-workers/enrollment', {
      method: 'POST',
      body: { name },
    }));
  },
  async revokeProcessingWorker(id: string): Promise<void> {
    await request(`/api/processing-workers/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await request('/api/auth/change-password', { method: 'POST', body: { currentPassword, newPassword } });
  },
  async reauthenticate(password: string): Promise<string> {
    const source = record(await request<unknown>('/api/auth/reauthenticate', { method: 'POST', body: { password } }));
    return text(source.elevatedUntil);
  },
  async hostedMembers(filter = ''): Promise<HostedMember[]> {
    const query = filter ? `?filter=${encodeURIComponent(filter)}` : '';
    return arrayFrom(await request<unknown>(`/api/account-lifecycle/members${query}`), 'members').map(normalizeHostedMember);
  },
  async hostedMemberPolicyReview(memberId: string): Promise<HostedMemberPolicyReview> {
    return normalizeHostedMemberPolicyReview(await request<unknown>(
      `/api/account-lifecycle/members/${encodeURIComponent(memberId)}/policy-review`,
    ));
  },
  async reviewHostedMemberPolicySignal(memberId: string, input: {
    signalKey: string;
    outcome: HostedPolicyReviewOutcome;
    note?: string;
  }): Promise<HostedPolicyReview> {
    const source = record(await request<unknown>(
      `/api/account-lifecycle/members/${encodeURIComponent(memberId)}/policy-reviews`,
      { method: 'POST', body: input },
    ));
    return normalizeHostedPolicyReview(first(source, 'review'));
  },
  async hostedPlans(): Promise<HostedPlan[]> {
    return arrayFrom(await request<unknown>('/api/account-lifecycle/plans'), 'plans').map(normalizeHostedPlan);
  },
  async hostedCapacity(): Promise<HostedCapacityOverview> {
    return normalizeHostedCapacity(await request<unknown>('/api/account-lifecycle/capacity'));
  },
  async hostedCommercialOrders(): Promise<HostedCommercialOrders> {
    const source = record(await request<unknown>('/api/account-lifecycle/commercial/orders'));
    return {
      fulfilmentEnabled: source.fulfilmentEnabled === true,
      cases: arrayFrom(source.cases).map((value) => {
        const row = record(value); const evidence = record(row.evidence);
        return {
          id: text(row.id), sourceSiteId: text(row.source_site_id), sourceOrderId: text(row.source_order_id),
          kind: text(row.case_kind), state: text(row.state), reason: text(evidence.reason),
          quantity: numberValue(evidence.quantity), productId: text(evidence.productId),
          memberName: text(row.memberName) || null, createdAt: text(row.created_at),
          consumedSeconds: numberValue(row.consumedSeconds), reservedSeconds: numberValue(row.reservedSeconds),
          availableSeconds: numberValue(row.availableSeconds), decision: text(row.decision) || null,
        };
      }),
      reservations: arrayFrom(source.reservations).map((value) => {
        const row = record(value);
        return { id: text(row.id), sourceOrderId: text(row.source_order_id), quantity: numberValue(row.quantity),
          reservedSeconds: numberValue(row.reserved_seconds), state: text(row.state), expiresAt: text(row.expires_at) };
      }),
      events: arrayFrom(source.events).map((value) => {
        const row = record(value);
        return { id: text(row.id), type: text(row.event_type), amountSeconds: nullableNumber(row.amount_seconds),
          createdAt: text(row.created_at), sourceOrderId: text(row.sourceOrderId) || null, actorName: text(row.actorName) || null };
      }),
    };
  },
  async decideHostedCommercialCase(id: string, action: HostedCommercialDecision): Promise<void> {
    await request(`/api/account-lifecycle/commercial/order-cases/${encodeURIComponent(id)}/decisions`, {
      method: 'POST', body: { action },
    });
  },
  async updateHostedCapacitySettings(input: HostedCapacitySettingsInput): Promise<HostedCapacityOverview> {
    return normalizeHostedCapacity(await request<unknown>('/api/account-lifecycle/capacity/settings', {
      method: 'PATCH', body: input,
    }));
  },
  async recordHostedR2CostObservation(input: HostedR2CostObservationInput): Promise<HostedCapacityOverview> {
    return normalizeHostedCapacity(await request<unknown>('/api/account-lifecycle/capacity/r2-observations', {
      method: 'POST', body: input,
    }));
  },
  async previewHostedPlanVersion(planCode: string, input: HostedPlanVersionInput): Promise<HostedPlanPublicationPreview> {
    return normalizeHostedPlanPreview(await request<unknown>(
      `/api/account-lifecycle/plans/${encodeURIComponent(planCode)}/versions/preview`,
      { method: 'POST', body: input },
    ));
  },
  async previewHostedPlan(input: HostedPlanInitialInput): Promise<HostedPlanPublicationPreview> {
    return normalizeHostedPlanPreview(await request<unknown>(
      '/api/account-lifecycle/plans/preview',
      { method: 'POST', body: input },
    ));
  },
  async createHostedPlan(input: HostedPlanCreateInput): Promise<HostedPlanPublicationPreview> {
    return normalizeHostedPlanPreview(await request<unknown>(
      '/api/account-lifecycle/plans',
      { method: 'POST', body: input },
    ));
  },
  async publishHostedPlanVersion(planCode: string, input: HostedPlanVersionInput): Promise<HostedPlanPublicationPreview> {
    return normalizeHostedPlanPreview(await request<unknown>(
      `/api/account-lifecycle/plans/${encodeURIComponent(planCode)}/versions`,
      { method: 'POST', body: input },
    ));
  },
  async hostedEntitlementManagement(memberId: string): Promise<HostedEntitlementManagement> {
    return normalizeHostedEntitlementManagement(await request<unknown>(
      `/api/account-lifecycle/members/${encodeURIComponent(memberId)}/entitlement-management`,
    ));
  },
  async assignHostedPlan(memberId: string, planCode: string, reason: string): Promise<HostedEntitlement> {
    const payload = await request<unknown>(`/api/account-lifecycle/members/${encodeURIComponent(memberId)}/entitlement`, {
      method: 'PATCH',
      body: { planCode, reason, idempotencyKey: `owner.assignment.${memberId}.${Date.now()}` },
    });
    return normalizeHostedEntitlement(payload);
  },
  async requestHostedPlanChange(memberId: string, targetPlanCode: string, reason: string): Promise<{ id: string; kind: string; status: string; scheduledFor: string | null; impact: HostedDowngradeImpact | null }> {
    const payload = await request<unknown>(
      `/api/account-lifecycle/members/${encodeURIComponent(memberId)}/plan-changes`,
      { method: 'POST', body: { targetPlanCode, reason, idempotencyKey: `owner.plan-change.${memberId}.${Date.now()}` } },
    );
    const source = record(unwrap(payload, 'planChange', 'plan_change'));
    const impactSource = first(record(payload), 'impact');
    return {
      id: text(source.id), kind: text(source.kind), status: text(source.status),
      scheduledFor: text(first(source, 'scheduledFor', 'scheduled_for')) || null,
      impact: impactSource ? normalizeHostedDowngradeImpact(impactSource) : null,
    };
  },
  async applyHostedPaidUpgrade(planChangeId: string, amountPaidIdr: number, sourceOrderId: string, calculationNote: string): Promise<HostedEntitlement> {
    return normalizeHostedEntitlement(await request<unknown>(
      `/api/account-lifecycle/plan-changes/${encodeURIComponent(planChangeId)}/apply-paid-upgrade`,
      { method: 'POST', body: { amountPaidIdr, sourceOrderId, calculationNote } },
    ));
  },
  async hostedPlanChangeImpact(planChangeId: string): Promise<HostedDowngradeImpact> {
    return normalizeHostedDowngradeImpact(await request<unknown>(
      `/api/account-lifecycle/plan-changes/${encodeURIComponent(planChangeId)}/impact`,
    ));
  },
  async updateHostedDowngradeOrigins(planChangeId: string, videoId: string, origins: string[]): Promise<HostedDowngradeImpact> {
    return normalizeHostedDowngradeImpact(await request<unknown>(
      `/api/account-lifecycle/plan-changes/${encodeURIComponent(planChangeId)}/embed-origins`,
      { method: 'PUT', body: { videoId, origins } },
    ));
  },
  async cleanupHostedDowngradeInternal(planChangeId: string): Promise<number> {
    const response = record(await request<unknown>(
      `/api/account-lifecycle/plan-changes/${encodeURIComponent(planChangeId)}/cleanup-internal`,
      { method: 'POST' },
    ));
    return numberValue(first(response, 'publicationCount', 'publication_count'));
  },
  async createHostedEntitlementGrant(memberId: string, input: {
    grantCode: string;
    values: Record<string, boolean | number>;
    effectiveFrom: string;
    effectiveUntil: string;
    reason: string;
  }): Promise<void> {
    await request(`/api/account-lifecycle/members/${encodeURIComponent(memberId)}/entitlement-grants`, {
      method: 'POST', body: input,
    });
  },
  async revokeHostedEntitlementGrant(grantId: string, reason: string): Promise<void> {
    await request(`/api/account-lifecycle/entitlement-grants/${encodeURIComponent(grantId)}/revoke`, {
      method: 'POST', body: { reason },
    });
  },
  async createHostedEntitlementAdjustment(memberId: string, input: {
    valueKey: string;
    value: boolean | number | string;
    priority: number;
    effectiveFrom: string;
    effectiveUntil: string;
    reason: string;
  }): Promise<void> {
    await request(`/api/account-lifecycle/members/${encodeURIComponent(memberId)}/entitlement-adjustments`, {
      method: 'POST', body: input,
    });
  },
  async revokeHostedEntitlementAdjustment(adjustmentId: string, reason: string): Promise<void> {
    await request(`/api/account-lifecycle/entitlement-adjustments/${encodeURIComponent(adjustmentId)}/revoke`, {
      method: 'POST', body: { reason },
    });
  },
  async inviteHostedMember(input: { email: string; displayName: string; operatorNote: string; planCode?: string }): Promise<HostedMember> {
    return normalizeHostedMember(unwrap(await request<unknown>('/api/account-lifecycle/invitations', {
      method: 'POST', body: input,
    }), 'user', 'member'));
  },
  async updateHostedMemberNote(id: string, operatorNote: string): Promise<void> {
    await request(`/api/account-lifecycle/members/${encodeURIComponent(id)}/note`, {
      method: 'PATCH', body: { operatorNote },
    });
  },
  async resendHostedInvitation(id: string, reason: string): Promise<void> {
    await request(`/api/account-lifecycle/members/${encodeURIComponent(id)}/invitations`, {
      method: 'POST', body: { reason },
    });
  },
  async changeHostedMemberEmail(id: string, email: string, reason: string): Promise<void> {
    await request(`/api/account-lifecycle/members/${encodeURIComponent(id)}/email`, {
      method: 'PATCH', body: { email, reason },
    });
  },
  async initiateHostedMemberEmailChange(id: string, newEmail: string, supportEvidence: string): Promise<HostedMemberEmailChange> {
    const source = record(await request<unknown>(`/api/account-lifecycle/members/${encodeURIComponent(id)}/email-changes`, {
      method: 'POST', body: { newEmail, supportEvidence },
    }));
    const change = record(first(source, 'emailChange', 'email_change'));
    return {
      id: text(change.id),
      oldEmail: text(first(change, 'oldEmail', 'old_email')),
      newEmail: text(first(change, 'newEmail', 'new_email')),
      expiresAt: text(first(change, 'expiresAt', 'expires_at')),
      createdAt: text(first(change, 'createdAt', 'created_at')),
    };
  },
  async verifyHostedMemberEmailChange(id: string, code: string): Promise<string> {
    const source = record(await request<unknown>(`/api/account-lifecycle/email-changes/${encodeURIComponent(id)}/verify`, {
      method: 'POST', body: { code },
    }));
    return text(source.email);
  },
  async cancelHostedMemberEmailChange(id: string): Promise<void> {
    await request(`/api/account-lifecycle/email-changes/${encodeURIComponent(id)}/cancel`, { method: 'POST' });
  },
  async hostedDashboardSessions(): Promise<HostedDashboardSession[]> {
    return arrayFrom(await request<unknown>('/api/account-lifecycle/sessions'), 'sessions')
      .map(normalizeHostedDashboardSession).filter((session) => session.id);
  },
  async revokeHostedDashboardSession(id: string): Promise<void> {
    await request(`/api/account-lifecycle/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
  async integrationKeys(): Promise<HostedIntegrationKey[]> {
    return arrayFrom(await request<unknown>('/api/account-lifecycle/integration-keys'), 'keys').map(normalizeHostedIntegrationKey).filter((key) => key.id);
  },
  async playbackIntegrations(): Promise<import('./components/PlaybackIntegrationsPanel').PlaybackIntegrationItem[]> {
    const response=await request<{integrations:import('./components/PlaybackIntegrationsPanel').PlaybackIntegrationItem[]}>('/api/account-lifecycle/playback-integrations');return response.integrations;
  },
  async createPlaybackIntegration(label:string,siteOrigin:string):Promise<{integration:import('./components/PlaybackIntegrationsPanel').PlaybackIntegrationItem;secret:string}> {
    return request('/api/account-lifecycle/playback-integrations',{method:'POST',body:{label,siteOrigin}});
  },
  async revokePlaybackIntegration(id:string):Promise<void>{await request(`/api/account-lifecycle/playback-integrations/${encodeURIComponent(id)}/revoke`,{method:'POST',body:{}});},
  async wordpressPlugin():Promise<import('./components/PlaybackIntegrationsPanel').WordPressPluginRelease>{return request('/api/account-lifecycle/playback-integrations/wordpress-plugin');},
  async watermarkHistory(filters: import('./types').WatermarkHistoryFilters = {}): Promise<import('./types').WatermarkHistoryResult> {
    return request('/api/account-lifecycle/playback-integrations/watermark-history/search', { method: 'POST', body: filters });
  },
  async integrationKeyUsage(): Promise<HostedQuotaUsage | null> {
    const source = record(await request<unknown>('/api/account-lifecycle/integration-keys'));
    const usage = record(source.usage);
    const active = Number(usage.active); const limit = Number(usage.limit);
    return Number.isSafeInteger(active) && Number.isSafeInteger(limit) && active >= 0 && limit > 0 ? { active, limit } : null;
  },
  async createIntegrationKey(label: string): Promise<{ key: HostedIntegrationKey; secret: string }> {
    const source = record(await request<unknown>('/api/account-lifecycle/integration-keys', { method: 'POST', body: { label } }));
    return { key: normalizeHostedIntegrationKey(source.key), secret: text(source.secret) };
  },
  async revokeIntegrationKey(id: string): Promise<void> {
    await request(`/api/account-lifecycle/integration-keys/${encodeURIComponent(id)}/revoke`, { method: 'POST', body: {} });
  },
  async revokeHostedMemberSessions(id: string): Promise<number> {
    const source = record(await request<unknown>(`/api/account-lifecycle/members/${encodeURIComponent(id)}/sessions/revoke-all`, {
      method: 'POST', body: {},
    }));
    return numberValue(first(source, 'revokedSessions', 'revoked_sessions'));
  },
  async suspendHostedMember(id: string, reason: string): Promise<void> {
    await request(`/api/account-lifecycle/members/${encodeURIComponent(id)}/suspend`, {
      method: 'POST', body: { reason },
    });
  },
  async reactivateHostedMember(id: string, reason: string): Promise<void> {
    await request(`/api/account-lifecycle/members/${encodeURIComponent(id)}/reactivate`, {
      method: 'POST', body: { reason },
    });
  },
  async initiateHostedRecovery(id: string, reason: string): Promise<void> {
    await request(`/api/account-lifecycle/members/${encodeURIComponent(id)}/recovery`, {
      method: 'POST', body: { reason },
    });
  },
  async hostedEmailDeliveries(memberId?: string): Promise<HostedEmailDelivery[]> {
    const query = memberId ? `?memberId=${encodeURIComponent(memberId)}` : '';
    return arrayFrom(await request<unknown>(`/api/account-lifecycle/email-deliveries${query}`), 'deliveries').map(normalizeHostedDelivery);
  },
  async retryHostedEmailDelivery(id: string, reason: string): Promise<void> {
    await request(`/api/account-lifecycle/email-deliveries/${encodeURIComponent(id)}/retry`, { method: 'POST', body: { reason } });
  },
  async reconcileHostedRemediation(planChangeId: string, reason: string): Promise<HostedDowngradeImpact> {
    return normalizeHostedDowngradeImpact(await request<unknown>(
      `/api/account-lifecycle/plan-changes/${encodeURIComponent(planChangeId)}/reconcile`,
      { method: 'POST', body: { reason } },
    ));
  },
  async retryHostedBillingProjection(eventId: string, reason: string): Promise<void> {
    await request(`/api/account-lifecycle/billing-projections/${encodeURIComponent(eventId)}/retry`, {
      method: 'POST', body: { reason },
    });
  },
  async users(): Promise<TeamUser[]> {
    return arrayFrom(await request<unknown>('/api/users'), 'users').map((value) => {
      const source = record(value);
      return {
        id: text(source.id),
        email: text(source.email),
        displayName: text(first(source, 'displayName', 'display_name'), text(source.email)),
        role: text(source.role, 'viewer'),
        disabledAt: text(first(source, 'disabledAt', 'disabled_at')) || null,
        createdAt: text(first(source, 'createdAt', 'created_at')) || undefined,
        updatedAt: text(first(source, 'updatedAt', 'updated_at')) || undefined,
      };
    });
  },
  async createUser(input: { email: string; displayName: string; role: string; password: string }): Promise<TeamUser> {
    return unwrap(await request<unknown>('/api/users', { method: 'POST', body: input }), 'user') as TeamUser;
  },
  async updateUser(id: string, input: { email?: string; displayName?: string; role?: string; password?: string; disabled?: boolean }): Promise<void> {
    await request(`/api/users/${encodeURIComponent(id)}`, { method: 'PATCH', body: input });
  },
  async deleteUser(id: string): Promise<void> {
    await request(`/api/users/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
};

export interface ResumableUploadTask {
  promise: Promise<UploadResult>;
  start: () => void;
  pause: () => Promise<void>;
  park: () => Promise<void>;
  cancel: () => Promise<void>;
}

const uploadSessionUuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function uploadSessionId(value: string | null | undefined): string | null {
  const candidate = value?.trim();
  if (!candidate) return null;
  if (uploadSessionUuidPattern.test(candidate)) return candidate;
  try {
    const url = new URL(candidate, typeof window === 'undefined' ? 'http://localhost' : window.location.origin);
    for (const segment of url.pathname.split('/').reverse()) {
      if (uploadSessionUuidPattern.test(segment)) return segment;
    }
  } catch {
    return null;
  }
  return null;
}

function uploadFingerprint(
  file: File,
  folderId: string | null,
  protectionMode: 'automatic' | 'r2_only' | 'protected',
): string {
  return ['streamline-v2', file.name, file.type, file.size, file.lastModified, folderId ?? 'root', protectionMode].join('-');
}

export function uploadVideo(
  file: File,
  folderId: string | null,
  onProgress: (percent: number) => void,
  publication: { protectionMode: 'automatic' | 'r2_only' | 'protected'; sourceDurationSeconds: number | null } = {
    protectionMode: 'automatic', sourceDurationSeconds: null,
  },
): ResumableUploadTask {
  let settled = false;
  let initialized = false;
  let initialization: Promise<void> | null = null;
  let paused = false;
  let resolvePromise: (result: UploadResult) => void;
  let rejectPromise: (reason: unknown) => void;
  const promise = new Promise<UploadResult>((resolve, reject) => {
    resolvePromise = resolve;
    rejectPromise = reject;
  });
  const token = currentCsrfToken();
  const uploadSupportVersion = supportContextVersion;
  const upload = new Upload(file, {
    endpoint: '/api/videos/upload/',
    chunkSize: 8 * 1024 * 1024,
    retryDelays: [0, 1_000, 3_000, 5_000, 10_000, 20_000],
    removeFingerprintOnSuccess: false,
    storeFingerprintForResuming: true,
    fingerprint: async () => uploadFingerprint(file, folderId, publication.protectionMode),
    metadata: {
      filename: file.name,
      filetype: file.type,
      folderId: folderId ?? '',
      fingerprint: uploadFingerprint(file, folderId, publication.protectionMode),
      protectionMode: publication.protectionMode,
      sourceDurationSeconds: publication.sourceDurationSeconds === null ? '' : String(publication.sourceDurationSeconds),
    },
    headers: token ? { 'X-CSRF-Token': token } : {},
    onBeforeRequest: (request) => {
      if (uploadSupportVersion !== supportContextVersion) {
        throw new ApiError('Support context changed while the upload was in progress.', 409, { code: 'stale_support_context' });
      }
      if (activeSupportContext) {
        request.setHeader('X-Dewe-Support-Owner-Id', activeSupportContext.memberId);
        request.setHeader('X-Dewe-Support-Reason', activeSupportContext.reason);
      }
      const xhr = request.getUnderlyingObject() as XMLHttpRequest | undefined;
      if (xhr) xhr.withCredentials = true;
    },
    onChunkComplete: (_chunkBytes, bytesAccepted, bytesTotal) => {
      onProgress(bytesTotal ? Math.min(100, Math.round((bytesAccepted / bytesTotal) * 100)) : 0);
    },
    onAfterResponse: (_request, response) => {
      const confirmedBytes = Number(response.getHeader('Upload-Offset'));
      if (Number.isFinite(confirmedBytes) && confirmedBytes >= 0) {
        onProgress(Math.min(100, Math.round((confirmedBytes / file.size) * 100)));
      }
    },
    onSuccess: ({ lastResponse }) => {
      if (settled) return;
      settled = true;
      onProgress(100);
      const id = uploadSessionId(lastResponse.getHeader('X-Upload-ID')) ?? uploadSessionId(upload.url);
      if (!id) {
        rejectPromise(new ApiError('The upload finished, but its durable upload session could not be identified.', 0));
        return;
      }
      void requestUploadSession(id).then(async (uploadSession) => {
        await removeStoredUploadsForSession(id);
        resolvePromise({ uploadSession });
      }, (error) => rejectPromise(error));
    },
    onError: (error) => {
      if (settled) return;
      settled = true;
      const detailed = error as { originalResponse?: { getStatus: () => number; getBody: () => string } | null };
      const status = detailed.originalResponse?.getStatus() ?? 0;
      const body = detailed.originalResponse?.getBody().trim();
      let details: unknown = error;
      let message = body || error.message || 'Upload failed.';
      if (body) {
        try {
          details = JSON.parse(body);
          const source = record(details);
          const nested = record(source.error);
          message = text(first(source, 'message', 'detail') ?? first(nested, 'message', 'detail'), message);
        } catch { /* Preserve non-JSON failures without inventing a quota code. */ }
      }
      rejectPromise(new ApiError(message, status, details));
    },
  });

  async function removeStoredUploadsForSession(id: string): Promise<void> {
    try {
      const previousUploads = await upload.findPreviousUploads();
      const matchingUploads = previousUploads.filter((candidate) => uploadSessionId(candidate.uploadUrl) === id);
      await Promise.all(matchingUploads.map(async (candidate) => {
        try {
          await upload.options.urlStorage?.removeUpload(candidate.urlStorageKey);
        } catch {
          // A later selection can remove browser metadata after confirming the terminal server session.
        }
      }));
    } catch {
      // The server session is authoritative even if browser resume metadata cannot be inspected.
    }
  }

  async function initialize(): Promise<void> {
    const previousUploads = await upload.findPreviousUploads();
    if (settled) return;
    const previous = previousUploads
      .filter((candidate) => candidate.size === file.size && candidate.uploadUrl)
      .sort((a, b) => b.creationTime.localeCompare(a.creationTime))[0];
    const previousId = uploadSessionId(previous?.uploadUrl);
    if (previous && previousId) {
      let uploadSession: UploadSession | null = null;
      try {
        uploadSession = await requestUploadSession(previousId);
      } catch (error) {
        if (!(error instanceof ApiError) || error.status !== 404) throw error;
      }
      if (settled) return;
      if (uploadSession && ['awaiting_duplicate_decision', 'completed'].includes(uploadSession.status)) {
        try {
          await upload.options.urlStorage?.removeUpload(previous.urlStorageKey);
        } catch {
          // The server session is authoritative even if browser resume metadata cannot be removed.
        }
        if (settled) return;
        settled = true;
        onProgress(100);
        resolvePromise({ uploadSession });
        return;
      }
      if (uploadSession?.status === 'active') {
        upload.resumeFromPreviousUpload(previous);
      } else {
        try {
          await upload.options.urlStorage?.removeUpload(previous.urlStorageKey);
        } catch {
          // Stale browser metadata must not prevent a fresh upload from starting.
        }
      }
    }
    initialized = true;
    if (!paused) upload.start();
  }

  async function start(): Promise<void> {
    if (settled) return;
    paused = false;
    try {
      if (!initialized) {
        initialization ??= initialize();
        await initialization;
        return;
      }
      upload.start();
    } catch (error) {
      if (!settled) {
        settled = true;
        rejectPromise(error);
      }
    }
  }

  return {
    promise,
    start: () => { void start(); },
    pause: () => {
      paused = true;
      return upload.abort(false);
    },
    park: async () => {
      paused = true;
      await upload.abort(false);
      if (!settled) {
        settled = true;
        rejectPromise(new ApiError('Upload paused. Select the same file to resume it.', 0));
      }
    },
    cancel: async () => {
      paused = true;
      let terminationError: unknown;
      try {
        await upload.abort(true);
      } catch (error) {
        terminationError = error;
      }
      if (!settled) {
        settled = true;
        rejectPromise(terminationError ?? new ApiError('Upload cancelled.', 0));
      }
    },
  };
}
